import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { API } from "@/App";
import { toast } from "sonner";
import Draggable from "react-draggable";
import { 
  ChevronDown, ChevronRight, FileText, FileSpreadsheet, Clipboard, 
  Download, RefreshCw, Loader2, Lock, Plus, X, Receipt, Check, Crosshair,
  Brain, Users, ArrowRight, Trash2, Copy, Target, MapPin, Phone, Mail,
  Calendar, Clock, User, CheckCircle2, AlertTriangle, Shield, ShieldAlert, ShieldCheck,
  Zap, MessageSquare, Send, Sparkles, HardHat, ShoppingCart, GitCompare, Menu, Info, Save,
  Home, FolderKanban, ClipboardCheck, ClipboardList, CreditCard, BarChart3, Settings, LogOut, Calculator
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import OverwatchHUD from "@/components/OverwatchHUD";
import AiCopilotModal from "@/components/AiCopilotModal";
import DocumentPreviewModal from "@/components/DocumentPreviewModal";
import ProposalBuilder from "@/components/ProposalBuilder";
import OperatorOrderBuilder from "@/components/OperatorOrderBuilder";
import ClientSOWBuilder from "@/components/ClientSOWBuilder";
import SubcontractorSOWBuilder from "@/components/SubcontractorSOWBuilder";
import MilestoneSOWBuilder from "@/components/MilestoneSOWBuilder";
import LogisticsManifestBuilder from "@/components/LogisticsManifestBuilder";
import MissionBriefViewer from "@/components/MissionBriefViewer";

// Import refactored constants and utilities
import { 
  SCOPE_TEMPLATES,
  SCOPE_ACTION_KEYWORDS,
  MATERIAL_OVERRIDE_KEYWORDS,
  LABOR_KEYWORDS,
  MARKET_AVERAGES,
  FORBIDDEN_PHRASES,
  LAZY_PHRASE_CORRECTIONS
} from './estimating/constants';

import {
  findMatchingTemplate,
  isNewBuildRequest,
  cleanSectionName,
  formatSectionName,
  detectCostType,
  estimateMarketPrice,
  validateEstimateQuality,
  extractItemsFromMessage,
  computeGroupTotals,
  computeScopeTotals,
  buildScopeSummary,
  generateClientBrief,
  generateOperatorOrder,
  createDefaultScopes
} from './estimating/utils';

export default function EstimatingPage() {
  const navigate = useNavigate();
  
  // State for data
  const [loading, setLoading] = useState(true);
  const [estimateDataLoading, setEstimateDataLoading] = useState(false); // NEW: Track estimate data loading
  const [leads, setLeads] = useState([]);
  const [clients, setClients] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [selectedLead, setSelectedLead] = useState(null);
  const [selectedEstimate, setSelectedEstimate] = useState(null);
  const [globalSettings, setGlobalSettings] = useState(null);
  
  // === SCOPE MANAGEMENT STATE ===
  const [scopes, setScopes] = useState(createDefaultScopes());
  const [activeScope, setActiveScope] = useState("base"); // "base" or "co-1", "co-2", etc.
  const [scopeMenuOpen, setScopeMenuOpen] = useState(false);
  
  // REF to track latest scopes for verification (avoids stale closure issues)
  const scopesRef = useRef(scopes);
  useEffect(() => {
    scopesRef.current = scopes;
  }, [scopes]);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // FIX: SECTION NAME CLEANER EFFECT - Strips recursive numbering on load
  // Runs once when groups change to fix "01. 01. 01..." patterns in existing data
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const groups = scopes?.base?.groups || [];
    
    // Check if any group name has recursive numbering (e.g., "01. 01. ")
    const hasRecursiveNumbering = groups.some(g => {
      const name = g.name || '';
      // Match patterns like "01. 01. " or "1. 2. 3. "
      return /^(\d+\.\s*){2,}/.test(name);
    });
    
    if (hasRecursiveNumbering) {
      console.log('[SectionCleaner] Detected recursive numbering - cleaning...');
      
      setScopes(prev => ({
        ...prev,
        base: {
          ...prev.base,
          groups: (prev.base.groups || []).map((group, index) => ({
            ...group,
            name: formatSectionName(index, group.name), // Clean and reformat
          })),
        },
      }));
    }
  }, [scopes?.base?.groups?.length]); // Only run when number of groups changes
  
  // ═══════════════════════════════════════════════════════════════════════════
  // MASTER SERGEANT CONTEXT CACHE - Zero Amnesia Protocol
  // Stores suggested items for "yes" confirmation workflow
  // ═══════════════════════════════════════════════════════════════════════════
  const [superintendentContextCache, setSuperintendentContextCache] = useState({
    lastSuggestedItems: [],        // Items AI suggested in last message
    lastSuggestedAction: null,     // "add", "modify", "remove"
    lastModifications: [],         // Pending modifications
    awaitingConfirmation: false,   // True if AI asked "should I add these?"
    lastQuery: "",                 // Original user query
  });
  
  // State for UI
  const [collapsedGroups, setCollapsedGroups] = useState({});
  const [convertMenuOpen, setConvertMenuOpen] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [hudOpen, setHudOpen] = useState(false); // HUD default CLOSED
  
  // Invoice modal state
  const [invoiceModalOpen, setInvoiceModalOpen] = useState(false);
  const [invoiceScopes, setInvoiceScopes] = useState({ base: true, changeOrders: [] });
  
  // DOCUMENT PREVIEW MODAL STATE
  const [docPreviewOpen, setDocPreviewOpen] = useState(false);
  const [docPreviewType, setDocPreviewType] = useState(null);
  const [returnToMissionBrief, setReturnToMissionBrief] = useState(false);
  
  // PROPOSAL BUILDER STATE
  const [proposalBuilderOpen, setProposalBuilderOpen] = useState(false);
  const [companySettings, setCompanySettings] = useState(null);
  const [selectedChangeOrderForProposal, setSelectedChangeOrderForProposal] = useState(null);
  
  // OPERATOR ORDER BUILDER STATE
  const [operatorOrderBuilderOpen, setOperatorOrderBuilderOpen] = useState(false);
  
  // CLIENT SOW BUILDER STATE
  const [clientSOWBuilderOpen, setClientSOWBuilderOpen] = useState(false);
  
  // SUBCONTRACTOR SOW BUILDER STATE
  const [subcontractorSOWBuilderOpen, setSubcontractorSOWBuilderOpen] = useState(false);
  
  // MILESTONE SOW BUILDER STATE
  const [milestoneSOWBuilderOpen, setMilestoneSOWBuilderOpen] = useState(false);
  
  // LOGISTICS MANIFEST BUILDER STATE
  const [logisticsManifestBuilderOpen, setLogisticsManifestBuilderOpen] = useState(false);
  
  // MISSION BRIEF VIEWER STATE
  const [missionBriefViewerOpen, setMissionBriefViewerOpen] = useState(false);
  
  // AI SOW modal state
  const [showSowModal, setShowSowModal] = useState(false);
  const [sowActiveTab, setSowActiveTab] = useState("client"); // "client" or "operator" or "schedule"
  const [sowContent, setSowContent] = useState({ client: "", operator: "" });
  const [generatingSow, setGeneratingSow] = useState(false);
  
  // Project Schedule state
  const [projectSchedule, setProjectSchedule] = useState(null);
  const [generatingSchedule, setGeneratingSchedule] = useState(false);
  
  // Deck refinement state (Caveman Mode post-build; frontend-only)
  // - deckRefinementActive: true once we've auto-built a deck and can show refinements
  // - deckParams: last known deterministic deck params (material, height, railing, stairs, demo)
  // - deckBaseScope: the base, human-readable deck scope text (without DECK_PARAMS)
  const [deckRefinementActive, setDeckRefinementActive] = useState(false);
  const [deckParams, setDeckParams] = useState(null);
  const [deckBaseScope, setDeckBaseScope] = useState(null);

  // Mission Threat Assessment state
  const [estDurationWeeks, setEstDurationWeeks] = useState(4);
  const [riskSettings, setRiskSettings] = useState({
    targetGPPerMonth: 10200,
    minimumGPPerMonth: 7000,
    maxAllowancePercent: 15,
  });
  
  // Mission Objectives / Scope Notes
  const [scopeNotes, setScopeNotes] = useState("");
  const [scopeNotesExpanded, setScopeNotesExpanded] = useState(false);
  
  // Initializing state for auto-create estimate
  const [initializing, setInitializing] = useState(false);
  
  // Smart Type Detection - track recently auto-detected items for visual feedback
  const [autoDetectedItems, setAutoDetectedItems] = useState(new Set());
  
  // AI Scope Commander state
  const [aiCommand, setAiCommand] = useState("");
  const [aiCommandLoading, setAiCommandLoading] = useState(false);
  const [lastCommandResult, setLastCommandResult] = useState(null);
  
  // Grandmaster Estimator Modal state
  const [grandmasterModalOpen, setGrandmasterModalOpen] = useState(false);
  const [grandmasterLoading, setGrandmasterLoading] = useState(false);
  const [projectAssessment, setProjectAssessment] = useState(null);
  const [assessmentChecklist, setAssessmentChecklist] = useState({
    demolition: [],
    risks: [],
    missing: [],
  });
  
  // Compact Mode state - collapses UI for more table space
  const [compactMode, setCompactMode] = useState(() => {
    const saved = localStorage.getItem('missionScopeCompactMode');
    return saved === 'true';
  });
  
  // Save compact mode preference
  useEffect(() => {
    localStorage.setItem('missionScopeCompactMode', compactMode.toString());
  }, [compactMode]);
  
  // Keyboard shortcut for compact mode (Cmd/Ctrl + B)
  useEffect(() => {
    const handleKeyPress = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        setCompactMode(prev => !prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, []);
  
  // Site Superintendent (AI Copilot) state - FAB triggered
  const [superintendentOpen, setSuperintendentOpen] = useState(false);
  const [superintendentMinimized, setSuperintendentMinimized] = useState(false);
  const [superintendentLoading, setSuperintendentLoading] = useState(false);
  const [superintendentChat, setSuperintendentChat] = useState([]);
  const [superintendentSessionId, setSuperintendentSessionId] = useState(null);
  
  // Conversational Estimator state - for tracking multi-turn conversations
  const [conversationalChatHistory, setConversationalChatHistory] = useState([]);
  const [conversationalEstimateId, setConversationalEstimateId] = useState(null);
  
  // REFs for estimate IDs to avoid stale closure issues in callbacks
  const selectedEstimateRef = useRef(null);
  const conversationalEstimateIdRef = useRef(null);
  useEffect(() => {
    selectedEstimateRef.current = selectedEstimate;
  }, [selectedEstimate]);
  useEffect(() => {
    conversationalEstimateIdRef.current = conversationalEstimateId;
  }, [conversationalEstimateId]);
  
  // FAB Draggable state
  const fabRef = useRef(null);
  
  // Threat Assessment Modal state
  const [threatModalOpen, setThreatModalOpen] = useState(false);
  const [threatModalPosition, setThreatModalPosition] = useState({ x: 100, y: 100 });
  const [isDraggingThreatModal, setIsDraggingThreatModal] = useState(false);
  const [threatDragOffset, setThreatDragOffset] = useState({ x: 0, y: 0 });
  const threatModalRef = useRef(null);
  
  // Smart Scrolling Header State & Refs
  const [headerScrolled, setHeaderScrolled] = useState(false);
  const scrollContainerRef = useRef(null);
  const [missionBriefOpen, setMissionBriefOpen] = useState(false);
  const [activeTab, setActiveTab] = useState("main"); // "main" | "change_orders" | "documents"
  
  // ESC key handler for Mission Brief overlay
  useEffect(() => {
    const handleEscKey = (e) => {
      if (e.key === 'Escape' && missionBriefOpen) {
        setMissionBriefOpen(false);
      }
    };
    document.addEventListener('keydown', handleEscKey);
    return () => document.removeEventListener('keydown', handleEscKey);
  }, [missionBriefOpen]);
  
  // Refs for dropdown click-outside handling
  const convertMenuRef = useRef(null);
  const scopeMenuRef = useRef(null);
  const aiCommandInputRef = useRef(null);
  
  // ═══════════════════════════════════════════════════════════════════════════
  // CRITICAL FIX: Load estimates when customer/lead changes
  // This ensures estimates are persisted and reloaded correctly
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    const loadCustomerEstimates = async () => {
      if (!selectedLead) {
        console.log('[Customer] No customer selected');
        // Don't clear estimates - user might be browsing
        return;
      }

      try {
        setInitializing(true);

        // Build query parameters - MUST pass lead_id or client_id for filtering
        const params = new URLSearchParams();
        
        if (selectedLead.lead_id) {
          params.append('lead_id', selectedLead.lead_id);
          console.log('[Customer] Querying by lead_id:', selectedLead.lead_id);
        }
        
        if (selectedLead.client_id) {
          params.append('client_id', selectedLead.client_id);
          console.log('[Customer] Querying by client_id:', selectedLead.client_id);
        }
        
        // If no IDs, can't query safely - would return ALL estimates
        if (!selectedLead.lead_id && !selectedLead.client_id) {
          console.warn('[Customer] No lead_id or client_id - cannot filter estimates');
          setEstimates([]);
          setSelectedEstimate(null);
          setConversationalEstimateId(null);
          setScopes(createDefaultScopes());
          setInitializing(false);
          return;
        }

        const queryString = params.toString();
        console.log('[Customer] Fetching: /estimates?' + queryString);
        
        const res = await API.get(`/estimates?${queryString}`);
        const customerEstimates = res.data || [];

        console.log(`[Customer] Loaded ${customerEstimates.length} estimates for customer:`, selectedLead.contact_name || selectedLead.name);

        setEstimates(customerEstimates);

        // Auto-select most recent estimate (if exists)
        if (customerEstimates.length > 0) {
          const mostRecent = customerEstimates[0]; // Assumes sorted by date
          console.log('[Customer] Auto-selecting estimate:', mostRecent.estimate_id);
          
          setSelectedEstimate(mostRecent);
          setConversationalEstimateId(mostRecent.estimate_id);
          
          // Load scopes from the estimate
          if (mostRecent.scopes_data && mostRecent.scopes_data.base) {
            console.log("[Customer] Loading scopes_data from estimate:", mostRecent.estimate_id);
            setScopes(mostRecent.scopes_data);
          } else if (mostRecent.sections && mostRecent.sections.length > 0) {
            // Convert backend sections to frontend scopes format
            console.log("[Customer] Converting backend sections to scopes");
            const convertedGroups = mostRecent.sections.map((sec, idx) => ({
              id: sec.section_id || String(idx + 1).padStart(2, "0"),
              code: String(idx + 1).padStart(2, "0"),
              name: formatSectionName(idx, sec.name),
              items: (sec.line_items || []).map(item => ({
                id: item.line_id || `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                itemId: item.line_id || `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                itemName: item.item_name || item.description || "Item",
                description: item.description || "",
                quantity: item.quantity || 1,
                unit: item.unit || "EA",
                unitCost: (item.material_unit_cost || 0) + (item.labor_unit_cost || 0),
                costType: item.labor_unit_cost > item.material_unit_cost ? "Labor" : "Material",
                markupPercent: item.markup_percent || 0,
              })),
            }));
            setScopes({
              base: { groups: convertedGroups },
              changeOrders: mostRecent.change_orders || []
            });
          } else {
            setScopes(createDefaultScopes());
          }
          
          toast.success(`Loaded: ${mostRecent.job_name || mostRecent.estimate_id}`);
        } else {
          // No estimates for this customer yet - reset to default
          console.log('[Customer] No estimates found for this customer');
          setSelectedEstimate(null);
          setConversationalEstimateId(null);
          setScopes(createDefaultScopes());
        }
      } catch (err) {
        console.error("[Customer] Failed to load estimates:", err);
        setEstimates([]);
        setSelectedEstimate(null);
        setConversationalEstimateId(null);
        setScopes(createDefaultScopes());
        toast.error("Failed to load customer estimates");
      } finally {
        setInitializing(false);
      }
    };
    
    loadCustomerEstimates();
  }, [selectedLead]);
  
  // Click-outside handler for dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (convertMenuRef.current && !convertMenuRef.current.contains(event.target)) {
        setConvertMenuOpen(false);
      }
      if (scopeMenuRef.current && !scopeMenuRef.current.contains(event.target)) {
        setScopeMenuOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Smart Scroll Detection for Header Collapse
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;
    
    const handleScroll = () => {
      const scrollTop = scrollContainer.scrollTop;
      setHeaderScrolled(scrollTop > 50);
    };
    
    // Initial check
    handleScroll();
    
    scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    
    return () => {
      scrollContainer.removeEventListener('scroll', handleScroll);
    };
  }, [selectedEstimate]); // Re-run when estimate changes

  // Threat Modal Drag Handlers
  const handleThreatModalMouseDown = (e) => {
    if (e.target.closest('.drag-handle-threat')) {
      setIsDraggingThreatModal(true);
      const rect = threatModalRef.current?.getBoundingClientRect();
      if (rect) {
        setThreatDragOffset({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
    }
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDraggingThreatModal) {
        const newX = Math.max(0, Math.min(window.innerWidth - 450, e.clientX - threatDragOffset.x));
        const newY = Math.max(0, Math.min(window.innerHeight - 100, e.clientY - threatDragOffset.y));
        setThreatModalPosition({ x: newX, y: newY });
      }
    };
    
    const handleMouseUp = () => {
      setIsDraggingThreatModal(false);
    };

    if (isDraggingThreatModal) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingThreatModal, threatDragOffset]);

  // CRITICAL: Helper to get current estimate ID from either selectedEstimate or conversationalEstimateId
  // This fixes the bug where newly created estimates via AI chat aren't recognized
  const getCurrentEstimateId = useCallback(() => {
    return selectedEstimate?.estimate_id || conversationalEstimateId || null;
  }, [selectedEstimate, conversationalEstimateId]);

  // Get current active groups based on selected scope
  const getActiveGroups = useCallback(() => {
    if (activeScope === "base") {
      return scopes.base.groups;
    }
    const co = scopes.changeOrders.find((c) => c.id === activeScope);
    return co?.groups || [];
  }, [activeScope, scopes]);

  // Set groups for current scope
  const setActiveGroups = useCallback((newGroups) => {
    setScopes((prev) => {
      if (activeScope === "base") {
        return { ...prev, base: { ...prev.base, groups: newGroups } };
      }
      return {
        ...prev,
        changeOrders: prev.changeOrders.map((co) =>
          co.id === activeScope ? { ...co, groups: newGroups } : co
        ),
      };
    });
  }, [activeScope]);

  // Create new Change Order
  const createChangeOrder = useCallback(() => {
    const coNumber = scopes.changeOrders.length + 1;
    const newCO = {
      id: `co-${coNumber}`,
      name: `CHANGE ORDER #${coNumber}`,
      type: "change_order",
      number: coNumber,
      groups: [
        {
          id: `co${coNumber}-01`,
          code: "CO",
          name: `CO #${coNumber} - NEW ITEMS`,
          items: [],
        },
      ],
      locked: false,
      createdAt: new Date().toISOString(),
    };
    
    setScopes((prev) => ({
      ...prev,
      changeOrders: [...prev.changeOrders, newCO],
    }));
    
    setActiveScope(newCO.id);
    setScopeMenuOpen(false);
    toast.success(`Change Order #${coNumber} created`);
  }, [scopes.changeOrders.length]);

  // Reset to new estimate - clears all state
  const handleNewEstimate = useCallback(() => {
    setSelectedEstimate(null);
    setConversationalEstimateId(null);
    setScopes(createDefaultScopes());
    setActiveScope("base");
    setScopeNotes("");
    setSuperintendentChat([]);
    setCollapsedGroups({});
    toast.success("Ready for new estimate");
  }, []);

  // Ensure we have estimate data before opening a document builder
  // FIX: Modal (Fix 3) handles the data fallback, so just open the builder
  const ensureEstimateAndOpenBuilder = useCallback((openBuilderFn) => {
    setMissionBriefOpen(false);
    openBuilderFn();
  }, []);

  // Calculate totals for all scopes
  const calculateAllTotals = useCallback(() => {
    const baseTotals = computeScopeTotals(scopes.base.groups);
    const coTotals = scopes.changeOrders.map((co) => ({
      id: co.id,
      name: co.name,
      number: co.number,
      ...computeScopeTotals(co.groups),
    }));
    
    const totalCOBuilder = coTotals.reduce((sum, co) => sum + co.builderTotal, 0);
    const totalCOClient = coTotals.reduce((sum, co) => sum + co.clientTotal, 0);
    
    return {
      base: baseTotals,
      changeOrders: coTotals,
      grandTotal: {
        builderTotal: baseTotals.builderTotal + totalCOBuilder,
        clientTotal: baseTotals.clientTotal + totalCOClient,
      },
    };
  }, [scopes]);

  const allTotals = calculateAllTotals();
  const groups = getActiveGroups();
  const currentScopeTotals = computeScopeTotals(groups);
  
  // Calculate Material vs Labor breakdown
  const calculateCostBreakdown = useCallback(() => {
    let materialCost = 0;
    let laborCost = 0;
    
    // Calculate from all scopes (base + change orders)
    const allGroups = [
      ...scopes.base.groups,
      ...scopes.changeOrders.flatMap(co => co.groups),
    ];
    
    allGroups.forEach(group => {
      (group.items || []).forEach(item => {
        const qty = parseFloat(item.quantity) || 0;
        const unitCost = parseFloat(item.unitCost) || 0;
        
        // FIX: Use separate materialCost and laborCost if provided by backend
        // This fixes the "deck can't build itself" issue where items have BOTH costs
        const itemMaterialCost = item.materialCost !== undefined ? parseFloat(item.materialCost) || 0 : null;
        const itemLaborCost = item.laborCost !== undefined ? parseFloat(item.laborCost) || 0 : null;
        
        if (itemMaterialCost !== null && itemLaborCost !== null) {
          // Backend provided separate costs - use them
          materialCost += qty * itemMaterialCost;
          laborCost += qty * itemLaborCost;
        } else {
          // Fallback: Use costType to categorize the entire cost
          const itemCost = qty * unitCost;
          if ((item.costType || "Material").toLowerCase() === "labor") {
            laborCost += itemCost;
          } else {
            materialCost += itemCost;
          }
        }
      });
    });
    
    return { materialCost, laborCost, totalCost: materialCost + laborCost };
  }, [scopes]);
  
  const costBreakdown = calculateCostBreakdown();
  
  // ═══════════════════════════════════════════════════════════════════════════
  // PERSIST SCOPES TO DATABASE - Solves the data loss on refresh issue
  // Uses REFS to always get latest values (avoids stale closure issues)
  // ═══════════════════════════════════════════════════════════════════════════
  const persistScopes = useCallback(async (scopesToSave) => {
    // FIX: Check for conversationalId fallback
    const activeEstId = selectedEstimate?.estimate_id || conversationalEstimateId;
    if (!activeEstId) {
      // console.warn("[Persist] No ID available to save.");
      return;
    }
    try {
      await API.post(`/estimates/${activeEstId}/save-scopes`, {
        scopes: scopesToSave || scopes,
      });
      console.log("[Persist] Auto-saved to:", activeEstId);
    } catch (err) {
      console.error("[Persist] Failed to save scopes:", err);
    }
  }, [selectedEstimate, conversationalEstimateId, scopes]);
  
  // Auto-save scopes when they change (debounced)
  const saveTimeoutRef = useRef(null);
  const batchOperationInProgressRef = useRef(false);
  useEffect(() => {
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    if (batchOperationInProgressRef.current) return;
    // FIX: Fallback ID check
    const activeEstId = selectedEstimate?.estimate_id || conversationalEstimateId;
    const hasItems = scopes.base.groups.length > 0 || scopes.changeOrders.length > 0;
    if (!hasItems || !activeEstId) return;
    saveTimeoutRef.current = setTimeout(() => {
      persistScopes(scopes);
    }, 2000); // 2s Debounce
    return () => clearTimeout(saveTimeoutRef.current);
  }, [scopes, selectedEstimate, conversationalEstimateId, persistScopes]);
  
  // Track last saved scopes to detect changes
  const lastSavedScopesRef = useRef(null);

  // Save on page unload/navigation - CRITICAL for data persistence
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      const estimateId = selectedEstimate?.estimate_id || conversationalEstimateId;
      const currentScopes = JSON.stringify(scopes);
      if (estimateId && lastSavedScopesRef.current !== currentScopes) {
        // Attempt to save synchronously using navigator.sendBeacon
        const hasItems = scopes.base.groups.length > 0 || scopes.changeOrders.length > 0;
        if (hasItems) {
          const API_BASE = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : "/api";
          const url = `${API_BASE}/estimates/${estimateId}/save-scopes`;
          const data = JSON.stringify({ scopes });
          navigator.sendBeacon(url, new Blob([data], { type: 'application/json' }));
          console.log("[AutoSave] Saved on page unload via sendBeacon");
        }
      }
    };
    
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [scopes, selectedEstimate, conversationalEstimateId]);

  // Manual Save Handler
  const handleSaveEstimate = useCallback(async () => {
    const currentEstimateId = selectedEstimate?.estimate_id || conversationalEstimateId;
    if (!currentEstimateId) {
      toast.warning("No estimate selected");
      return;
    }
    
    try {
      await persistScopes();
      toast.success("Estimate saved successfully");
    } catch (err) {
      console.error("Failed to save estimate:", err);
      toast.error("Failed to save estimate");
    }
  }, [selectedEstimate, conversationalEstimateId, persistScopes]);

  // Mission Threat Assessment calculation
  const calculateThreatLevel = useCallback(() => {
    const totalMarkup = allTotals.grandTotal.clientTotal - allTotals.grandTotal.builderTotal;
    const durationMonths = estDurationWeeks / 4;
    const gpVelocity = durationMonths > 0 ? totalMarkup / durationMonths : 0;
    
    // Calculate allowance percentage (items with "allowance" in name or generic items)
    let allowanceTotal = 0;
    const allGroups = [
      ...scopes.base.groups,
      ...scopes.changeOrders.flatMap(co => co.groups),
    ];
    
    allGroups.forEach(group => {
      (group.items || []).forEach(item => {
        const name = (item.itemName || "").toLowerCase();
        if (name.includes("allowance") || name.includes("contingency") || name.includes("misc")) {
          const qty = parseFloat(item.quantity) || 0;
          const unitCost = parseFloat(item.unitCost) || 0;
          const markup = parseFloat(item.markupPercent) || 0;
          allowanceTotal += qty * unitCost * (1 + markup / 100);
        }
      });
    });
    
    const allowancePercent = allTotals.grandTotal.clientTotal > 0 
      ? (allowanceTotal / allTotals.grandTotal.clientTotal) * 100 
      : 0;
    
    // Determine threat level based on settings
    const { targetGPPerMonth, minimumGPPerMonth, maxAllowancePercent } = riskSettings;
    
    let threatLevel = "green";
    let threatLabel = "MISSION GO";
    let threatMessage = "Project meets profit targets";
    
    if (gpVelocity < minimumGPPerMonth || allowancePercent > maxAllowancePercent * 1.5) {
      threatLevel = "red";
      threatLabel = "CRITICAL THREAT";
      threatMessage = gpVelocity < minimumGPPerMonth 
        ? `GP Velocity $${gpVelocity.toFixed(0)}/mo below minimum $${minimumGPPerMonth}`
        : `Allowance ${allowancePercent.toFixed(1)}% exceeds ${maxAllowancePercent * 1.5}%`;
    } else if (gpVelocity < targetGPPerMonth || allowancePercent > maxAllowancePercent) {
      threatLevel = "yellow";
      threatLabel = "RISK DETECTED";
      threatMessage = gpVelocity < targetGPPerMonth 
        ? `GP Velocity $${gpVelocity.toFixed(0)}/mo below target $${targetGPPerMonth}`
        : `Allowance ${allowancePercent.toFixed(1)}% exceeds ${maxAllowancePercent}%`;
    }
    
    return {
      gpVelocity,
      allowancePercent,
      allowanceTotal,
      threatLevel,
      threatLabel,
      threatMessage,
      totalMarkup,
    };
  }, [allTotals, estDurationWeeks, scopes, riskSettings]);
  
  const threatAssessment = calculateThreatLevel();
  
  // Define totals for the current scope view (used by footer and HUD)
  const totals = {
    builderTotal: currentScopeTotals.builderTotal,
    clientTotal: currentScopeTotals.clientTotal,
  };

  // Fetch initial data
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [leadsRes, clientsRes, estimatesRes, settingsRes, riskRes, companyRes] = await Promise.all([
          API.get("/leads").catch(() => ({ data: [] })),
          API.get("/clients").catch(() => ({ data: [] })),
          API.get("/estimates").catch(() => ({ data: [] })),
          API.get("/settings/global-estimating").catch(() => ({ data: null })),
          API.get("/settings/risk-tolerance").catch(() => ({ data: null })),
          API.get("/settings/company").catch(() => ({ data: null })),
        ]);

        setLeads(leadsRes.data || []);
        setClients(clientsRes.data || []);
        setEstimates(estimatesRes.data || []);
        setGlobalSettings(settingsRes.data);
        setCompanySettings(companyRes.data);
        
        // Load risk settings if available
        if (riskRes.data) {
          setRiskSettings(prev => ({
            ...prev,
            ...riskRes.data,
          }));
        }

        // DO NOT auto-select a lead - user should manually select from dropdown
        // Leads are available in the dropdown but nothing is pre-selected
        if (leadsRes.data && leadsRes.data.length > 0) {
          // Leads loaded successfully, but don't auto-select
          console.log(`[Estimating] ${leadsRes.data.length} leads available for selection`);
        }
      } catch (err) {
        console.error("Failed to fetch data:", err);
        toast.error("Failed to load data");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Load estimate data
  const loadEstimateData = useCallback(async (estimateId) => {
    if (!estimateId) return;
    
    setEstimateDataLoading(true); // Start loading
    try {
      const res = await API.get(`/estimates/${estimateId}/takeoff-data`);
      const data = res.data || {};
      
      // Check if we have full scopes_data (includes change orders)
      if (data.scopes_data && data.scopes_data.base) {
        console.log("[loadEstimateData] Loading from scopes_data (full UI state)");
        const scopesData = data.scopes_data;
        
        // Restore the full scopes structure including change orders
        setScopes({
          base: {
            groups: (scopesData.base?.groups || []).map((g, index) => ({
              ...g,
              id: g.id || String(index + 1).padStart(2, "0"),
              code: g.code || String(index + 1).padStart(2, "0"),
              name: g.name || `Section ${index + 1}`,
              items: (g.items || []).map(item => ({
                ...item,
                costType: item.costType || detectCostType(item.itemName),
              })),
            })),
          },
          changeOrders: (scopesData.changeOrders || []).map(co => ({
            ...co,
            groups: (co.groups || []).map((g, index) => ({
              ...g,
              id: g.id || String(index + 1).padStart(2, "0"),
              items: (g.items || []).map(item => ({
                ...item,
                costType: item.costType || detectCostType(item.itemName),
              })),
            })),
          })),
        });
        
        const totalItems = (scopesData.base?.groups || []).reduce((sum, g) => sum + (g.items?.length || 0), 0);
        const coCount = (scopesData.changeOrders || []).length;
        console.log("[loadEstimateData] Restored:", totalItems, "base items,", coCount, "change orders");
        return;
      }
      
      // Fallback: Load from grouped_items format
      const grouped = data.grouped_items || {};

      const newGroups = Object.entries(grouped).map(([category, items], index) => ({
        id: String(index + 1).padStart(2, "0"),
        code: String(index + 1).padStart(2, "0"),
        name: formatSectionName(index, category), // FIX: Use cleaner to prevent recursive numbering
        // FIX: Use backend's costType if provided, only detect if missing
        items: (items || []).map(item => ({
          ...item,
          costType: item.costType || detectCostType(item.itemName),
        })),
      }));

      console.log("[loadEstimateData] Loaded groups:", newGroups.length, "with items:", newGroups.reduce((sum, g) => sum + (g.items?.length || 0), 0));
      
      // Always update base scope from backend truth, even if there are no groups
      setScopes((prev) => ({
        ...prev,
        base: { ...prev.base, groups: newGroups },
      }));
    } catch (err) {
      console.error("Failed to load estimate data:", err);
    } finally {
      setEstimateDataLoading(false); // End loading
    }
  }, []);

  // Idempotently apply deterministic DECK_PARAMS tokens to a scope string.
  // - Strips any existing [DECK_PARAMS] lines first (case + whitespace safe)
  // - Only emits tokens for non-null, >0 values (for count-based fields)
  const applyDeckParamsToScope = (scopeDescription, params) => {
    const rawScope = scopeDescription || "";

    // Remove any existing [DECK_PARAMS] line(s) safely (line-based filter)
    const lines = rawScope.split("\n");
    const filteredLines = lines.filter(
      (line) =>
        !line
          .trimStart()
          .toUpperCase()
          .startsWith("[DECK_PARAMS]")
    );
    const baseScope = filteredLines.join("\n").trim();

    if (!params || typeof params !== "object") return baseScope;

    const tokens = [];

    if (params.material_type) {
      tokens.push(`material_type=${params.material_type}`);
    }

    if (params.height_off_ground_ft != null) {
      tokens.push(`height_off_ground_ft=${params.height_off_ground_ft}`);
    }

    if (
      params.ledger_or_freestanding &&
      (params.ledger_or_freestanding === "ledger" ||
        params.ledger_or_freestanding === "freestanding")
    ) {
      tokens.push(`ledger_or_freestanding=${params.ledger_or_freestanding}`);
    }

    // Railing / stairs: use null to represent "none"; only emit when > 0
    if (
      typeof params.railing_sides === "number" &&
      params.railing_sides > 0
    ) {
      tokens.push(`railing_sides=${params.railing_sides}`);
    }

    if (
      typeof params.stairs_risers === "number" &&
      params.stairs_risers > 0
    ) {
      tokens.push(`stairs_risers=${params.stairs_risers}`);
    }

    if (params.demo_existing != null) {
      tokens.push(`demo_existing=${params.demo_existing}`);
    }
    if (params.demo_disposal != null) {
      tokens.push(`demo_disposal=${params.demo_disposal}`);
    }
    if (params.demo_scope) {
      tokens.push(`demo_scope=${params.demo_scope}`);
    }

    if (!tokens.length) return baseScope;

    const tokenString = tokens.join("; ");
    return `${baseScope}\n\n[DECK_PARAMS] ${tokenString}`;
  };

  // Canonical deck refinement runner (no LLM):
  // - Build scope_notes via applyDeckParamsToScope(...)
  // - Persist updated scope_notes locally
  // - POST /api/estimates/{id}/auto-build
  // - loadEstimateData(id)
  // - Respond with short confirmation (no item IDs, no questions)
  const runDeckRefinement = async (nextParams, sourceLabel = "refinement") => {
    const currentEstimateId = selectedEstimate?.estimate_id || conversationalEstimateId;
    if (!currentEstimateId) {
      console.warn("[DeckRefinement] No selected estimate; skipping deterministic refinement");
      return;
    }

    const baseScopeText =
      deckBaseScope ||
      scopeNotes ||
      selectedEstimate?.project_description ||
      selectedEstimate?.job_name ||
      "Deck scope";

    const mergedScope = applyDeckParamsToScope(baseScopeText, nextParams);

    // Start refinement UI state (do NOT touch deckHas* flags here)
    setDeckRefinementActive(true);
    setDeckParams(nextParams);
    setSuperintendentLoading(true);
    setScopeNotes(mergedScope);

    try {
      await API.post(`/estimates/${currentEstimateId}/auto-build`, {
        scope_notes: mergedScope,
        use_material_db: true,
      });

      await loadEstimateData(currentEstimateId);

      const confirmMessage =
        "Deck estimate updated and rebuilt using your refinement. No item IDs required.";

      setSuperintendentChat((prev) => [
        ...prev,
        {
          role: "assistant",
          content: confirmMessage,
          timestamp: new Date().toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
      ]);
    } catch (err) {
      console.error("[DeckRefinement] Failed to apply refinement:", err);
      setSuperintendentChat((prev) => [
        ...prev,
        {
          role: "assistant",
          content:
            "I had trouble updating the deck estimate. The existing estimate is still valid—try again or adjust manually if needed.",
          timestamp: new Date().toLocaleTimeString("en-US", {
            hour: "2-digit",
            minute: "2-digit",
          }),
        },
      ]);
    } finally {
      setSuperintendentLoading(false);
    }
  };

  // Handle lead selection change
  const handleLeadChange = async (leadId) => {
    const lead = leads.find((l) => l.lead_id === leadId);
    setSelectedLead(lead || null);
    
    // FIX: Don't auto-link estimates! The useEffect will load the correct estimates.
    // When user selects a customer, the useEffect [selectedLead] will:
    // 1. Query estimates filtered by lead_id
    // 2. Auto-select the most recent estimate for that customer
    // 3. Load the scopes from that estimate
    
    // Clear current estimate state - useEffect will load the right one
    if (!lead) {
      // No customer selected - clear everything
      setSelectedEstimate(null);
      setConversationalEstimateId(null);
      setScopes(createDefaultScopes());
    }
    // If lead is selected, useEffect [selectedLead] will handle loading
  };

  // Handle estimate selection change
  const handleEstimateChange = async (estimateId) => {
    const estimate = estimates.find((e) => e.estimate_id === estimateId);
    setSelectedEstimate(estimate || null);
    
    if (estimate) {
      await loadEstimateData(estimate.estimate_id);
      
      if (estimate.lead_id) {
        const linkedLead = leads.find((l) => l.lead_id === estimate.lead_id);
        if (linkedLead) {
          setSelectedLead(linkedLead);
        }
      }
    }
  };

  // Handle Auto-Build from Takeoff
  // DEPRECATED: older auto-build path. Use handleAutoBuild instead.
  const handleAutoBuildFromTakeoff = async () => {
    let estimateToUse = selectedEstimate;
    
    // If no estimate selected, auto-create one
    if (!estimateToUse) {
      setInitializing(true);
      try {
        const dateStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const clientName = selectedLead?.contact_name || selectedLead?.name || "New Client";
        const newEstimateData = {
          project_description: scopeNotes || selectedLead?.service_type || selectedLead?.project_description || `Scope for ${clientName}`,
          job_name: `${clientName} - Scope ${dateStr}`,
          lead_id: selectedLead?.lead_id || null,
          client_id: selectedLead?.client_id || null,
          job_address: selectedLead?.address || selectedLead?.project_address || null,
        };
        
        const createRes = await API.post("/estimates", newEstimateData);
        estimateToUse = createRes.data;
        setSelectedEstimate(estimateToUse);
        setEstimates(prev => [...prev, estimateToUse]);
        toast.success("Mission file initialized");
      } catch (err) {
        console.error("Failed to create estimate:", err);
        toast.error("Failed to initialize mission file");
        setInitializing(false);
        return;
      } finally {
        setInitializing(false);
      }
    }

    setGenerating(true);
    try {
      // Use the enhanced AI Auto-Build endpoint
      const res = await API.post(`/estimates/${estimateToUse.estimate_id}/auto-build`, {
        scope_notes: scopeNotes || selectedLead?.project_description || selectedLead?.service_type || "",
      });
      const data = res.data;
      
      if (data && data.grouped_items && Object.keys(data.grouped_items).length > 0) {
        const newGroups = Object.entries(data.grouped_items).map(([category, items], index) => ({
          id: String(index + 1).padStart(2, "0"),
          code: String(index + 1).padStart(2, "0"),
          name: formatSectionName(index, category), // FIX: Use cleaner to prevent recursive numbering
          items: items.map((item, itemIdx) => ({
            ...item,
            id: item.id || `${String(index + 1).padStart(2, "0")}-${String(itemIdx + 1).padStart(3, "0")}`,
          })),
        }));
        
        // Force immediate UI update
        setScopes((prev) => ({
          ...prev,
          base: { ...prev.base, groups: newGroups },
        }));
        setActiveScope("base");
        
        // Force a re-render by toggling collapsed state
        setCollapsedGroups({});
        
        const totalItems = newGroups.reduce((sum, g) => sum + (g.items?.length || 0), 0);
        toast.success("🎯 Mission Plan Updated", {
          description: data.message || `${newGroups.length} sections with ${totalItems} items generated`,
        });
        
        // === TRIGGER SITE SUPERINTENDENT ===
        // Collect all line items for analysis
        const allLineItems = newGroups.flatMap(g => g.items || []);
        triggerSuperintendentAnalysis(allLineItems, scopeNotes || selectedLead?.project_description || "");
        
      } else {
        // Fallback if no items generated
        toast.info("No items generated. Try adding more detail to the scope notes.");
      }
    } catch (err) {
      console.error("Auto-build failed:", err);
      toast.error("Failed to build estimate. Please try again.");
    } finally {
      setGenerating(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // GRANDMASTER ESTIMATOR - Project Assessment & Full Build
  // ═══════════════════════════════════════════════════════════════════
  
  // Trigger the Grandmaster Assessment Modal
  const handleGrandmasterAssessment = async () => {
    if (!scopeNotes && !selectedLead?.project_description) {
      toast.error("Please enter a scope description first");
      return;
    }
    
    setGrandmasterLoading(true);
    try {
      const existingItems = getActiveGroups()?.flatMap(g => g.items || []) || [];
      
      const res = await API.post("/ai/project-assessment", {
        project_description: selectedLead?.project_description || scopeNotes,
        scope_notes: scopeNotes,
        room_type: "", // Let AI detect
        existing_items: existingItems,
      });
      
      if (res.data.success) {
        setProjectAssessment(res.data);
        
        // Initialize the checklist state from assessment
        setAssessmentChecklist({
          demolition: res.data.assessment.implied_demolition || [],
          risks: res.data.assessment.risk_factors || [],
          missing: res.data.assessment.missing_scope || [],
        });
        
        setGrandmasterModalOpen(true);
      }
    } catch (err) {
      console.error("Assessment failed:", err);
      toast.error("Failed to generate project assessment");
    } finally {
      setGrandmasterLoading(false);
    }
  };
  
  // Toggle checkbox in assessment checklist
  const toggleAssessmentItem = (section, itemId) => {
    setAssessmentChecklist(prev => ({
      ...prev,
      [section]: prev[section].map(item => 
        item.id === itemId ? { ...item, checked: !item.checked } : item
      ),
    }));
  };
  
  // Execute the full Grandmaster build
  const handleGrandmasterBuild = async () => {
    setGrandmasterLoading(true);
    try {
      let estimateToUse = selectedEstimate;
      
      // Auto-create estimate if needed
      if (!estimateToUse) {
        const dateStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const clientName = selectedLead?.contact_name || selectedLead?.name || "New Client";
        const newEstimateData = {
          project_description: scopeNotes || selectedLead?.project_description,
          job_name: `${clientName} - Grandmaster ${dateStr}`,
          lead_id: selectedLead?.lead_id || null,
        };
        
        const createRes = await API.post("/estimates", newEstimateData);
        estimateToUse = createRes.data;
        setSelectedEstimate(estimateToUse);
        setEstimates(prev => [...prev, estimateToUse]);
      }
      
      const res = await API.post("/ai/grandmaster-build", {
        estimate_id: estimateToUse.estimate_id,
        project_description: selectedLead?.project_description || scopeNotes,
        scope_notes: scopeNotes,
        room_type: projectAssessment?.room_type || "general",
        demolition_items: assessmentChecklist.demolition,
        risk_items: assessmentChecklist.risks,
        missing_items: assessmentChecklist.missing,
      });
      
      if (res.data.success) {
        // Update the UI with generated scope
        const newGroups = res.data.scope_groups.map(g => ({
          ...g,
          name: g.name.includes(". ") ? g.name : `${g.code}. ${g.name}`,
        }));
        
        setScopes((prev) => ({
          ...prev,
          base: { ...prev.base, groups: newGroups },
        }));
        setActiveScope("base");
        setCollapsedGroups({});
        
        setGrandmasterModalOpen(false);
        
        toast.success("🎖️ GRANDMASTER ESTIMATE COMPLETE", {
          description: res.data.message,
        });
      }
    } catch (err) {
      console.error("Grandmaster build failed:", err);
      toast.error("Failed to generate estimate");
    } finally {
      setGrandmasterLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // SITE SUPERINTENDENT - Conversational AI Partner
  // ═══════════════════════════════════════════════════════════════════
  
  // Trigger Site Superintendent analysis after Auto-Build
  const assertLLMAllowed = (intentLabel, contextLabel = "") => {
    if (intentLabel === "deck_refine" || intentLabel === "pricing_adjust") {
      const msg = `[LLM_FORBIDDEN] Blocked LLM call for intent=${intentLabel} ${contextLabel}`.trim();
      console.error(msg);
      throw new Error(msg);
    }
  };
  
  const triggerSuperintendentAnalysis = async (lineItems, projectDescription) => {
    const newSessionId = `super_${Date.now()}`;
    setSuperintendentSessionId(newSessionId);
    setSuperintendentLoading(true);
    setSuperintendentOpen(true);
    setSuperintendentMinimized(false);
    
    try {
      assertLLMAllowed(lastSuperintendentIntentRef.current, "triggerSuperintendentAnalysis");
      const res = await API.post("/ai/superintendent/analyze", {
        session_id: newSessionId,
        project_description: projectDescription,
        scope_notes: scopeNotes,
        line_items: lineItems,
        room_type: "",
      });
      
      if (res.data.success) {
        // Add the analysis message to chat
        const timestamp = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        setSuperintendentChat([{
          role: "assistant",
          content: res.data.message,
          suggestions: res.data.suggestions,
          timestamp,
        }]);
      }
    } catch (err) {
      console.error("Superintendent analysis failed:", err);
      setSuperintendentChat([{
        role: "assistant",
        content: "I'm having trouble analyzing the estimate right now. Let me know if you need any help!",
        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      }]);
    } finally {
      setSuperintendentLoading(false);
    }
  };
  
  // Handle sending messages to the Superintendent - COMMAND CENTER LOGIC
  // Restored implementation will be pasted here incrementally.
  let handleSuperintendentMessage_impl = null;
  const lastSuperintendentIntentRef = useRef(null);
  const COPILOT_TRACE = process.env.REACT_APP_COPILOT_TRACE === "true"; // TEMP: env-gated trace


  // Stable wrapper keeps JSX call-site unchanged.
  const handleSuperintendentMessage = async (message) => {
    if (typeof handleSuperintendentMessage_impl !== "function") {
      console.warn("[TEMP] handleSuperintendentMessage disabled while repairing delimiter mismatch.");
      toast.error(
        "Copilot temporarily disabled (build stabilization). This is expected while we repair the core command-center handler."
      );
      return;
    }
    return handleSuperintendentMessage_impl(message);
  };

  function classifyModifyIntent(text) {
    const t = (text || "").toLowerCase();
  
    // Deck is handled earlier; do not classify as deck here.
    const isRemove = /\b(remove|delete|drop|exclude|take out|eliminate)\b/.test(t);
    const isAdd = /\b(add|include|also include|plus|install|put in)\b/.test(t);
    const isUpgrade = /\b(upgrade|better|premium|higher grade|swap|change to)\b/.test(t);
  
    // Pricing/GP (only if you have a stable path to apply)
    const mentionsPricing = /\b(markup|margin|gross profit|gp|profit|price|overhead)\b/.test(t);
    const hasPercent = /(\d+(\.\d+)?)\s*%/.test(t);

    // Construction instructions detection
    const constructionKeywords = [
      "step by step", "instructions", "how to build", 
      "construction sequence", "build order", "installation",
      "how do i construct", "what's the process", "construction steps",
      "build process", "how to install"
    ];
    const isConstructionRequest = constructionKeywords.some(kw => t.includes(kw));
    if (isConstructionRequest) {
      return { kind: "construction_instructions", text };
    }
  
    if (mentionsPricing && hasPercent) {
      return { kind: "pricing_adjust", text };
    }
    if (isRemove) return { kind: "remove", text };
    if (isAdd) return { kind: "add", text };
    if (isUpgrade) return { kind: "upgrade", text };
  
    return { kind: "scope_change", text };
  }
  
  // ═══════════════════════════════════════════════════════════════════════════
  // EMERGENCY FIX: Complete replacement of handleSuperintendentMessage_impl
  // This calls /ai/superintendent/chat API instead of broken /auto-build
  // ═══════════════════════════════════════════════════════════════════════════
  handleSuperintendentMessage_impl = async (message) => {
    if (!message?.trim()) {
        console.warn("Empty message received");
        return;
    }
    
    console.log("🤖 Superintendent message received:", message);
    
    setSuperintendentLoading(true);
    
    // Add user message to chat immediately
    setSuperintendentChat(prev => [...prev, { 
        role: "user", 
        content: message 
    }]);
    
    try {
        // ═══════════════════════════════════════════════════════════════════
        // 1. GET CURRENT ESTIMATE CONTEXT
        // ═══════════════════════════════════════════════════════════════════
        const estimateId = selectedEstimate?.estimate_id || conversationalEstimateId;
        
        // Check if this is a BUILD request (empty estimate)
        const currentGroups = getActiveGroups();
        const currentItems = currentGroups.flatMap(g => 
            (g.items || []).map(item => ({
                id: item.id || item.itemId,
                itemName: item.itemName || "Unnamed Item",
                description: item.description || "",
                quantity: item.quantity || 0,
                unit: item.unit || "EA",
                unitCost: item.unitCost || 0,
                costType: item.costType || "Labor",
                markupPercent: item.markupPercent || 0,
                group: g.id,
                groupName: g.name
            }))
        );
        
        const estimateIsEmpty = currentItems.length === 0;
        
        // ═══════════════════════════════════════════════════════════════════
        // GET MAIN ESTIMATE CONTEXT (for Change Orders)
        // ═══════════════════════════════════════════════════════════════════
        let mainEstimateContext = null;
        const isChangeOrder = activeScope !== "base";
        
        if (isChangeOrder && scopes.base && scopes.base.groups) {
            // Collect all items from the Main Estimate with FULL details
            const mainEstimateItems = scopes.base.groups.flatMap(g => 
                (g.items || []).map(item => ({
                    itemName: item.itemName || "Unnamed Item",
                    description: item.description || "",
                    quantity: item.quantity || 0,
                    unit: item.unit || "EA",
                    unitCost: item.unitCost || 0,
                    costType: item.costType || "Labor",
                    groupName: g.name
                }))
            );
            
            if (mainEstimateItems.length > 0) {
                // Calculate main estimate total
                const mainEstimateTotal = mainEstimateItems.reduce((sum, item) => {
                    const builderCost = (item.quantity || 0) * (item.unitCost || 0);
                    return sum + builderCost;
                }, 0);
                
                // Build a detailed item list with descriptions
                const detailedItemList = mainEstimateItems.map(item => {
                    let detail = `- ${item.itemName}: ${item.quantity} ${item.unit} @ $${item.unitCost}`;
                    if (item.description) {
                        detail += ` (${item.description})`;
                    }
                    return detail;
                }).join("\n");
                
                mainEstimateContext = {
                    itemCount: mainEstimateItems.length,
                    total: mainEstimateTotal,
                    items: mainEstimateItems,
                    // Include the original project description/scope notes which has dimensions
                    projectDescription: scopeNotes || selectedEstimate?.project_description || selectedEstimate?.job_name || "",
                    detailedItemList: detailedItemList,
                    summary: mainEstimateItems.map(item => 
                        `${item.itemName} (${item.quantity} ${item.unit} @ $${item.unitCost})`
                    ).join(", ")
                };
                
                console.log("📋 Main Estimate context for CO:", {
                    itemCount: mainEstimateContext.itemCount,
                    total: mainEstimateTotal,
                    projectDescription: mainEstimateContext.projectDescription
                });
            }
        }
        
        console.log("📦 Current estimate context:", {
            estimateId,
            groupCount: currentGroups.length,
            itemCount: currentItems.length,
            isEmpty: estimateIsEmpty,
            isChangeOrder,
            hasMainEstimateContext: !!mainEstimateContext
        });
        
        // ═══════════════════════════════════════════════════════════════════
        // BUILD MODE: If estimate is empty, use Superintendent V2 to build
        // ═══════════════════════════════════════════════════════════════════
        if (estimateIsEmpty) {
            console.log("📋 Empty estimate - routing to Superintendent V2 build mode");
            setScopeNotes(message);
            setSuperintendentChat(prev => [
                ...prev,
                { type: "action", content: "Analyzing scope..." },
            ]);
            
            try {
                // Build conversation context from chat history
                // This ensures the AI knows what was already discussed
                const chatHistory = superintendentChat
                    .filter(msg => msg.role === "user" || msg.role === "assistant")
                    .map(msg => `${msg.role === "user" ? "User" : "Assistant"}: ${msg.content}`)
                    .join("\n");
                
                // Build the message with context
                let fullMessage = message;
                
                // If this is a Change Order and we have main estimate context, prepend it
                if (isChangeOrder && mainEstimateContext) {
                    const coInfo = scopes.changeOrders.find(co => co.id === activeScope);
                    const coNumber = coInfo?.number || "?";
                    
                    const mainEstimateSummary = `
=== CHANGE ORDER #${coNumber} - REFERENCE: ORIGINAL MAIN ESTIMATE ===
ORIGINAL PROJECT: ${mainEstimateContext.projectDescription || "Construction Project"}
MAIN ESTIMATE TOTAL: $${mainEstimateContext.total.toLocaleString()} (${mainEstimateContext.itemCount} items)

EXISTING ITEMS IN MAIN ESTIMATE:
${mainEstimateContext.detailedItemList}

CRITICAL INSTRUCTIONS FOR THIS CHANGE ORDER:
1. The dimensions and specifications above are from the ORIGINAL estimate - use them as reference
2. This Change Order should include ONLY NEW or ADDITIONAL items
3. Do NOT duplicate items already in the main estimate
4. Reference the original project dimensions when calculating new additions
5. ***YOU MUST ASK QUALIFYING QUESTIONS about the NEW work before generating items***
   - The main estimate context tells you what EXISTS, not what the user WANTS for the new work
   - Ask about materials, finishes, specifications for the NEW requested item
=== END MAIN ESTIMATE CONTEXT ===

CHANGE ORDER REQUEST: `;
                    fullMessage = mainEstimateSummary + message;
                }
                
                // Add chat history if exists
                if (chatHistory) {
                    fullMessage = `Previous conversation:\n${chatHistory}\n\nUser's latest message: ${fullMessage}`;
                }
                
                console.log("📝 Sending with context:", fullMessage.substring(0, 500) + "...");
                
                // Use V2 API for build mode with extended timeout (deck estimates need time for tool calls)
                // Note: Global API timeout is 180s, but we extend for this specific call
                const res = await API.post("/ai/superintendent/v2/chat", {
                    message: fullMessage,
                    session_id: superintendentSessionId,
                    estimate_id: estimateId,
                    current_items: [],
                    zip_code: "66101",
                    is_change_order: isChangeOrder,
                    main_estimate_context: mainEstimateContext
                }, { 
                    timeout: 180000 // 3 minute timeout for complex estimates with many tool calls
                });
                
                const data = res.data;
                console.log("📥 Build mode response:", {
                    success: data.success,
                    status: data.status,
                    model: data.model_used,
                    hasQuestions: !!data.questions,
                    itemCount: data.items_to_add?.length || 0
                });
                
                // ═══════════════════════════════════════════════════════════════
                // HANDLE CLARIFYING QUESTIONS
                // ═══════════════════════════════════════════════════════════════
                if (data.status === "needs_clarification" || data.questions) {
                    const questions = data.questions || [];
                    const questionsText = questions.length > 0
                        ? `I need a few details to build an accurate estimate:\n\n${questions.map((q, i) => `${i + 1}. ${q}`).join('\n')}`
                        : data.message || "Could you provide more details about the project?";
                    
                    setSuperintendentChat(prev => [...prev, {
                        role: "assistant",
                        content: questionsText,
                        clarification_questions: questions,
                    }]);
                    
                    setSuperintendentLoading(false);
                    return;
                }
                
                // ═══════════════════════════════════════════════════════════════
                // HANDLE ITEMS_TO_ADD
                // ═══════════════════════════════════════════════════════════════
                if (data.items_to_add && data.items_to_add.length > 0) {
                    console.log(`➕ Building estimate with ${data.items_to_add.length} items for scope: ${activeScope}`);
                    
                    // Build the new scopes object
                    const buildNewScopes = (prevScopes) => {
                        const newScopes = JSON.parse(JSON.stringify(prevScopes));
                        
                        // Determine target scope based on activeScope
                        let targetScope;
                        if (activeScope === "base") {
                            if (!newScopes.base) {
                                newScopes.base = { groups: [] };
                            }
                            if (!newScopes.base.groups) {
                                newScopes.base.groups = [];
                            }
                            targetScope = newScopes.base;
                        } else {
                            const coIndex = newScopes.changeOrders.findIndex(co => co.id === activeScope);
                            if (coIndex >= 0) {
                                if (!newScopes.changeOrders[coIndex].groups) {
                                    newScopes.changeOrders[coIndex].groups = [];
                                }
                                targetScope = newScopes.changeOrders[coIndex];
                            } else {
                                console.warn(`⚠️ Change Order ${activeScope} not found, using base`);
                                targetScope = newScopes.base;
                            }
                        }
                        
                        data.items_to_add.forEach(newItem => {
                            const targetSectionName = newItem.category || newItem.section || "Miscellaneous";
                            
                            let targetGroup = targetScope.groups.find(g => 
                                g.name === targetSectionName || 
                                (g.name || "").toLowerCase() === targetSectionName.toLowerCase()
                            );
                            
                            if (!targetGroup) {
                                const newGroupId = targetSectionName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                                targetGroup = {
                                    id: newGroupId,
                                    name: targetSectionName,
                                    items: []
                                };
                                targetScope.groups.push(targetGroup);
                                console.log(`📁 Created group: ${targetSectionName}`);
                            }
                            
                            const newItemObj = {
                                id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                                itemId: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                                itemName: newItem.itemName || newItem.item_name || "New Item",
                                description: newItem.description || "",
                                quantity: parseFloat(newItem.quantity) || 1,
                                unit: newItem.unit || "EA",
                                unitCost: parseFloat(newItem.unitCost || newItem.unit_cost || 0),
                                costType: newItem.costType || newItem.cost_type || "Material",
                                markupPercent: parseFloat(newItem.markupPercent || newItem.markup || 0),
                                pricingSource: newItem.pricingSource || ""
                            };
                            
                            targetGroup.items.push(newItemObj);
                        });
                        
                        return newScopes;
                    };
                    
                    // Build the scopes
                    const newScopes = buildNewScopes(scopesRef.current);
                    setScopes(newScopes);
                    
                    const scopeName = activeScope === "base" ? "Main Estimate" : `Change Order ${activeScope}`;
                    console.log(`✅ Built estimate with ${data.items_to_add.length} items for ${scopeName}`);
                    
                    // CRITICAL FIX: Create estimate in database and save scopes
                    // This ensures data persists across page refreshes
                    if (!conversationalEstimateId && !selectedEstimate?.estimate_id) {
                        // ✅ VALIDATION: Require customer selection before creating estimate
                        if (!selectedLead?.lead_id && !selectedLead?.client_id) {
                            console.warn("[V2 Build] No customer selected - prompting user");
                            setSuperintendentChat(prev => [...prev, {
                                role: "assistant",
                                content: "⚠️ **Please select a customer first** from the top-left dropdown before I can create and save your estimate. This ensures your work is properly saved and linked to the right customer.\n\nOnce you've selected a customer, say 'continue' and I'll build your estimate."
                            }]);
                            setSuperintendentLoading(false);
                            return; // Stop here until customer is selected
                        }
                        
                        try {
                            console.log("[V2 Build] Creating estimate in database...");
                            const dateStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                            const clientName = selectedLead?.contact_name || selectedLead?.name || "New Client";
                            
                            const createRes = await API.post("/estimates", {
                                project_description: message,
                                job_name: `${clientName} - ${dateStr}`,
                                lead_id: selectedLead?.lead_id || null,
                                client_id: selectedLead?.client_id || null,
                                job_address: selectedLead?.address || null,
                            });
                            
                            const newEstimate = createRes.data;
                            console.log("[V2 Build] Estimate created:", newEstimate.estimate_id);
                            
                            // Set the estimate ID for auto-save
                            setConversationalEstimateId(newEstimate.estimate_id);
                            setSelectedEstimate(newEstimate);
                            setEstimates(prev => [...prev, newEstimate]);
                            
                            // Save scopes immediately
                            await API.post(`/estimates/${newEstimate.estimate_id}/save-scopes`, { scopes: newScopes });
                            console.log("[V2 Build] Scopes saved to database");
                            
                            toast.success("Estimate created and saved");
                        } catch (createErr) {
                            console.error("[V2 Build] Failed to create/save estimate:", createErr);
                            toast.error("Warning: Estimate may not be saved. Please save manually.");
                        }
                    } else {
                        // Estimate already exists, just save the updated scopes
                        const estId = selectedEstimate?.estimate_id || conversationalEstimateId;
                        try {
                            await API.post(`/estimates/${estId}/save-scopes`, { scopes: newScopes });
                            console.log("[V2 Build] Updated scopes saved to:", estId);
                        } catch (saveErr) {
                            console.error("[V2 Build] Failed to save scopes:", saveErr);
                        }
                    }
                    
                    const scopeLabel = activeScope === "base" ? "estimate" : "Change Order";
                    setSuperintendentChat(prev => [...prev, {
                        role: "assistant",
                        content: data.message || `I've created a detailed ${data.items_to_add.length}-line item ${scopeLabel}. The items are now in your ${scopeLabel} below.`,
                    }]);
                } else {
                    // No items - show the response message (might be questions or error)
                    setSuperintendentChat(prev => [...prev, {
                        role: "assistant",
                        content: data.message || "I need more details to build this estimate. What are the dimensions and materials you want?",
                    }]);
                }
                
            } catch (buildErr) {
                console.error("Build mode failed:", buildErr);
                const errorMsg = buildErr.name === 'AbortError' || buildErr.code === 'ECONNABORTED'
                    ? "The request took too long. Let me try with simpler parameters - what are the basic dimensions of your project?"
                    : "I had trouble building that estimate. Could you give me more details about the project?";
                    
                setSuperintendentChat(prev => [...prev, {
                    role: "assistant",
                    content: errorMsg,
                }]);
            }
            setSuperintendentLoading(false);
            return;
        }
        
        // ═══════════════════════════════════════════════════════════════════
        // 2. CALL SUPERINTENDENT V2 API (Gemini-powered with native tool calling)
        // ═══════════════════════════════════════════════════════════════════
        console.log("📡 Calling /ai/superintendent/v2/chat API (Gemini)...");
        
        // Build modified message with main estimate context for Change Orders
        let modifyMessage = message;
        if (isChangeOrder && mainEstimateContext) {
            const coInfo = scopes.changeOrders.find(co => co.id === activeScope);
            const coNumber = coInfo?.number || "?";
            
            modifyMessage = `[CHANGE ORDER #${coNumber}]
ORIGINAL PROJECT: ${mainEstimateContext.projectDescription || "Construction Project"}
MAIN ESTIMATE: $${mainEstimateContext.total.toLocaleString()} (${mainEstimateContext.itemCount} items)
ORIGINAL ITEMS:
${mainEstimateContext.detailedItemList.substring(0, 1500)}

CHANGE ORDER REQUEST: ${message}`;
        }
        
        const res = await API.post("/ai/superintendent/v2/chat", {
            message: modifyMessage,
            session_id: superintendentSessionId,
            estimate_id: estimateId,
            current_items: currentItems,
            zip_code: "66101",
            is_change_order: isChangeOrder,
            main_estimate_context: mainEstimateContext
        });
        
        const data = res.data;
        
        console.log("📥 Superintendent response:", {
            intent: data.intent,
            hasRateChanges: !!data.rate_changes && Object.keys(data.rate_changes).length > 0,
            hasItemsToRemove: !!data.items_to_remove && data.items_to_remove.length > 0,
            hasItemsToAdd: !!data.items_to_add && data.items_to_add.length > 0,
            hasItemsToModify: !!data.items_to_modify && data.items_to_modify.length > 0
        });
        
        // ═══════════════════════════════════════════════════════════════════
        // 3. APPLY CHANGES TO ESTIMATE
        // ═══════════════════════════════════════════════════════════════════
        
        let changesApplied = false;
        
        // ───────────────────────────────────────────────────────────────────
        // 3A. HANDLE DELETIONS (items_to_remove)
        // ───────────────────────────────────────────────────────────────────
        if (data.items_to_remove && data.items_to_remove.length > 0) {
            console.log("🗑️ Processing deletions:", data.items_to_remove);
            
            setScopes(prevScopes => {
                const newScopes = JSON.parse(JSON.stringify(prevScopes)); // Deep clone
                
                const removeFromScope = (scopeNode) => {
                    if (!scopeNode || !scopeNode.groups) return;
                    
                    scopeNode.groups.forEach(group => {
                        const originalCount = (group.items || []).length;
                        
                        if (data.items_to_remove.includes("all") || 
                            data.items_to_remove.includes("*")) {
                            // Delete ALL items from this group
                            group.items = [];
                            console.log(`✅ Deleted all ${originalCount} items from group ${group.id}`);
                        } else {
                            // Delete specific items - normalize IDs for comparison
                            const removeIdsNormalized = data.items_to_remove.map(id => 
                                String(id || "").toLowerCase().trim()
                            );
                            
                            group.items = (group.items || []).filter(item => {
                                const itemId = String(item.id || item.itemId || "").toLowerCase().trim();
                                const itemName = String(item.itemName || "").toLowerCase().trim();
                                
                                // Check if this item should be removed
                                const shouldRemove = removeIdsNormalized.some(removeId => {
                                    // Exact ID match
                                    if (removeId === itemId) return true;
                                    // Name contains removeId (for semantic removal like "composite", "trex")
                                    if (itemName.includes(removeId) || removeId.includes(itemName)) return true;
                                    // Check for partial item name match (for cases like "trex" matching "Trex Enhance...")
                                    const removeWords = removeId.split(/[\s_-]+/);
                                    if (removeWords.some(word => word.length > 3 && itemName.includes(word))) return true;
                                    return false;
                                });
                                
                                if (shouldRemove) {
                                    console.log(`✅ Removed: ${item.itemName} (id: ${itemId})`);
                                }
                                
                                return !shouldRemove; // Keep items that DON'T match
                            });
                            
                            const deletedCount = originalCount - group.items.length;
                            if (deletedCount > 0) {
                                console.log(`Removed ${deletedCount} items from group ${group.name}`);
                            }
                        }
                    });
                    
                    // Remove empty groups
                    const originalGroupCount = scopeNode.groups.length;
                    scopeNode.groups = scopeNode.groups.filter(g => 
                        g.items && g.items.length > 0
                    );
                    
                    if (scopeNode.groups.length < originalGroupCount) {
                        console.log(`Removed ${originalGroupCount - scopeNode.groups.length} empty groups`);
                    }
                };
                
                // Apply to base scope
                if (newScopes.base) {
                    removeFromScope(newScopes.base);
                }
                
                // Apply to change orders
                if (newScopes.changeOrders && Array.isArray(newScopes.changeOrders)) {
                    newScopes.changeOrders.forEach(co => removeFromScope(co));
                }
                
                return newScopes;
            });
            
            changesApplied = true;
        }
        
        // ───────────────────────────────────────────────────────────────────
        // 3B. HANDLE RATE CHANGES (rate_changes) - WITH COST TYPE FILTERING
        // ───────────────────────────────────────────────────────────────────
        if (data.rate_changes && Object.keys(data.rate_changes).length > 0) {
            console.log("💰 Processing rate changes:", data.rate_changes);
            
            setScopes(prevScopes => {
                const newScopes = JSON.parse(JSON.stringify(prevScopes));
                
                const applyRateChanges = (scopeNode) => {
                    if (!scopeNode || !scopeNode.groups) return;
                    
                    let updateCount = 0;
                    
                    scopeNode.groups.forEach(group => {
                        (group.items || []).forEach(item => {
                            let itemUpdated = false;
                            const itemCostType = (item.costType || "").toLowerCase();
                            
                            // Apply labor markup change (ONLY to Labor cost type)
                            if (data.rate_changes.labor !== undefined && itemCostType === "labor") {
                                const rawMarkup = parseFloat(data.rate_changes.labor);
                                // Handle both decimal (0.7) and percentage (70) formats
                                const newMarkup = rawMarkup < 5 ? rawMarkup * 100 : rawMarkup;
                                item.markupPercent = newMarkup;
                                console.log(`✅ ${item.itemName} (Labor): ${newMarkup}% markup`);
                                itemUpdated = true;
                            }
                            
                            // Apply material markup change (ONLY to Material cost type)
                            if (data.rate_changes.material !== undefined && itemCostType === "material") {
                                const rawMarkup = parseFloat(data.rate_changes.material);
                                const newMarkup = rawMarkup < 5 ? rawMarkup * 100 : rawMarkup;
                                item.markupPercent = newMarkup;
                                console.log(`✅ ${item.itemName} (Material): ${newMarkup}% markup`);
                                itemUpdated = true;
                            }
                            
                            // Apply "all" markup change (to ALL items regardless of cost type)
                            if (data.rate_changes.all !== undefined) {
                                const allChanges = data.rate_changes.all;
                                if (typeof allChanges === 'object' && allChanges.markup !== undefined) {
                                    item.markupPercent = parseFloat(allChanges.markup);
                                } else {
                                    const rawMarkup = parseFloat(allChanges);
                                    item.markupPercent = rawMarkup < 5 ? rawMarkup * 100 : rawMarkup;
                                }
                                console.log(`✅ ${item.itemName} (All): ${item.markupPercent}% markup`);
                                itemUpdated = true;
                            }
                            
                            // Apply specific item changes by ID or name
                            const itemId = item.id || item.itemId;
                            const itemKey1 = `${group.id}_${itemId}`;
                            const itemKey2 = itemId;
                            const itemName = (item.itemName || "").toLowerCase();
                            
                            // Check all possible key formats
                            const itemChanges = 
                                data.rate_changes[itemKey1] || 
                                data.rate_changes[itemKey2] ||
                                data.rate_changes[itemName];
                            
                            if (itemChanges && typeof itemChanges === 'object') {
                                if (itemChanges.markup !== undefined) {
                                    const rawMarkup = parseFloat(itemChanges.markup);
                                    item.markupPercent = rawMarkup < 5 ? rawMarkup * 100 : rawMarkup;
                                    console.log(`✅ ${item.itemName}: ${item.markupPercent}% markup`);
                                    itemUpdated = true;
                                }
                                if (itemChanges.unit_cost !== undefined) {
                                    const newCost = parseFloat(itemChanges.unit_cost);
                                    item.unitCost = newCost;
                                    console.log(`✅ ${item.itemName}: $${newCost} unit cost`);
                                    itemUpdated = true;
                                }
                                if (itemChanges.quantity !== undefined) {
                                    const newQty = parseFloat(itemChanges.quantity);
                                    item.quantity = newQty;
                                    console.log(`✅ ${item.itemName}: ${newQty} ${item.unit}`);
                                    itemUpdated = true;
                                }
                            }
                            
                            if (itemUpdated) {
                                updateCount++;
                            }
                        });
                    });
                    
                    console.log(`Updated ${updateCount} items in scope`);
                };
                
                // Apply to base scope
                if (newScopes.base) {
                    applyRateChanges(newScopes.base);
                }
                
                // Apply to change orders
                if (newScopes.changeOrders && Array.isArray(newScopes.changeOrders)) {
                    newScopes.changeOrders.forEach(co => applyRateChanges(co));
                }
                
                return newScopes;
            });
            
            changesApplied = true;
        }
        
        // ───────────────────────────────────────────────────────────────────
        // 3C. HANDLE ADDITIONS (items_to_add)
        // ───────────────────────────────────────────────────────────────────
        if (data.items_to_add && data.items_to_add.length > 0) {
            console.log("➕ Processing additions:", data.items_to_add.length, "items for scope:", activeScope);
            
            setScopes(prevScopes => {
                const newScopes = JSON.parse(JSON.stringify(prevScopes));
                
                const addToScope = (scopeNode) => {
                    if (!scopeNode) return;
                    if (!scopeNode.groups) scopeNode.groups = [];
                    
                    data.items_to_add.forEach(newItem => {
                        // Find or create target group (handle both old and new API formats)
                        const targetSectionName = newItem.category || newItem.section || newItem.group || "Miscellaneous";
                        
                        let targetGroup = scopeNode.groups.find(g => 
                            g.name === targetSectionName || 
                            g.id === targetSectionName ||
                            (g.name || "").toLowerCase() === targetSectionName.toLowerCase()
                        );
                        
                        if (!targetGroup) {
                            // Create new group
                            const newGroupId = targetSectionName.toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
                            targetGroup = {
                                id: newGroupId,
                                name: targetSectionName,
                                items: []
                            };
                            scopeNode.groups.push(targetGroup);
                            console.log(`📁 Created new group: ${targetSectionName}`);
                        }
                        
                        // Add item to group (handle both old snake_case and new camelCase API formats)
                        const newItemObj = {
                            id: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                            itemId: `item_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
                            itemName: newItem.itemName || newItem.item_name || newItem.name || "New Item",
                            description: newItem.description || "",
                            quantity: parseFloat(newItem.quantity) || 1,
                            unit: newItem.unit || "EA",
                            unitCost: parseFloat(newItem.unitCost || newItem.unit_cost || newItem.cost || 0),
                            costType: newItem.costType || newItem.cost_type || newItem.type || "Material",
                            markupPercent: parseFloat(newItem.markupPercent || newItem.markup || 0),
                            pricingSource: newItem.pricingSource || newItem.pricing_source || ""
                        };
                        
                        targetGroup.items.push(newItemObj);
                        console.log(`✅ Added: ${newItemObj.itemName} to ${targetGroup.name} ($${newItemObj.unitCost})`);
                    });
                };
                
                // Apply to the ACTIVE scope (base or change order)
                if (activeScope === "base") {
                    if (newScopes.base) {
                        addToScope(newScopes.base);
                    }
                } else {
                    // Adding to a Change Order
                    const coIndex = newScopes.changeOrders.findIndex(co => co.id === activeScope);
                    if (coIndex >= 0) {
                        addToScope(newScopes.changeOrders[coIndex]);
                    } else {
                        console.warn(`⚠️ Change Order ${activeScope} not found for additions`);
                    }
                }
                
                return newScopes;
            });
            
            changesApplied = true;
        }
        
        // ───────────────────────────────────────────────────────────────────
        // 3D. HANDLE MODIFICATIONS (items_to_modify)
        // ───────────────────────────────────────────────────────────────────
        if (data.items_to_modify && data.items_to_modify.length > 0) {
            console.log("✏️ Processing modifications:", data.items_to_modify);
            
            setScopes(prevScopes => {
                const newScopes = JSON.parse(JSON.stringify(prevScopes));
                
                const modifyInScope = (scopeNode) => {
                    if (!scopeNode || !scopeNode.groups) return;
                    
                    let modifyCount = 0;
                    
                    scopeNode.groups.forEach(group => {
                        (group.items || []).forEach(item => {
                            data.items_to_modify.forEach(mod => {
                                const itemId = item.id || item.itemId;
                                const itemName = (item.itemName || "").toLowerCase();
                                const modId = (mod.id || "").toLowerCase();
                                const modName = (mod.item_name || "").toLowerCase();
                                
                                const matches = 
                                    mod.id === itemId ||
                                    mod.id === `${group.id}_${itemId}` ||
                                    modId === itemName ||
                                    modName === itemName ||
                                    (modName && itemName.includes(modName)) ||
                                    (modName && modName.includes(itemName));
                                
                                if (matches) {
                                    if (mod.quantity !== undefined) {
                                        item.quantity = parseFloat(mod.quantity);
                                        console.log(`✅ ${item.itemName}: quantity = ${item.quantity}`);
                                    }
                                    if (mod.unit_cost !== undefined) {
                                        item.unitCost = parseFloat(mod.unit_cost);
                                        console.log(`✅ ${item.itemName}: unit cost = $${item.unitCost}`);
                                    }
                                    if (mod.markup !== undefined) {
                                        const rawMarkup = parseFloat(mod.markup);
                                        item.markupPercent = rawMarkup < 5 ? rawMarkup * 100 : rawMarkup;
                                        console.log(`✅ ${item.itemName}: markup = ${item.markupPercent}%`);
                                    }
                                    if (mod.item_name !== undefined) {
                                        item.itemName = mod.item_name;
                                        console.log(`✅ Renamed to: ${item.itemName}`);
                                    }
                                    if (mod.description !== undefined) {
                                        item.description = mod.description;
                                    }
                                    
                                    modifyCount++;
                                }
                            });
                        });
                    });
                    
                    console.log(`Modified ${modifyCount} items`);
                };
                
                // Apply to base scope
                if (newScopes.base) {
                    modifyInScope(newScopes.base);
                }
                
                // Apply to change orders
                if (newScopes.changeOrders && Array.isArray(newScopes.changeOrders)) {
                    newScopes.changeOrders.forEach(co => modifyInScope(co));
                }
                
                return newScopes;
            });
            
            changesApplied = true;
        }
        
        // ═══════════════════════════════════════════════════════════════════
        // 4. SAVE CHANGES TO BACKEND
        // ═══════════════════════════════════════════════════════════════════
        if (changesApplied) {
            console.log("💾 Saving changes to backend...");
            try {
                await persistScopes();
                console.log("✅ Changes saved successfully");
            } catch (saveErr) {
                console.error("Error saving changes:", saveErr);
                // Don't throw - changes are applied in UI even if save fails
            }
        }
        
        // ═══════════════════════════════════════════════════════════════════
        // 5. UPDATE CHAT UI
        // ═══════════════════════════════════════════════════════════════════
        setSuperintendentChat(prev => [...prev, { 
            role: "assistant", 
            content: data.message || "Changes applied successfully."
        }]);
        
        if (!changesApplied && !data.message) {
            console.warn("No changes applied and no message from AI");
        }
        
    } catch (err) {
        console.error("❌ Superintendent Error:", err);
        console.error("Error stack:", err.stack);
        
        setSuperintendentChat(prev => [...prev, { 
            role: "assistant", 
            content: `Sorry, I encountered an error: ${err.message || 'Unknown error'}. Please try again.`
        }]);
    } finally {
        setSuperintendentLoading(false);
    }
  };

  
  // Auto-Build with scope from chat (for Command Center mode)
  const handleAutoBuildWithScope = async (scopeDescription, answeredParams = null) => {
    if (COPILOT_TRACE) {
      console.info("[COPILOT_TRACE] handleAutoBuildWithScope invoked", {
        scopeDescription,
        answeredParams,
      });
    }
    console.log("[Auto-Build] Starting with scope:", scopeDescription);

    // Merge structured Q&A answers into deterministic tokens for scope_notes
    let mergedScope = scopeDescription || "";
    if (answeredParams && typeof answeredParams === "object") {
      try {
        const tokens = [];
        if (answeredParams.material_type) tokens.push(`material_type=${answeredParams.material_type}`);
        if (answeredParams.height_off_ground_ft != null) tokens.push(`height_off_ground_ft=${answeredParams.height_off_ground_ft}`);
        if (answeredParams.ledger_or_freestanding) tokens.push(`ledger_or_freestanding=${answeredParams.ledger_or_freestanding}`);
        if (answeredParams.railing_sides != null) tokens.push(`railing_sides=${answeredParams.railing_sides}`);
        if (answeredParams.stairs_risers != null) tokens.push(`stairs_risers=${answeredParams.stairs_risers}`);
        if (answeredParams.demo_existing != null) tokens.push(`demo_existing=${answeredParams.demo_existing}`);
        if (answeredParams.demo_disposal != null) tokens.push(`demo_disposal=${answeredParams.demo_disposal}`);
        if (answeredParams.demo_scope) tokens.push(`demo_scope=${answeredParams.demo_scope}`);

        if (tokens.length > 0) {
          const tokenString = tokens.join("; ");
          mergedScope = `${scopeDescription}\n\n[DECK_PARAMS] ${tokenString}`;
        }

        console.log("[Auto-Build] answeredParams=", answeredParams);
        console.log("[Auto-Build] merged scope_notes=", mergedScope);
        if (COPILOT_TRACE) {
          console.info("[COPILOT_TRACE] Merged scope with DECK_PARAMS", {
            mergedScope,
          });
        }
      } catch (e) {
        console.warn("[Auto-Build] Failed to merge answered params into scope:", e);
      }
    }

    let estimateToUse = selectedEstimate;
    
    // If no estimate selected, auto-create one
    if (!estimateToUse) {
      if (COPILOT_TRACE) {
        console.info("[COPILOT_TRACE] No estimate selected, creating new one (auto-build)", {
          scopeDescription,
        });
      }
      console.log("[Auto-Build] No estimate selected, creating new one...");
      setInitializing(true);
      try {
        const dateStr = new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
        const clientName = selectedLead?.contact_name || selectedLead?.name || "New Client";
        const newEstimateData = {
          project_description: scopeDescription,
          job_name: `${clientName} - Scope ${dateStr}`,
          lead_id: selectedLead?.lead_id || null,
          client_id: selectedLead?.client_id || null,
          job_address: selectedLead?.address || selectedLead?.project_address || null,
        };
        
        console.log("[Auto-Build] Creating estimate with data:", newEstimateData);
        const createRes = await API.post("/estimates", newEstimateData);
        estimateToUse = createRes.data;
        console.log("[Auto-Build] Estimate created:", estimateToUse.estimate_id);
        if (COPILOT_TRACE) {
          console.info("[COPILOT_TRACE] Estimate created in handleAutoBuildWithScope", {
            estimate_id: estimateToUse.estimate_id,
          });
        }
        setSelectedEstimate(estimateToUse);
        setEstimates(prev => [...prev, estimateToUse]);
      } catch (err) {
        console.error("[Auto-Build] Failed to create estimate:", err);
        throw err;
      } finally {
        setInitializing(false);
      }
    }
    
    // Now trigger the actual Auto-Build
    if (COPILOT_TRACE) {
      console.info("[COPILOT_TRACE] Calling backend auto-build endpoint", {
        estimate_id: estimateToUse.estimate_id,
      });
    }
    console.log("[Auto-Build] Calling auto-build endpoint for estimate:", estimateToUse.estimate_id);
    setGenerating(true);
    try {
      const buildRes = await API.post(`/estimates/${estimateToUse.estimate_id}/auto-build`, {
        scope_notes: mergedScope,
        use_material_db: true,
      });

      if (COPILOT_TRACE) {
        console.info("[COPILOT_TRACE] Auto-build response received", {
          estimate_id: estimateToUse.estimate_id,
          success: buildRes.data?.success,
          materials_count: buildRes.data?.materials_count,
          hasGroupedItems: !!buildRes.data?.grouped_items,
        });
      }
      
      const data = buildRes.data;
      console.log("[Auto-Build] Response received:", {
        success: data.success,
        materials_count: data.materials_count,
        grouped_items_keys: Object.keys(data.grouped_items || {}),
      });
      
      if (data.grouped_items && Object.keys(data.grouped_items).length > 0) {
        const newGroups = Object.entries(data.grouped_items).map(([category, items], index) => ({
          id: String(index + 1).padStart(2, "0"),
          code: String(index + 1).padStart(2, "0"),
          name: formatSectionName(index, category), // FIX: Use cleaner to prevent recursive numbering
          items: items,
        }));
        
        console.log("[Auto-Build] Setting groups:", newGroups.length, "sections");
        
        setScopes((prev) => ({
          ...prev,
          base: { ...prev.base, groups: newGroups },
        }));
        setActiveScope("base");
        setCollapsedGroups({});
        
        const totalItems = newGroups.reduce((sum, g) => sum + (g.items?.length || 0), 0);
        
        // Trigger the analysis for missing items
        const allLineItems = newGroups.flatMap(g => g.items || []);
        await triggerSuperintendentAnalysisFromBuild(allLineItems, scopeDescription);
        
        toast.success("🎯 Estimate Built", {
          description: `${newGroups.length} sections with ${totalItems} items generated`,
        });
      } else if (buildRes.data && buildRes.data.needs_clarification) {
        console.warn("[Auto-Build] Scope gate needs clarification", buildRes.data.clarification_questions);
        setSuperintendentChat(prev => [...prev, {
          role: "assistant",
          content: buildRes.data.message || "I need a few more details before I can build a solid estimate.",
          clarification_questions: buildRes.data.clarification_questions || [],
          timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        }]);
      } else {
        console.warn("[Auto-Build] No grouped items in response");
        setSuperintendentChat(prev => [...prev, {
          role: "assistant",
          content: "I couldn't generate items from that description. Could you give me more specific details? For example: 'Master bathroom with new toilet, 48-inch vanity, tile floor, and shower glass'",
          timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        }]);
      }
    } catch (err) {
      if (COPILOT_TRACE) {
        console.warn("[COPILOT_TRACE] Auto-build request failed", {
          error: err?.message || String(err),
        });
      }
      console.error("[Auto-Build] Request failed:", err);
      throw err;
    } finally {
      if (COPILOT_TRACE) {
        console.info("[COPILOT_TRACE] Auto-build flow complete", {
          estimate_id: selectedEstimate?.estimate_id || null,
        });
      }
      setGenerating(false);
      setSuperintendentLoading(false);
    }
  };
  
  // ═══════════════════════════════════════════════════════════════════════════
  // CONVERSATIONAL ESTIMATOR - Uses /api/estimates/conversational endpoint
  // Handles multi-turn conversations with clarification questions
  // ═══════════════════════════════════════════════════════════════════════════
  const handleConversationalEstimate = async (message) => {
    const timestamp = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    
    // Build chat history for multi-turn conversation replay
    const chatHistoryForAPI = conversationalChatHistory.map(msg => ({
      role: msg.role,
      content: msg.content,
    }));
    
    setSuperintendentLoading(true);
    
    try {
      // Call the conversational endpoint
      console.log('[Conversational] Sending request:', {
        message,
        estimate_id: conversationalEstimateId || selectedEstimate?.estimate_id || null,
        lead_id: selectedLead?.lead_id || "default_lead",
      });
      
      const response = await API.post("/estimates/conversational", {
        message: message,
        lead_id: selectedLead?.lead_id || "default_lead",
        estimate_id: conversationalEstimateId || selectedEstimate?.estimate_id || null,
        chat_history: chatHistoryForAPI,
      });
      
      console.log('[Conversational] Response:', {
        status: response.data?.status,
        response_type: response.data?.response_type,
        changes: response.data?.changes,
      });
      
      const data = response.data;
      const responseType = data.response_type || "estimate";
      
      // ═══════════════════════════════════════════════════════════════════════
      // HANDLE CLARIFICATION RESPONSES
      // ═══════════════════════════════════════════════════════════════════════
      if (data.status === "needs_clarification") {
        const questions = data.questions || [];
        const questionsText = questions.length > 0
          ? `I need a few more details before I can build an accurate estimate:\n\n${questions.map((q, i) => `${i + 1}. ${q}`).join('\n')}`
          : "I need more details to build this estimate. Could you provide more specifics?";
        
        setSuperintendentChat(prev => [...prev, {
          role: "assistant",
          content: questionsText,
          timestamp,
          clarification_questions: questions,
          gate_result: data.gate_result,
        }]);
        
        setConversationalChatHistory(prev => [
          ...prev,
          { role: "user", content: message },
          { role: "assistant", content: questionsText },
        ]);
        
        return;
      }
      
      // ═══════════════════════════════════════════════════════════════════════
      // HANDLE TEXT RESPONSES (Guidance, Questions, Explanations)
      // ═══════════════════════════════════════════════════════════════════════
      if (responseType === "text" || responseType === "guidance") {
        // Plain text response - display directly in chat without loading estimate
        const textResponse = data.response || "I'm here to help!";
        
        // Clean up response - remove any markdown json blocks if present
        const cleanedResponse = textResponse
          .replace(/```json[\s\S]*?```/g, '')
          .replace(/```[\s\S]*?```/g, '')
          .trim();
        
        // Add special formatting header for guidance responses
        const formattedResponse = responseType === "guidance" 
          ? `📋 **Construction Guidance**\n\n${cleanedResponse}`
          : cleanedResponse;
        
        setSuperintendentChat(prev => [...prev, {
          role: "assistant",
          content: formattedResponse,
          timestamp,
          isGuidance: true, // Mark as guidance/explanation type
          responseType: responseType,
        }]);
        
        setConversationalChatHistory(prev => [
          ...prev,
          { role: "user", content: message },
          { role: "assistant", content: cleanedResponse },
        ]);
        
        return;
      }
      
      // ═══════════════════════════════════════════════════════════════════════
      // HANDLE MODIFICATION RESPONSES
      // ═══════════════════════════════════════════════════════════════════════
      if (responseType === "modification") {
        const changes = data.changes || [];
        const explanation = data.explanation || "Estimate updated.";
        const estimate = data.estimate;
        
        // Check if this is a "remove all items" action
        const isRemoveAll = changes.some(c => c.type === "remove_all_items");
        
        if (isRemoveAll) {
          // Clear the frontend scopes completely
          setScopes(prev => ({
            ...prev,
            base: { ...prev.base, groups: [] },
          }));
          
          // Update the selected estimate
          if (estimate) {
            setConversationalEstimateId(estimate.estimate_id);
            setSelectedEstimate(estimate);
          }
          
          // Show clear action notification
          setSuperintendentChat(prev => [...prev, {
            type: "action",
            content: "All items deleted. Estimate is now empty.",
          }]);
          
          setSuperintendentChat(prev => [...prev, {
            role: "assistant",
            content: explanation || "I've cleared all items from the estimate. What would you like to build?",
            timestamp,
          }]);
          
          setConversationalChatHistory(prev => [
            ...prev,
            { role: "user", content: message },
            { role: "assistant", content: explanation },
          ]);
          
          toast.success("🗑️ Estimate Cleared", {
            description: "All items deleted. Ready for new items.",
          });
          
          return;
        }
        
        if (estimate) {
          // Store the estimate ID
          setConversationalEstimateId(estimate.estimate_id);
          
          // Refresh frontend scopes
          try {
            const scopesResponse = await API.get(`/estimates/${estimate.estimate_id}/frontend-scopes`);
            const frontendScopes = scopesResponse.data.scopes;
            
            if (frontendScopes?.base?.groups) {
              setScopes(frontendScopes);
              setActiveScope("base");
              setSelectedEstimate(estimate);
              
              if (!estimates.find(e => e.estimate_id === estimate.estimate_id)) {
                setEstimates(prev => [...prev, estimate]);
              }
            }
          } catch (err) {
            console.warn("[Conversational] Could not refresh scopes after modification:", err);
          }
        }
        
        // Show action notification
        setSuperintendentChat(prev => [...prev, {
          type: "action",
          content: `Estimate modified: ${changes.length} change(s) applied`,
        }]);
        
        // Show explanation
        setSuperintendentChat(prev => [...prev, {
          role: "assistant",
          content: explanation,
          timestamp,
        }]);
        
        setConversationalChatHistory(prev => [
          ...prev,
          { role: "user", content: message },
          { role: "assistant", content: explanation },
        ]);
        
        toast.success("✏️ Estimate Modified", {
          description: explanation,
        });
        
        return;
      }
      
      // ═══════════════════════════════════════════════════════════════════════
      // HANDLE ESTIMATE GENERATION RESPONSES
      // ═══════════════════════════════════════════════════════════════════════
      if (data.status === "success" && (responseType === "estimate" || data.estimate)) {
        const estimate = data.estimate;
        
        if (!estimate) {
          setSuperintendentChat(prev => [...prev, {
            role: "assistant",
            content: "I processed your request but didn't generate an estimate. Could you provide more details about what you'd like priced?",
            timestamp,
          }]);
          return;
        }
        
        // Store the estimate ID for future modifications
        setConversationalEstimateId(estimate.estimate_id);
        
        // Fetch the frontend-compatible scopes
        try {
          const scopesResponse = await API.get(`/estimates/${estimate.estimate_id}/frontend-scopes`);
          const frontendScopes = scopesResponse.data.scopes;
          
          if (frontendScopes?.base?.groups && frontendScopes.base.groups.length > 0) {
            // Load scopes into UI
            setScopes(frontendScopes);
            setActiveScope("base");
            setCollapsedGroups({});
            
            // Update selected estimate
            setSelectedEstimate(estimate);
            if (!estimates.find(e => e.estimate_id === estimate.estimate_id)) {
              setEstimates(prev => [...prev, estimate]);
            }
            
            // CRITICAL: Trigger immediate save to ensure data persistence
            // This bypasses the debounced auto-save for newly created estimates
            try {
              await API.post(`/estimates/${estimate.estimate_id}/save-scopes`, { scopes: frontendScopes });
              console.log("[Conversational] Immediate save after creation successful");
            } catch (saveErr) {
              console.warn("[Conversational] Immediate save failed:", saveErr);
            }
            
            const totalItems = frontendScopes.base.groups.reduce(
              (sum, g) => sum + (g.items?.length || 0), 0
            );
            
            // Add success message
            setSuperintendentChat(prev => [...prev, {
              type: "action",
              content: `Estimate built with ${frontendScopes.base.groups.length} sections and ${totalItems} items`,
            }]);
            
            // Add assistant response with review flags
            const reviewFlags = data.review_flags || [];
            let responseContent = "I've built your estimate. Review the line items above and let me know if you'd like to make any changes.";
            if (reviewFlags.length > 0) {
              const formatFlag = (flag) => {
                if (typeof flag === 'string') return flag;
                if (flag && typeof flag === 'object') {
                  return flag.message || flag.text || flag.description || flag.item || 
                         `${flag.type || 'Note'}: ${flag.action_needed || flag.action || 'Review needed'}`;
                }
                return String(flag);
              };
              responseContent += `\n\n⚠️ Items to review:\n${reviewFlags.map(f => `• ${formatFlag(f)}`).join('\n')}`;
            }
            
            setSuperintendentChat(prev => [...prev, {
              role: "assistant",
              content: responseContent,
              timestamp,
            }]);
            
            toast.success("🎯 Estimate Built", {
              description: `${frontendScopes.base.groups.length} sections with ${totalItems} items generated`,
            });
          } else {
            // Scopes conversion returned empty - fallback to raw sections
            console.warn("[Conversational] frontend-scopes returned empty, using raw sections");
            if (estimate.sections && estimate.sections.length > 0) {
              const newGroups = estimate.sections.map((sec, idx) => ({
                id: sec.section_id || String(idx + 1).padStart(2, "0"),
                code: String(idx + 1).padStart(2, "0"),
                name: formatSectionName(idx, sec.name),
                items: (sec.line_items || []).map(item => ({
                  itemName: item.item_name || item.description || "Item",
                  quantity: item.quantity || 1,
                  unit: item.unit || "EA",
                  unitCost: (item.material_unit_cost || 0) + (item.labor_unit_cost || 0),
                  costType: item.labor_unit_cost > item.material_unit_cost ? "Labor" : "Material",
                  markupPercent: 0,
                })),
              }));
              
              const newScopes = {
                base: { groups: newGroups },
                changeOrders: [],
              };
              
              setScopes(prev => ({
                ...prev,
                base: { ...prev.base, groups: newGroups },
              }));
              setActiveScope("base");
              setSelectedEstimate(estimate);
              
              // CRITICAL: Trigger immediate save for fallback path
              try {
                await API.post(`/estimates/${estimate.estimate_id}/save-scopes`, { scopes: newScopes });
                console.log("[Conversational] Fallback immediate save successful");
              } catch (saveErr) {
                console.warn("[Conversational] Fallback immediate save failed:", saveErr);
              }
              
              const totalItems = newGroups.reduce((sum, g) => sum + (g.items?.length || 0), 0);
              
              setSuperintendentChat(prev => [...prev, {
                type: "action",
                content: `Built ${totalItems} items from AI response`,
              }]);
              
              setSuperintendentChat(prev => [...prev, {
                role: "assistant",
                content: "Estimate built. Review the line items and let me know if you'd like any changes.",
                timestamp,
              }]);
            }
          }
        } catch (scopesErr) {
          console.error("[Conversational] Failed to fetch frontend-scopes:", scopesErr);
          setSuperintendentChat(prev => [...prev, {
            role: "assistant",
            content: "Estimate created. Let me know if you'd like to make any modifications.",
            timestamp,
          }]);
        }
        
        // Update chat history
        setConversationalChatHistory(prev => [
          ...prev,
          { role: "user", content: message },
          { role: "assistant", content: data.response || "Estimate generated successfully." },
        ]);
        
      } else if (data.status === "error") {
        // Error from AI
        setSuperintendentChat(prev => [...prev, {
          role: "assistant",
          content: `I had trouble processing that request: ${data.message || 'Unknown error'}. Could you try rephrasing?`,
          timestamp,
        }]);
      }
      
    } catch (err) {
      console.error("[Conversational] Request failed:", err);
      setSuperintendentChat(prev => [...prev, {
        role: "assistant",
        content: "I had trouble connecting to the estimating engine. Please try again.",
        timestamp,
      }]);
    } finally {
      setSuperintendentLoading(false);
    }
  };
  
  // Trigger Superintendent analysis after build (internal use)
  const triggerSuperintendentAnalysisFromBuild = async (lineItems, projectDescription) => {
    const newSessionId = superintendentSessionId || `super_${Date.now()}`;
    if (!superintendentSessionId) {
      setSuperintendentSessionId(newSessionId);
    }
    
    try {
      assertLLMAllowed(lastSuperintendentIntentRef.current, "triggerSuperintendentAnalysisFromBuild");
      const res = await API.post("/ai/superintendent/analyze", {
        session_id: newSessionId,
        project_description: projectDescription,
        scope_notes: projectDescription,
        line_items: lineItems,
        room_type: "",
      });
      
      if (res.data.success) {
        const timestamp = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
        
        // Add action message for built items
        setSuperintendentChat(prev => [...prev, {
          type: "action",
          content: `Built ${lineItems.length} item(s) from scope`,
        }]);
        
        // Add the analysis message
        setSuperintendentChat(prev => [...prev, {
          role: "assistant",
          content: res.data.message,
          suggestions: res.data.suggestions,
          timestamp,
        }]);
      }
    } catch (err) {
      if (err?.message?.includes && err.message.includes("[LLM_FORBIDDEN]")) {
        setSuperintendentChat(prev => [
          ...prev,
          {
            role: "assistant",
            content:
              "That request is handled deterministically (no AI). I rebuilt using the deterministic pipeline. If you still need help, rephrase as a scope change.",
            timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
          },
        ]);
        setSuperintendentLoading(false);
        return;
      }
      console.error("Superintendent analysis failed:", err);
      setSuperintendentChat(prev => [...prev, {
        role: "assistant",
        content: "Estimate built! Let me know if you'd like to add or change anything.",
        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      }]);
    }
  };
  
  // Add items suggested by Superintendent to the estimate
  const addSuperintendentItems = (items) => {
    const currentGroups = getActiveGroups() || [];
    
    // Apply smart cost type detection to each item
    const processedItems = items.map(item => {
      // Force detection based on item name
      const detectedType = detectCostType(item.itemName);
      return {
        ...item,
        // Use detected type if found, otherwise use provided type or default to Material
        costType: detectedType || item.costType || "Material",
      };
    });
    
    // Find or create "ADDITIONAL ITEMS" section
    let additionalGroupIndex = currentGroups.findIndex(g => 
      (g.name || '').toLowerCase().includes("additional") || (g.name || '').toLowerCase().includes("superintendent")
    );
    
    const newGroups = [...currentGroups];
    
    if (additionalGroupIndex === -1) {
      // Create new section for superintendent items
      const newCode = String(currentGroups.length + 1).padStart(2, "0");
      newGroups.push({
        id: newCode,
        code: newCode,
        name: `${newCode}. SUPERINTENDENT ADDITIONS`,
        items: processedItems.map((item, idx) => ({
          ...item,
          id: `${newCode}-${String(idx + 1).padStart(3, "0")}`,
        })),
      });
    } else {
      // Add to existing section
      const existingItems = newGroups[additionalGroupIndex].items || [];
      newGroups[additionalGroupIndex] = {
        ...newGroups[additionalGroupIndex],
        items: [
          ...existingItems,
          ...processedItems.map((item, idx) => ({
            ...item,
            id: `${newGroups[additionalGroupIndex].code}-${String(existingItems.length + idx + 1).padStart(3, "0")}`,
          })),
        ],
      };
    }
    
    // Update the scopes
    setScopes((prev) => ({
      ...prev,
      base: { ...prev.base, groups: newGroups },
    }));
    
    toast.success("Items added by Superintendent", {
      description: `${processedItems.length} item(s) added to estimate`,
    });
  };

  // BATCH RECLASSIFICATION: Apply deterministic rules to ALL items
  const reclassifyAllItems = useCallback(() => {
    let laborCount = 0;
    let materialCount = 0;
    
    setScopes((prev) => {
      const newBase = {
        ...prev.base,
        groups: (prev.base.groups || []).map(group => ({
          ...group,
          items: (group.items || []).map(item => {
            const newType = detectCostType(item.itemName);
            if (newType === "Labor") laborCount++;
            else materialCount++;
            
            if (item.costType !== newType) {
              console.log(`[Reclassify] "${item.itemName}" -> ${newType} (was ${item.costType})`);
            }
            return { ...item, costType: newType };
          }),
        })),
      };
      
      // Also fix change orders
      const newChangeOrders = prev.changeOrders.map(co => ({
        ...co,
        groups: (co.groups || []).map(group => ({
          ...group,
          items: (group.items || []).map(item => {
            const newType = detectCostType(item.itemName);
            return { ...item, costType: newType };
          }),
        })),
      }));
      
      return { ...prev, base: newBase, changeOrders: newChangeOrders };
    });
    
    toast.success("Classification Complete", {
      description: `${laborCount} Labor items, ${materialCount} Material items`,
    });
  }, []);

  // BACKFILL ZERO-COST ITEMS: Apply market averages to items with $0 cost
  const backfillZeroCostItems = useCallback(() => {
    let updatedCount = 0;
    let skippedCount = 0;
    
    setScopes((prev) => {
      const processItems = (items) => items.map(item => {
        const qty = parseFloat(item.quantity) || 0;
        const cost = parseFloat(item.unitCost) || 0;
        
        // Only backfill if Quantity > 0 AND UnitCost == 0
        if (qty > 0 && cost === 0) {
          const estimatedPrice = estimateMarketPrice(item.itemName);
          if (estimatedPrice) {
            updatedCount++;
            console.log(`[Backfill] "${item.itemName}" -> $${estimatedPrice} (was $0)`);
            return { 
              ...item, 
              unitCost: estimatedPrice,
              autoEstimated: true, // Flag for yellow highlighting
            };
          } else {
            skippedCount++;
            console.log(`[Backfill] "${item.itemName}" - No price estimate available`);
          }
        }
        return item;
      });
      
      const newBase = {
        ...prev.base,
        groups: (prev.base.groups || []).map(group => ({
          ...group,
          items: processItems(group.items || []),
        })),
      };
      
      return { ...prev, base: newBase };
    });
    
    if (updatedCount > 0) {
      toast.success("Pricing Backfill Complete", {
        description: `${updatedCount} items updated with market estimates. ${skippedCount} items need manual pricing.`,
      });
    } else {
      toast.info("No Zero-Cost Items", {
        description: "All items with quantities already have pricing.",
      });
    }
  }, []);

  // EXPLODE ITEM: Replace a vague item with detailed breakdown
  const explodeItem = useCallback((itemNameToReplace, newItems, addToGroup = null) => {
    let replaced = false;
    
    setScopes((prev) => {
      const newBase = {
        ...prev.base,
        groups: (prev.base.groups || []).map(group => {
          const itemIndex = (group.items || []).findIndex(item => 
            item.itemName.toLowerCase().includes(itemNameToReplace.toLowerCase())
          );
          
          if (itemIndex !== -1) {
            replaced = true;
            // Create new items array with the replacement
            const updatedItems = [...group.items];
            // Remove the old item
            updatedItems.splice(itemIndex, 1);
            // Insert new detailed items at the same position
            const processedNewItems = newItems.map((newItem, idx) => ({
              id: `${group.code}-${String(itemIndex + idx + 1).padStart(3, "0")}-NEW`,
              itemName: newItem.name,
              quantity: newItem.qty || 1,
              unit: newItem.unit || "LS",
              unitCost: newItem.cost,
              costType: detectCostType(newItem.name),
              markup: 0,
            }));
            updatedItems.splice(itemIndex, 0, ...processedNewItems);
            
            return { ...group, items: updatedItems };
          }
          return group;
        }),
      };
      
      return { ...prev, base: newBase };
    });
    
    if (replaced) {
      toast.success("Item Exploded", {
        description: `Replaced "${itemNameToReplace}" with ${newItems.length} detailed items`,
      });
    }
    return replaced;
  }, []);

  // ADD ITEM TO GROUP: Add a new item to a specific group
  const addItemToGroup = useCallback((groupName, newItem) => {
    setScopes((prev) => {
      const existingGroups = prev.base.groups || [];
      
      // Check if group exists
      const groupExists = existingGroups.some(
        group => (group.name || '').toLowerCase().includes(groupName.toLowerCase())
      );
      
      let newGroups;
      
      if (groupExists) {
        // Add to existing group
        newGroups = existingGroups.map(group => {
          if ((group.name || '').toLowerCase().includes(groupName.toLowerCase())) {
            const newId = `${group.code}-${String((group.items?.length || 0) + 1).padStart(3, "0")}-ADD`;
            return {
              ...group,
              items: [...(group.items || []), {
                id: newId,
                itemName: newItem.name,
                quantity: newItem.qty || 1,
                unit: newItem.unit || "EA",
                unitCost: newItem.cost,
                costType: newItem.type || detectCostType(newItem.name),
                markupPercent: 0,
              }],
            };
          }
          return group;
        });
      } else {
        // CREATE NEW GROUP
        const newGroupCode = `${String(existingGroups.length + 1).padStart(2, "0")}`;
        const newGroup = {
          id: `group-${Date.now()}`,
          code: newGroupCode,
          name: groupName,
          items: [{
            id: `${newGroupCode}-001-ADD`,
            itemName: newItem.name,
            quantity: newItem.qty || 1,
            unit: newItem.unit || "EA",
            unitCost: newItem.cost,
            costType: newItem.type || detectCostType(newItem.name),
            markupPercent: 0,
          }],
        };
        newGroups = [...existingGroups, newGroup];
        console.log(`[addItemToGroup] Created new group: ${groupName}`);
      }
      
      const newBase = {
        ...prev.base,
        groups: newGroups,
      };
      return { ...prev, base: newBase };
    });
    
    toast.success("Item Added", {
      description: `Added "${newItem.name}" to ${groupName}`,
    });
  }, []);

  // ADD ITEM AT TOP: Add a new item at the beginning of a group
  const addItemAtTop = useCallback((groupName, newItem) => {
    setScopes((prev) => {
      const newBase = {
        ...prev.base,
        groups: (prev.base.groups || []).map(group => {
          if ((group.name || '').toLowerCase().includes(groupName.toLowerCase())) {
            const newId = `${group.code}-000-TOP`;
            const newItemObj = {
              id: newId,
              itemName: newItem.name,
              quantity: newItem.qty || 1,
              unit: newItem.unit || "LS",
              unitCost: newItem.cost,
              costType: newItem.type || detectCostType(newItem.name),
              markup: 25,
            };
            return {
              ...group,
              items: [newItemObj, ...(group.items || [])],
            };
          }
          return group;
        }),
      };
      return { ...prev, base: newBase };
    });
    
    toast.success("Item Added at Top", {
      description: `Added "${newItem.name}" as first item`,
    });
  }, []);

  // STANDARDIZE DEMO RATES: Set all demolition items to a standard hourly rate
  const standardizeDemoRates = useCallback((hourlyRate) => {
    let updatedCount = 0;
    
    setScopes((prev) => {
      const newBase = {
        ...prev.base,
        groups: (prev.base.groups || []).map(group => ({
          ...group,
          items: (group.items || []).map(item => {
            const lowerName = (item.itemName || '').toLowerCase();
            // Target demolition-related items
            if (lowerName.includes('demo') || lowerName.includes('demolition') || 
                lowerName.includes('tear out') || lowerName.includes('removal')) {
              updatedCount++;
              return { ...item, unitCost: hourlyRate };
            }
            return item;
          }),
        })),
      };
      return { ...prev, base: newBase };
    });
    
    toast.success("Demo Rates Standardized", {
      description: `${updatedCount} demolition items set to $${hourlyRate}/hr`,
    });
  }, []);

  // RENAME ITEM: Rename an item by partial name match
  const renameItem = useCallback((oldName, newName) => {
    let renamed = false;
    
    setScopes((prev) => {
      const newBase = {
        ...prev.base,
        groups: (prev.base.groups || []).map(group => ({
          ...group,
          items: (group.items || []).map(item => {
            if (item.itemName.toLowerCase().includes(oldName.toLowerCase())) {
              renamed = true;
              return { ...item, itemName: newName };
            }
            return item;
          }),
        })),
      };
      return { ...prev, base: newBase };
    });
    
    if (renamed) {
      toast.success("Item Renamed", {
        description: `"${oldName}" → "${newName}"`,
      });
    }
  }, []);

  // BATCH LABOR UPDATE: Apply percentage change to ALL labor items
  const batchUpdateLaborCost = useCallback((percentageOrValue, isPercentage = true) => {
    let updatedCount = 0;
    
    setScopes((prev) => {
      const processItems = (items) => items.map(item => {
        // Target EVERY item where Type === 'Labor' or 'LBR' (trust the type column)
        const costType = (item.costType || "").toLowerCase();
        if (costType === "labor" || costType === "lbr") {
          updatedCount++;
          const currentCost = parseFloat(item.unitCost) || 0;
          let newCost;
          
          if (isPercentage) {
            // Apply percentage (e.g., 70% means multiply by 0.70)
            newCost = currentCost * (percentageOrValue / 100);
          } else {
            // Direct value assignment
            newCost = percentageOrValue;
          }
          
          console.log(`[BatchUpdate] "${item.itemName}" $${currentCost} -> $${newCost.toFixed(2)}`);
          return { ...item, unitCost: parseFloat(newCost.toFixed(2)) };
        }
        return item;
      });
      
      const newBase = {
        ...prev.base,
        groups: (prev.base.groups || []).map(group => ({
          ...group,
          items: processItems(group.items || []),
        })),
      };
      
      return { ...prev, base: newBase };
    });
    
    // Note: updatedCount is captured at call time, before setState completes
    // So we show a generic message
    toast.success("Labor Costs Updated", {
      description: `All labor items adjusted to ${isPercentage ? percentageOrValue + '%' : '$' + percentageOrValue}`,
    });
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // LABOR KEYWORD HEURISTIC - For items misclassified as MAT
  // FIX: If costType is MAT but description contains labor-related keywords,
  // treat as Labor for markup updates (e.g., "Toilet Installation" marked as MAT)
  // ═══════════════════════════════════════════════════════════════════════════
  const LABOR_DESCRIPTION_KEYWORDS = [
    'installation', 'install', 'labor', 'demo', 'demolition', 
    'removal', 'haul', 'cleanup', 'rough-in', 'rough in',
    'trim-out', 'finishing', 'hanging', 'mounting', 'setup',
    'set up', 'laying', 'application', 'prep', 'protection'
  ];
  
  const isEffectivelyLabor = useCallback((item) => {
    const costType = (item.costType || "").toLowerCase();
    const itemName = (item.itemName || "").toLowerCase();
    
    // If already classified as Labor, return true
    if (costType === "labor" || costType === "lbr") {
      return true;
    }
    
    // FIX: Fallback heuristic - check if description contains labor keywords
    // This catches items like "Toilet Installation" that might be marked as MAT
    if (costType === "material" || costType === "mat" || costType === "") {
      for (const keyword of LABOR_DESCRIPTION_KEYWORDS) {
        if (itemName.includes(keyword)) {
          console.log(`[LaborHeuristic] "${item.itemName}" classified as MAT but contains "${keyword}" - treating as Labor`);
          return true;
        }
      }
    }
    
    return false;
  }, []);
  
  const isEffectivelyMaterial = useCallback((item) => {
    const costType = (item.costType || "").toLowerCase();
    const itemName = (item.itemName || "").toLowerCase();
    
    // If already classified as Material, check if it should actually be labor
    if (costType === "material" || costType === "mat") {
      // If it contains labor keywords, it's not effectively material
      for (const keyword of LABOR_DESCRIPTION_KEYWORDS) {
        if (itemName.includes(keyword)) {
          return false;
        }
      }
      return true;
    }
    
    return false;
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // BATCH UPDATE MARKUP - "Set markup for LBR to 70%" command support
  // FIX: Uses fallback heuristic to catch misclassified items
  // ═══════════════════════════════════════════════════════════════════════════
  const batchUpdateMarkup = useCallback((newMarkup, targetType = null) => {
    // targetType: "labor", "material", or null (all items)
    let updatedCount = 0;
    let verificationData = [];
    
    // Pause auto-save during batch operation
    batchOperationInProgressRef.current = true;
    
    // FIX: Helper function to check if item matches target type using heuristics
    const itemMatchesTarget = (item, target) => {
      if (target === null) return true; // Update all items
      
      const costType = (item.costType || "").toLowerCase();
      const itemName = (item.itemName || "").toLowerCase();
      
      // Labor keywords for fallback heuristic
      const laborKeywords = [
        'installation', 'install', 'labor', 'demo', 'demolition', 
        'removal', 'haul', 'cleanup', 'rough-in', 'rough in',
        'trim-out', 'finishing', 'hanging', 'mounting', 'setup',
        'set up', 'laying', 'application', 'prep', 'protection'
      ];
      
      if (target === "labor") {
        // Check explicit type first
        if (costType === "labor" || costType === "lbr") return true;
        
        // FIX: Fallback heuristic - check if item name contains labor keywords
        // This catches "Toilet Installation" even if marked as MAT
        for (const keyword of laborKeywords) {
          if (itemName.includes(keyword)) {
            console.log(`[MarkupHeuristic] "${item.itemName}" marked as ${costType} but contains "${keyword}" - treating as LABOR`);
            return true;
          }
        }
        return false;
      }
      
      if (target === "material") {
        // Check explicit type first
        if (costType === "material" || costType === "mat") {
          // FIX: But exclude if it contains labor keywords (misclassified)
          for (const keyword of laborKeywords) {
            if (itemName.includes(keyword)) {
              console.log(`[MarkupHeuristic] "${item.itemName}" marked as MAT but contains "${keyword}" - excluding from material update`);
              return false;
            }
          }
          return true;
        }
        return false;
      }
      
      return false;
    };
    
    setScopes((prev) => {
      const processItems = (items) => items.map(item => {
        // FIX: Use heuristic matching instead of strict type check
        const shouldUpdate = itemMatchesTarget(item, targetType);
        
        if (shouldUpdate) {
          updatedCount++;
          const oldMarkup = parseFloat(item.markupPercent) || 0;
          verificationData.push({
            name: item.itemName,
            oldMarkup,
            newMarkup: parseFloat(newMarkup),
          });
          
          console.log(`[BatchMarkup] "${item.itemName}" Markup: ${oldMarkup}% -> ${newMarkup}%`);
          return { ...item, markupPercent: parseFloat(newMarkup) };
        }
        return item;
      });
      
      const newBase = {
        ...prev.base,
        groups: (prev.base.groups || []).map(group => ({
          ...group,
          items: processItems(group.items || []),
        })),
      };
      
      // Also update change orders if any
      const newChangeOrders = (prev.changeOrders || []).map(co => ({
        ...co,
        groups: (co.groups || []).map(group => ({
          ...group,
          items: processItems(group.items || []),
        })),
      }));
      
      return { ...prev, base: newBase, changeOrders: newChangeOrders };
    });
    
    // Re-enable auto-save after state settles
    setTimeout(() => {
      batchOperationInProgressRef.current = false;
    }, 500);
    
    // Return verification data for "Confirmed Kills" protocol
    return { updatedCount, verificationData };
  }, []);

  // ═══════════════════════════════════════════════════════════════════════════
  // VERIFY STATE CHANGE - "Confirmed Kills" Protocol
  // FIX: Uses same heuristic as batchUpdateMarkup for consistent behavior
  // ═══════════════════════════════════════════════════════════════════════════
  const verifyStateChange = useCallback((verificationType, expectedValue, targetType = null) => {
    const currentItems = getActiveGroups()?.flatMap(g => g.items || []) || [];
    
    // Labor keywords for fallback heuristic (same as batchUpdateMarkup)
    const laborKeywords = [
      'installation', 'install', 'labor', 'demo', 'demolition', 
      'removal', 'haul', 'cleanup', 'rough-in', 'rough in',
      'trim-out', 'finishing', 'hanging', 'mounting', 'setup',
      'set up', 'laying', 'application', 'prep', 'protection'
    ];
    
    // FIX: Heuristic matcher for consistent behavior
    const itemMatchesTarget = (item, target) => {
      if (target === null) return true;
      
      const costType = (item.costType || "").toLowerCase();
      const itemName = (item.itemName || "").toLowerCase();
      
      if (target === "labor") {
        if (costType === "labor" || costType === "lbr") return true;
        // Fallback heuristic
        for (const keyword of laborKeywords) {
          if (itemName.includes(keyword)) return true;
        }
        return false;
      }
      
      if (target === "material") {
        if (costType === "material" || costType === "mat") {
          // Exclude items with labor keywords
          for (const keyword of laborKeywords) {
            if (itemName.includes(keyword)) return false;
          }
          return true;
        }
        return false;
      }
      
      return false;
    };
    
    if (verificationType === "markup") {
      // Check if target items have the expected markup
      let matchCount = 0;
      let targetCount = 0;
      
      currentItems.forEach(item => {
        // FIX: Use heuristic matching
        const isTarget = itemMatchesTarget(item, targetType);
        
        if (isTarget) {
          targetCount++;
          if (parseFloat(item.markupPercent) === parseFloat(expectedValue)) {
            matchCount++;
          }
        }
      });
      
      return { success: matchCount === targetCount, matchCount, targetCount };
    }
    
    return { success: true };
  }, [getActiveGroups]);

  // ═══════════════════════════════════════════════════════════════════════════
  // EXECUTE CACHED SUGGESTIONS - For "yes" confirmations
  // ═══════════════════════════════════════════════════════════════════════════
  const executeCachedSuggestions = useCallback(() => {
    const { lastSuggestedItems, lastSuggestedAction, awaitingConfirmation } = superintendentContextCache;
    
    if (!awaitingConfirmation || lastSuggestedItems.length === 0) {
      return { success: false, reason: "No cached suggestions to execute" };
    }
    
    if (lastSuggestedAction === "add") {
      // Add all cached items
      lastSuggestedItems.forEach(item => {
        addItemToGroup(item.category || 'General', {
          name: item.itemName || item.name,
          cost: item.unitCost || item.cost,
          qty: item.quantity || item.qty || 1,
          unit: item.unit || 'EA',
          type: item.costType || item.type || 'Material',
        });
      });
      
      // Clear the cache
      setSuperintendentContextCache(prev => ({
        ...prev,
        lastSuggestedItems: [],
        awaitingConfirmation: false,
      }));
      
      return { success: true, count: lastSuggestedItems.length, items: lastSuggestedItems };
    }
    
    return { success: false, reason: "Unknown action type" };
  }, [superintendentContextCache, addItemToGroup]);

  // Handle file upload from Superintendent chat (Blueprint/PDF analysis)
  const handleFileUpload = async (file, additionalInstructions = "") => {
    console.log("[File Upload] Starting analysis for:", file.name);
    
    // Add user message with file indicator
    const timestamp = new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    setSuperintendentChat(prev => [...prev, {
      role: "user",
      content: additionalInstructions || `📎 Uploaded: ${file.name}`,
      fileAttachment: {
        name: file.name,
        type: file.type,
        size: file.size,
      },
      timestamp,
    }]);
    
    setSuperintendentLoading(true);
    
    // Add processing message
    setSuperintendentChat(prev => [...prev, {
      type: "action",
      content: `Analyzing ${file.type.includes('pdf') ? 'PDF document' : 'image'}...`,
    }]);
    
    try {
      // Create FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('instructions', additionalInstructions);
      formData.append('lead_id', selectedLead?.lead_id || '');
      formData.append('client_name', selectedLead?.contact_name || selectedLead?.name || 'Client');
      
      // Call blueprint analysis endpoint
      const res = await API.post('/ai/blueprint/analyze', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      if (res.data.success && res.data.grouped_items) {
        // Convert grouped items to estimate format
        const newGroups = Object.entries(res.data.grouped_items).map(([category, items], index) => ({
          id: String(index + 1).padStart(2, "0"),
          code: String(index + 1).padStart(2, "0"),
          name: formatSectionName(index, category), // FIX: Use cleaner to prevent recursive numbering
          items: items,
        }));
        
        // Update scopes with new items
        setScopes((prev) => ({
          ...prev,
          base: { ...prev.base, groups: newGroups },
        }));
        setActiveScope("base");
        setCollapsedGroups({});
        
        const totalItems = newGroups.reduce((sum, g) => sum + (g.items?.length || 0), 0);
        
        // Add success message to chat
        setSuperintendentChat(prev => [...prev, {
          type: "action",
          content: `Extracted ${totalItems} item(s) from ${file.name}`,
        }]);
        
        setSuperintendentChat(prev => [...prev, {
          role: "assistant",
          content: res.data.message || `I've analyzed the ${file.type.includes('pdf') ? 'document' : 'floor plan'} and generated ${totalItems} line items across ${newGroups.length} sections. Let me know if you'd like to add or modify anything.`,
          suggestions: res.data.suggestions,
          timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
        }]);
        
        toast.success("📋 Blueprint Analyzed", {
          description: `${newGroups.length} sections with ${totalItems} items generated`,
        });
      } else {
        throw new Error(res.data.message || "Failed to analyze file");
      }
    } catch (err) {
      console.error("[File Upload] Analysis failed:", err);
      
      // Fallback: Use text-based analysis with file name as context
      setSuperintendentChat(prev => [...prev, {
        role: "assistant",
        content: `I couldn't fully analyze that ${file.type.includes('pdf') ? 'document' : 'image'}. Could you describe the project scope in text? For example: "10x12 bathroom with shower, toilet, and vanity"`,
        timestamp: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' }),
      }]);
    } finally {
      setSuperintendentLoading(false);
    }
  };

  // Generate Project Schedule (Operator Order)
  const handleGenerateSchedule = async () => {
    const currentEstimateId = getCurrentEstimateId();
    if (!currentEstimateId) {
      toast.error("Please select an estimate first");
      return;
    }
    
    setGeneratingSchedule(true);
    try {
      // Collect all line items from current scopes
      const allItems = [];
      const groups = getActiveGroups();
      groups.forEach(group => {
        (group.items || []).forEach(item => {
          allItems.push({
            itemName: item.itemName,
            costType: item.costType,
            category: group.name,
          });
        });
      });
      
      const res = await API.post(`/estimates/${currentEstimateId}/generate-schedule`, {
        line_items: allItems,
        scope_notes: scopeNotes,
        use_chatgpt: true,  // Use ChatGPT for accurate timelines
      });
      
      if (res.data.success) {
        setProjectSchedule(res.data.schedule);
        setSowActiveTab("schedule");
        
        // Show generation source
        const source = res.data.generated_by || "AI";
        const timeline = res.data.timeline || {};
        const weeksText = timeline.recommended_duration || `${res.data.total_days} days`;
        
        toast.success(`📅 Project Schedule Generated by ${source}`, {
          description: `${res.data.total_phases} phases - ${weeksText}`,
        });
        
        // If we have draw schedule, update the estimate state
        if (res.data.draw_schedule && res.data.draw_schedule.length > 0) {
          console.log("Draw schedule received:", res.data.draw_schedule);
        }
      }
    } catch (err) {
      console.error("Schedule generation failed:", err);
      toast.error("Failed to generate schedule");
    } finally {
      setGeneratingSchedule(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════════
  // AI SCOPE COMMANDER - Natural Language Editing
  // ═══════════════════════════════════════════════════════════════════
  const handleAiScopeCommand = async () => {
    if (!aiCommand.trim()) {
      toast.error("Please enter a command");
      return;
    }
    
    // Collect all current line items
    const groups = getActiveGroups();
    const allItems = [];
    groups.forEach(group => {
      (group.items || []).forEach(item => {
        allItems.push({
          id: item.id,
          itemName: item.itemName,
          quantity: item.quantity,
          unit: item.unit,
          unitCost: item.unitCost,
          costType: item.costType || "Material",
          section: group.name,
          markupPercent: item.markupPercent || 0,
        });
      });
    });
    
    if (allItems.length === 0) {
      toast.error("No items to modify. Add some items first or use Auto-Build.");
      return;
    }
    
    setAiCommandLoading(true);
    try {
      const res = await API.post("/ai-scope/command", {
        command: aiCommand,
        line_items: allItems,
        estimate_id: selectedEstimate?.estimate_id,
      });
      
      if (res.data.success) {
        const updatedItems = res.data.updated_items || [];
        
        // Group items by section
        const groupedBySection = {};
        updatedItems.forEach(item => {
          const section = item.section || "General";
          if (!groupedBySection[section]) {
            groupedBySection[section] = [];
          }
          groupedBySection[section].push(item);
        });
        
        // Convert to our groups format
        const newGroups = Object.entries(groupedBySection).map(([sectionName, items], index) => ({
          id: String(index + 1).padStart(2, "0"),
          code: String(index + 1).padStart(2, "0"),
          name: sectionName.toUpperCase(),
          items: items.map((item, itemIdx) => ({
            id: item.id || `${String(index + 1).padStart(2, "0")}-${String(itemIdx + 1).padStart(3, "0")}`,
            itemName: item.itemName,
            quantity: item.quantity,
            unit: item.unit,
            unitCost: item.unitCost,
            costType: item.costType,
            markupPercent: item.markupPercent || 0,
          })),
        }));
        
        // Update the scope
        setScopes((prev) => ({
          ...prev,
          base: { ...prev.base, groups: newGroups },
        }));
        
        // Store result for feedback
        setLastCommandResult(res.data);
        
        // Clear command and show success
        setAiCommand("");
        
        const summary = res.data.change_summary || "Changes applied";
        const modified = res.data.items_modified || 0;
        const added = res.data.items_added || 0;
        const removed = res.data.items_removed || 0;
        
        toast.success("🎯 Command Executed", {
          description: `${summary} (${modified} modified, ${added} added, ${removed} removed)`,
        });
      }
    } catch (err) {
      console.error("AI Scope Command failed:", err);
      toast.error("Command failed: " + (err.response?.data?.detail || err.message));
    } finally {
      setAiCommandLoading(false);
    }
  };

  // Handle Convert To PDF or Invoice
  const handleConvertTo = async (docType) => {
    setConvertMenuOpen(false);
    
    // Open invoice modal for invoice type
    if (docType === "invoice") {
      setInvoiceScopes({ 
        base: true, 
        changeOrders: scopes.changeOrders.map((co) => co.id) 
      });
      setInvoiceModalOpen(true);
      return;
    }
    
    const currentEstimateId = getCurrentEstimateId();
    if (!currentEstimateId) {
      toast.error("Please select an estimate first");
      return;
    }

    setGenerating(true);
    try {
      // Get all items from all scopes
      const baseItems = scopes.base.groups.flatMap((g) =>
        (g.items || []).map((item) => ({
          ...item,
          groupName: g.name,
          scopeType: "base",
          clientTotal: (parseFloat(item.quantity) || 0) * (parseFloat(item.unitCost) || 0) * (1 + (parseFloat(item.markupPercent) || 0) / 100),
        }))
      );
      
      const coItems = scopes.changeOrders.flatMap((co) =>
        co.groups.flatMap((g) =>
          (g.items || []).map((item) => ({
            ...item,
            groupName: `${co.name} - ${g.name}`,
            scopeType: "change_order",
            coNumber: co.number,
            clientTotal: (parseFloat(item.quantity) || 0) * (parseFloat(item.unitCost) || 0) * (1 + (parseFloat(item.markupPercent) || 0) / 100),
          }))
        )
      );

      const response = await API.post(
        `/estimates/${currentEstimateId}/generate-document`,
        {
          document_type: docType,
          line_items: [...baseItems, ...coItems],
        },
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const filename = `${selectedEstimate?.job_name || "estimate"}_${docType}.pdf`;
      link.setAttribute("download", filename.replace(/\s+/g, "_"));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success(`${docType.replace("_", " ")} downloaded`);
    } catch (err) {
      console.error("Document generation failed:", err);
      toast.error("Failed to generate document");
    } finally {
      setGenerating(false);
    }
  };

  // Handle Invoice Generation with scope selection
  const handleGenerateInvoice = async () => {
    setInvoiceModalOpen(false);
    
    const currentEstimateId = getCurrentEstimateId();
    if (!currentEstimateId) {
      toast.error("Please select an estimate first");
      return;
    }

    setGenerating(true);
    try {
      let allItems = [];
      
      // Add base items if selected
      if (invoiceScopes.base) {
        const baseItems = scopes.base.groups.flatMap((g) =>
          (g.items || []).map((item) => ({
            ...item,
            groupName: "BASE CONTRACT - " + g.name,
            scopeType: "base",
            clientTotal: (parseFloat(item.quantity) || 0) * (parseFloat(item.unitCost) || 0) * (1 + (parseFloat(item.markupPercent) || 0) / 100),
          }))
        );
        allItems = [...allItems, ...baseItems];
      }
      
      // Add selected change order items
      for (const coId of invoiceScopes.changeOrders) {
        const co = scopes.changeOrders.find((c) => c.id === coId);
        if (co) {
          const coItems = co.groups.flatMap((g) =>
            (g.items || []).map((item) => ({
              ...item,
              groupName: `${co.name} - ${g.name}`,
              scopeType: "change_order",
              coNumber: co.number,
              clientTotal: (parseFloat(item.quantity) || 0) * (parseFloat(item.unitCost) || 0) * (1 + (parseFloat(item.markupPercent) || 0) / 100),
            }))
          );
          allItems = [...allItems, ...coItems];
        }
      }

      const response = await API.post(
        `/estimates/${currentEstimateId}/generate-document`,
        {
          document_type: "invoice",
          line_items: allItems,
        },
        { responseType: "blob" }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const filename = `${selectedEstimate?.job_name || "estimate"}_invoice.pdf`;
      link.setAttribute("download", filename.replace(/\s+/g, "_"));
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success("Invoice generated");
    } catch (err) {
      console.error("Invoice generation failed:", err);
      toast.error("Failed to generate invoice");
    } finally {
      setGenerating(false);
    }
  };

  // Handle group item change
  const handleGroupItemChange = (groupIndex, itemIndex, field, value) => {
    const currentGroups = getActiveGroups();
    const next = [...(currentGroups || [])];
    const group = { ...(next[groupIndex] || {}) };
    const items = [...(group.items || [])];
    const updated = { ...items[itemIndex] };
    const itemId = updated.id;

    if (field === "quantity" || field === "unitCost" || field === "markupPercent") {
      const num = parseFloat(value);
      updated[field] = Number.isNaN(num) ? 0 : num;
    } else {
      updated[field] = value;
    }

    // SMART TYPE DETECTION: Auto-detect costType when itemName changes
    if (field === "itemName" && value) {
      const detectedType = detectCostType(value);
      const currentType = updated.costType || "Material";
      
      // Only auto-switch if we detected Labor AND current type is still Material (default)
      // This respects manual user changes
      if (detectedType === "Labor" && currentType === "Material") {
        updated.costType = "Labor";
        
        // Add visual feedback - track this item as auto-detected
        if (itemId) {
          setAutoDetectedItems(prev => new Set([...prev, itemId]));
          // Clear the visual feedback after 2 seconds
          setTimeout(() => {
            setAutoDetectedItems(prev => {
              const newSet = new Set(prev);
              newSet.delete(itemId);
              return newSet;
            });
          }, 2000);
        }
      }
    }

    items[itemIndex] = updated;
    group.items = items;
    next[groupIndex] = group;
    setActiveGroups(next);
  };

  // Toggle group collapse
  const toggleGroupCollapse = (groupId) => {
    setCollapsedGroups((prev) => ({
      ...prev,
      [groupId]: !prev[groupId],
    }));
  };

  // Add new line item to current scope
  const addNewLineItem = useCallback(() => {
    const currentGroups = getActiveGroups();
    const newItemId = `item-${Date.now()}`;
    
    if (currentGroups.length === 0) {
      // Create a new group first
      const newGroup = {
        id: `grp-${Date.now()}`,
        code: "NEW",
        name: activeScope === "base" ? "NEW ITEMS" : "CO ITEMS",
        items: [{
          id: newItemId,
          itemName: "",
          quantity: 0,
          unit: "EA",
          unitCost: 0,
          costType: "Material",
          markupPercent: 0,
        }],
      };
      setActiveGroups([newGroup]);
    } else {
      // Add to last group
      const next = [...currentGroups];
      const lastGroupIndex = next.length - 1;
      next[lastGroupIndex] = {
        ...next[lastGroupIndex],
        items: [
          ...(next[lastGroupIndex].items || []),
          {
            id: newItemId,
            itemName: "",
            quantity: 0,
            unit: "EA",
            unitCost: 0,
            costType: "Material",
            markupPercent: 0,
          },
        ],
      };
      setActiveGroups(next);
    }
    
    // Auto-focus the description field after render
    setTimeout(() => {
      const newInput = document.querySelector(`input[data-item-id="${newItemId}"]`);
      if (newInput) newInput.focus();
    }, 100);
  }, [activeScope, getActiveGroups, setActiveGroups]);
  
  // Add new section/group header
  const addNewSection = useCallback(() => {
    const currentGroups = getActiveGroups();
    const sectionNum = String(currentGroups.length + 1).padStart(2, "0");
    const newSectionId = `sec-${Date.now()}`;
    
    const newSection = {
      id: newSectionId,
      code: sectionNum,
      name: `${sectionNum}. NEW SECTION`,
      items: [],
    };
    
    setActiveGroups([...currentGroups, newSection]);
    
    // Auto-focus for section name editing could be added here
    toast.success("New section added - click header to rename");
  }, [getActiveGroups, setActiveGroups]);

  // Handle adding item from HUD (Vision AI)
  const handleAddFromHUD = useCallback((itemData) => {
    if (!itemData) return;
    
    const currentGroups = getActiveGroups();
    const next = [...(currentGroups || [])];
    let targetGroupIndex = next.findIndex((g) => g.name?.includes("Finishes") || g.name?.includes("ITEMS"));
    
    if (targetGroupIndex === -1) {
      const newGroup = {
        id: "AI",
        code: "AI",
        name: "AI. VISION EXTRACTED",
        items: [],
      };
      next.push(newGroup);
      targetGroupIndex = next.length - 1;
    }
    
    const newItem = {
      id: `ai-${Date.now()}`,
      itemName: itemData.itemName || "AI Extracted Item",
      quantity: itemData.quantity || 0,
      unit: itemData.unit || "EA",
      unitCost: itemData.unitCost || 0,
      costType: itemData.costType || "Material",
      markupPercent: itemData.markupPercent || 25,
    };
    
    next[targetGroupIndex] = {
      ...next[targetGroupIndex],
      items: [...(next[targetGroupIndex].items || []), newItem],
    };
    
    setActiveGroups(next);
    toast.success(`Added "${itemData.itemName}" from Vision AI`);
  }, [getActiveGroups, setActiveGroups]);

  // Delete a line item from the current scope
  const handleDeleteItem = useCallback((groupIndex, itemIndex) => {
    const currentGroups = getActiveGroups();
    const next = [...currentGroups];
    const group = { ...next[groupIndex] };
    const items = [...(group.items || [])];
    items.splice(itemIndex, 1);
    group.items = items;
    next[groupIndex] = group;
    
    // Remove empty groups
    if (items.length === 0) {
      next.splice(groupIndex, 1);
    }
    
    setActiveGroups(next);
    toast.success("Item removed");
  }, [getActiveGroups, setActiveGroups]);

  // Generate Estimate - Navigate to Estimates page with current scope
  const handleGenerateEstimate = async () => {
    if (!selectedLead) {
      toast.warning("Please select a Client/Target first");
      return;
    }

    setGenerating(true);
    try {
      // Create or update estimate
      let estimateId = selectedEstimate?.estimate_id;
      
      if (!estimateId) {
        const today = new Date().toISOString().split("T")[0];
        const createRes = await API.post("/estimates", {
          job_name: `${selectedLead.name || selectedLead.contact_name} - Scope ${today}`,
          client_name: selectedLead.name || selectedLead.contact_name,
          lead_id: selectedLead.lead_id,
        });
        estimateId = createRes.data.estimate_id;
      }

      // Save current scope items as materials
      const allItems = scopes.base.groups.flatMap((g) =>
        (g.items || []).map((item) => ({
          item_name: item.itemName || "Unnamed Item",
          quantity: Number(item.quantity) || 0,
          unit: item.unit || "EA",
          unit_cost: Number(item.unitCost) || 0,
          markup_percent: Number(item.markupPercent) || 0,
        }))
      );

      await API.post(`/estimates/${estimateId}/materials/bulk`, { materials: allItems });

      // Calculate and update grand total
      const grandTotal = allItems.reduce((sum, m) => {
        return sum + m.quantity * m.unit_cost * (1 + m.markup_percent / 100);
      }, 0);
      await API.put(`/estimates/${estimateId}`, { grand_total: grandTotal, stage: "draft" });

      toast.success("Estimate generated! Redirecting...");
      setTimeout(() => {
        navigate("/estimates", { state: { newEstimateId: estimateId } });
      }, 1000);
    } catch (err) {
      toast.error("Failed to generate estimate");
    } finally {
      setGenerating(false);
    }
  };

  // Generate AI SOW Documents
  const handleGenerateAISow = async () => {
    const groups = getActiveGroups();
    if (!groups || groups.length === 0) {
      toast.warning("No line items to generate SOW from");
      return;
    }

    setGeneratingSow(true);
    setShowSowModal(true);
    
    try {
      // Build line items summary
      const allItems = groups.flatMap((g) =>
        (g.items || []).map((item) => ({
          name: item.itemName,
          quantity: item.quantity,
          unit: item.unit,
          unitCost: item.unitCost,
          group: g.name,
        }))
      );

      const totals = computeScopeTotals(groups);
      const scopeDescription = buildScopeSummary(groups);

      // Generate CLIENT BRIEF (Homeowner-focused)
      const clientBrief = generateClientBrief(allItems, scopeDescription, totals, selectedLead);
      
      // Generate OPERATOR ORDER (Subcontractor-focused)
      const operatorOrder = generateOperatorOrder(allItems, groups, selectedLead);

      setSowContent({
        client: clientBrief,
        operator: operatorOrder,
      });
    } catch (err) {
      toast.error("Failed to generate SOW documents");
    } finally {
      setGeneratingSow(false);
    }
  };

  // Helper function to format status badge
  const formatStatus = (status) => {
    if (!status) return "Draft";
    return status.replace(/_/g, " ").split(" ").map(word => 
      word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    ).join(" ");
  };

  // Helper function to get margin color
  const getMarginColor = (margin) => {
    if (margin > 20) return "text-emerald-500";
    if (margin < 15) return "text-red-500";
    return "text-amber-500";
  };

  // Get active scope display info
  const getActiveScopeInfo = () => {
    if (activeScope === "base") {
      return { name: "BASE CONTRACT", icon: Lock, type: "base" };
    }
    const co = scopes.changeOrders.find((c) => c.id === activeScope);
    return co ? { name: co.name, icon: FileText, type: "change_order", number: co.number } : { name: "Unknown", icon: FileText, type: "unknown" };
  };

  const activeScopeInfo = getActiveScopeInfo();
  const scopeSummary = buildScopeSummary(groups);
  const clientData = selectedLead || { name: "Select a Lead", status: "Draft" };

  // Sidebar visibility state for full-width mode
  const [sidebarVisible, setSidebarVisible] = useState(false);

  if (loading) {
    return (
      <div className="flex h-screen w-full bg-slate-950 text-slate-100 items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-emerald-400" />
          <p className="text-slate-400">Loading estimating data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen w-full bg-slate-950 text-slate-100 overflow-hidden flex flex-col">
      {/* ═══════════════════════════════════════════════════════════════════════════════
          FULL-WIDTH MISSION SCOPE - NO SIDEBAR
          Smart Scrolling Header with single scroll container
      ═══════════════════════════════════════════════════════════════════════════════ */}
      
      {/* ROW 1: STICKY HEADER - Always Visible */}
      <div 
        className="flex-none bg-slate-900 border-b border-slate-800"
        style={{ 
          zIndex: 200,
          height: '48px',
          boxShadow: headerScrolled ? '0 4px 12px rgba(0, 0, 0, 0.5)' : 'none',
          transition: 'box-shadow 0.3s ease'
        }}
      >
        <div className="h-full px-4 flex items-center justify-between">
          {/* LEFT: Hamburger + Mission Brief + Title + Total (when scrolled) */}
          <div className="flex items-center gap-3">
            {/* Hamburger Menu - Opens sidebar */}
            <button 
              className="p-2 rounded-lg transition-all hover:bg-white/10 active:scale-95"
              style={{ border: '1px solid rgba(255, 255, 255, 0.15)' }}
              onClick={() => setSidebarVisible(!sidebarVisible)}
              data-testid="hamburger-menu-btn"
              title="Toggle Navigation"
            >
              <Menu className="h-5 w-5 text-slate-300" />
            </button>
            
            {/* Mission Brief Button */}
            <button
              onClick={() => setMissionBriefOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-full transition-all"
              style={{
                background: 'rgba(16, 185, 129, 0.15)',
                border: '1px solid rgba(16, 185, 129, 0.4)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'rgba(16, 185, 129, 0.25)';
                e.currentTarget.style.boxShadow = '0 0 15px rgba(16, 185, 129, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'rgba(16, 185, 129, 0.15)';
                e.currentTarget.style.boxShadow = 'none';
              }}
              data-testid="mission-brief-btn"
            >
              <Info className="h-4 w-4 text-emerald-400" />
              <span className={`text-xs font-semibold text-emerald-400 ${headerScrolled ? 'hidden md:inline' : ''}`}>
                Mission Brief
              </span>
            </button>
            
            {/* Estimate Title */}
            <span className="text-sm font-bold text-white truncate max-w-[300px] lg:max-w-[500px]">
              {selectedEstimate?.job_name || conversationalEstimateId ? 'Active Estimate' : 'New Estimate'}
            </span>
            
            {/* New Estimate Button - Only show if there's an active estimate */}
            {(selectedEstimate || conversationalEstimateId || scopes.base.groups.length > 0) && (
              <button
                onClick={handleNewEstimate}
                className="ml-2 px-2 py-1 text-xs font-bold text-slate-400 hover:text-white border border-slate-600 hover:border-emerald-500 rounded transition-all"
                data-testid="new-estimate-btn"
              >
                + NEW
              </button>
            )}
            
            {/* Compact Total - Shows when scrolled */}
            {headerScrolled && (
              <span className="text-sm font-bold text-emerald-400 ml-2">
                ${allTotals.grandTotal.clientTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            )}
          </div>
          
          {/* RIGHT: ESTIMATING COMMAND Button */}
          {!superintendentOpen && (
            <button
              onClick={() => setSuperintendentOpen(true)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg font-bold text-white transition-all hover:scale-105 active:scale-95"
              style={{
                background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)',
                border: '1px solid #10b981',
                boxShadow: '0 0 20px rgba(16, 185, 129, 0.4)',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #34d399 0%, #10b981 100%)';
                e.currentTarget.style.boxShadow = '0 0 30px rgba(16, 185, 129, 0.6)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'linear-gradient(135deg, #10b981 0%, #059669 100%)';
                e.currentTarget.style.boxShadow = '0 0 20px rgba(16, 185, 129, 0.4)';
              }}
              data-testid="estimating-command-btn"
            >
              <HardHat className="h-4 w-4" />
              <span className="hidden sm:inline text-sm tracking-wide">ESTIMATING COMMAND</span>
              <span className="sm:hidden text-xs">COMMAND</span>
            </button>
          )}
        </div>
      </div>
      
      {/* SLIDE-OUT NAVIGATION SIDEBAR */}
      {sidebarVisible && (
        <>
          {/* Overlay */}
          <div 
            className="fixed inset-0 bg-black/60 z-[300] transition-opacity"
            onClick={() => setSidebarVisible(false)}
          />
          {/* Sidebar Panel - Matching Main Sidebar Styling */}
          <div 
            className="fixed left-0 top-0 h-full w-60 bg-slate-900 border-r border-slate-800 z-[301] flex flex-col"
            style={{ boxShadow: '4px 0 20px rgba(0,0,0,0.5)' }}
          >
            {/* Header - Matching Main Sidebar */}
            <div className="flex-shrink-0 p-6 border-b border-slate-800 flex items-center justify-between">
              <div>
                <h1 className="text-lg font-chivo font-bold text-white tracking-tight">
                  CONTRACTOR<span className="text-emerald-500"> OVERWATCH</span>
                </h1>
                <p className="text-xs text-slate-500 mt-1 font-ibm tracking-widest uppercase">Tactical Operations Command</p>
              </div>
              <button 
                onClick={() => setSidebarVisible(false)}
                className="p-1.5 rounded-lg hover:bg-slate-800 transition-colors"
              >
                <X className="h-4 w-4 text-slate-400" />
              </button>
            </div>
            
            {/* Nav Items - Scrollable - Matching Main Sidebar */}
            <nav className="flex-1 min-h-0 p-4 overflow-y-auto overflow-x-hidden">
              <ul className="space-y-1">
                {[
                  { path: "/dashboard", label: "Command Center", icon: Home },
                  { path: "/leads", label: "Targets & Assets", icon: Users },
                  { path: "/appointments", label: "Calendar", icon: Calendar },
                  { path: "/calendar/field-visit", label: "Field Visit", icon: Calendar },
                  { path: "/estimating", label: "Mission Scope", icon: Calculator, active: true },
                  { path: "/blueprint-takeoff", label: "Blueprint Takeoff", icon: FileText },
                  { path: "/estimates", label: "Estimates", icon: ClipboardCheck },
                  { path: "/proposals", label: "Proposals", icon: Send },
                  { path: "/projects", label: "Active Operations", icon: FolderKanban },
                  { path: "/subcontractors", label: "Subcontractors", icon: HardHat },
                  { path: "/walkthroughs", label: "Walkthroughs", icon: ClipboardList },
                  { path: "/purchase-orders", label: "Purchase Orders", icon: ShoppingCart },
                  { path: "/time-tracking", label: "Duty Logs", icon: Clock },
                  { path: "/job-costing", label: "Job Costing", icon: Calculator },
                  { path: "/payments", label: "Payments", icon: CreditCard },
                  { path: "/documents", label: "Documents", icon: FileText },
                  { path: "/reports", label: "Reports", icon: BarChart3 },
                  { path: "/settings", label: "Settings", icon: Settings },
                ].map((item) => (
                  <li key={item.path}>
                    <a
                      href={item.path}
                      className={`flex items-center gap-3 px-4 py-2.5 rounded-md font-ibm text-sm transition-all duration-200 ${
                        item.active 
                          ? 'bg-emerald-500/10 text-emerald-400 font-semibold border-l-2 border-emerald-500' 
                          : 'text-slate-400 hover:bg-slate-800 hover:text-slate-200'
                      }`}
                    >
                      <item.icon size={18} />
                      {item.label}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
            
            {/* User Section - Matching Main Sidebar */}
            <div className="flex-shrink-0 p-4 border-t border-slate-800 bg-slate-900">
              {/* VETERAN OWNED Badge */}
              <div className="flex items-center justify-center gap-1.5 mb-3 py-1.5 px-2 rounded bg-slate-800/50 border border-slate-700/50">
                <span className="text-sm">🇺🇸</span>
                <span className="text-xs font-semibold text-slate-400 tracking-wider">VETERAN OWNED</span>
              </div>
              
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center text-white font-semibold">
                  D
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-white truncate">Demo User</p>
                  <p className="text-xs text-slate-400 truncate">demo@buildtrackpm.com</p>
                </div>
              </div>
              <a
                href="/logout"
                className="flex items-center gap-2 w-full px-4 py-2 text-sm text-slate-300 hover:bg-slate-800 hover:text-white rounded-md transition-colors"
              >
                <LogOut size={16} />
                Sign Out
              </a>
            </div>
          </div>
        </>
      )}
      
      {/* ROW 2: COLLAPSIBLE CONTROLS - Auto-hides on scroll */}
      <div 
        className="flex-none bg-slate-950 border-b border-slate-800"
        style={{ 
          maxHeight: headerScrolled ? '0px' : '120px',
          opacity: headerScrolled ? 0 : 1,
          transition: 'max-height 0.3s ease, opacity 0.2s ease',
          pointerEvents: headerScrolled ? 'none' : 'auto',
          overflow: headerScrolled ? 'hidden' : 'visible',
        }}
      >
          {/* Sub-row A: Controls */}
          <div className="px-4 py-2 flex items-center gap-4 flex-wrap">
            {/* Client/Target Selector */}
            <div className="relative flex-1 min-w-[200px] max-w-[350px]">
              <select
                className="w-full text-xs bg-slate-800 border border-slate-700 rounded-lg pl-3 pr-10 py-2 text-slate-100 focus:outline-none focus:ring-2 focus:ring-emerald-500"
                value={selectedLead?.lead_id || ""}
                onChange={(e) => handleLeadChange(e.target.value)}
                data-testid="client-selector"
              >
                <option value="">Select Client / Target...</option>
                <optgroup label="Leads">
                  {leads.map((lead) => (
                    <option key={lead.lead_id} value={lead.lead_id}>
                      {lead.name || lead.contact_name} - {lead.project_description || lead.service_type || "Project"}
                    </option>
                  ))}
                </optgroup>
                <optgroup label="Clients">
                  {clients.map((client) => (
                    <option key={client.client_id} value={client.client_id}>
                      {client.name}
                    </option>
                  ))}
                </optgroup>
              </select>
              <Users className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 pointer-events-none" />
            </div>
            
            {/* Main Estimate Tab - Same row as client selector */}
            <button
              onClick={() => {
                setActiveScope("base");
                setActiveTab("main");
              }}
              className={`flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all ${
                activeScope === "base"
                  ? "text-emerald-500"
                  : "text-slate-400 hover:text-slate-200"
              }`}
              style={{
                background: activeScope === "base" ? 'rgba(16, 185, 129, 0.2)' : 'rgba(30, 41, 59, 0.5)',
                border: `1px solid ${activeScope === "base" ? 'rgba(16, 185, 129, 0.4)' : 'rgba(255, 255, 255, 0.1)'}`,
                minWidth: '140px'
              }}
              data-testid="tab-main-estimate"
            >
              <FileText className="h-4 w-4" />
              <span>Main Estimate</span>
            </button>
            
            {/* + Change Order Button - Same row as client selector */}
            <button
              onClick={() => {
                createChangeOrder();
                setActiveTab("main");
              }}
              className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-all text-amber-400 hover:text-amber-300"
              style={{
                background: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.3)',
                minWidth: '140px'
              }}
              data-testid="btn-new-change-order"
            >
              <Plus className="h-4 w-4" />
              <span>Change Order</span>
            </button>
            
            {/* Risk Badge - Same row */}
            {threatAssessment.threatLevel !== "green" && (
              <button
                onClick={() => setThreatModalOpen(true)}
                className="flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold cursor-pointer transition-all hover:scale-105"
                style={{
                  background: threatAssessment.threatLevel === "red" 
                    ? 'rgba(255, 68, 68, 0.15)' 
                    : 'rgba(245, 158, 11, 0.15)',
                  border: `1px solid ${threatAssessment.threatLevel === "red" ? 'rgba(255, 68, 68, 0.4)' : 'rgba(245, 158, 11, 0.4)'}`,
                  minWidth: '100px'
                }}
                data-testid="risk-badge"
              >
                {threatAssessment.threatLevel === "red" ? (
                  <ShieldAlert className="h-4 w-4 text-red-400" />
                ) : (
                  <Shield className="h-4 w-4 text-amber-400" />
                )}
                <span className={threatAssessment.threatLevel === "red" ? 'text-red-400' : 'text-amber-400'}>
                  {threatAssessment.threatLevel === "red" ? 'CRITICAL' : 'CAUTION'}
                </span>
              </button>
            )}
            
            {/* Current Total - Same row */}
            <div 
              className="flex items-center justify-center px-4 py-2 rounded-lg text-sm font-semibold"
              style={{
                background: 'rgba(16, 185, 129, 0.1)',
                border: '1px solid rgba(16, 185, 129, 0.3)',
                minWidth: '140px'
              }}
            >
              <span className="text-emerald-400 font-mono">
                ${currentScopeTotals.clientTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
            
            {/* Save Button */}
            <button
              onClick={handleSaveEstimate}
              className="p-2 rounded-lg transition-all hover:bg-white/5"
              style={{ border: '1px solid rgba(255, 255, 255, 0.1)' }}
              title="Save Estimate"
              data-testid="save-estimate-btn"
            >
              <Save className="h-4 w-4 text-slate-400" />
            </button>
          </div>
          
          {/* Sub-row B: Change Order Tabs (only show if there are change orders) */}
          {scopes.changeOrders.length > 0 && (
            <div className="px-4 py-2 flex items-center gap-2 flex-wrap">
              <span className="text-xs text-slate-500 uppercase tracking-wider mr-2">Change Orders:</span>
              {/* Individual Change Order Tabs */}
              {scopes.changeOrders.map((co) => (
                <button
                  key={co.id}
                  onClick={() => {
                    setActiveScope(co.id);
                    setActiveTab("main");
                  }}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                    activeScope === co.id
                      ? "text-amber-400"
                      : "text-slate-400 hover:text-slate-200"
                  }`}
                  style={{
                    background: activeScope === co.id ? 'rgba(245, 158, 11, 0.2)' : 'rgba(255, 255, 255, 0.02)',
                    border: `1px solid ${activeScope === co.id ? 'rgba(245, 158, 11, 0.4)' : 'rgba(255, 255, 255, 0.1)'}`,
                  }}
                  data-testid={`tab-${co.id}`}
                >
                  <GitCompare className="h-3.5 w-3.5" />
                  <span>CO #{co.number}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Table Container - TACTICAL FINANCIAL ENGINE with scroll ref */}
        <div 
          ref={scrollContainerRef}
          data-testid="estimate-scroll-container"
          className="flex-1 overflow-auto px-4 py-3"
        >
          <div className="w-full">
            <div className="rounded-lg border border-slate-800 overflow-x-auto">
              <table className="w-full text-sm" style={{ tableLayout: 'fixed' }}>
                <colgroup>
                  <col style={{width: '40px'}} />
                  <col style={{width: '200px'}} />
                  <col style={{width: '140px'}} />
                  <col style={{width: '70px'}} />
                  <col style={{width: '70px'}} />
                  <col style={{width: '90px'}} />
                  <col style={{width: '80px'}} />
                  <col style={{width: '80px'}} />
                  <col style={{width: '100px'}} />
                  <col style={{width: '40px'}} />
                </colgroup>
                <thead 
                  className="sticky top-0"
                  style={{ 
                    backgroundColor: '#0f172a',
                    zIndex: 100,
                  }}
                >
                  <tr 
                    className="text-slate-400 text-left text-xs uppercase tracking-wider"
                    style={{ backgroundColor: '#0f172a' }}
                  >
                    <th className="px-2 py-3" style={{ backgroundColor: '#0f172a' }}></th>
                    <th className="px-2 py-3" style={{ backgroundColor: '#0f172a' }}>Item</th>
                    <th className="px-2 py-3" style={{ backgroundColor: '#0f172a' }}>Description</th>
                    <th className="px-2 py-3 text-center" style={{ backgroundColor: '#0f172a' }}>Qty</th>
                    <th className="px-2 py-3 text-right" style={{ backgroundColor: '#0f172a' }}>Unit cost</th>
                    <th className="px-2 py-3 text-center" style={{ backgroundColor: '#0f172a' }}>Cost type</th>
                    <th className="px-2 py-3 text-right" style={{ backgroundColor: '#0f172a' }}>Builder cost</th>
                    <th className="px-2 py-3 text-center" style={{ backgroundColor: '#0f172a' }}>Markup</th>
                    <th className="px-2 py-3 text-right" style={{ backgroundColor: '#0f172a' }}>Client total</th>
                    <th className="px-2 py-3 text-center" style={{ backgroundColor: '#0f172a' }}></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/50">
                  {groups.map((group, gIndex) => {
                    const groupTotals = computeGroupTotals(group);
                    const isCollapsed = collapsedGroups[group.id];
                    const sectionNumber = String(gIndex + 1);

                    return (
                      <React.Fragment key={group.id}>
                        {/* Section Header Row - Handoff AI Style */}
                        <tr
                          className="bg-slate-800/80 cursor-pointer hover:bg-slate-700/80 border-l-4 border-emerald-500"
                          onClick={() => toggleGroupCollapse(group.id)}
                        >
                          <td className="px-2 py-3">
                            {isCollapsed ? (
                              <ChevronRight className="h-4 w-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-slate-400" />
                            )}
                          </td>
                          <td className="px-2 py-3">
                            <div className="flex items-center gap-2">
                              <span className="text-white font-semibold text-sm">
                                {sectionNumber}
                              </span>
                              <span className="font-semibold text-white text-sm">
                                {group.name.replace(/^\d+\.\s*/, '')}
                              </span>
                            </div>
                          </td>
                          <td className="px-2 py-3"></td>
                          <td className="px-2 py-3 text-center text-slate-400 text-sm">
                            {(group.items || []).length} items
                          </td>
                          <td></td>
                          <td></td>
                          <td className="px-2 py-3 text-right">
                            <span className="text-white font-semibold text-sm">
                              ${groupTotals.builder.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </td>
                          <td></td>
                          <td className="px-2 py-3 text-right">
                            <span className="text-white font-semibold text-sm">
                              ${groupTotals.client.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                            </span>
                          </td>
                          <td></td>
                        </tr>

                        {/* Group Items - Indented under section */}
                        {!isCollapsed &&
                          (group.items || []).map((item, index) => {
                            // === OVERWATCH FINANCIAL ENGINE: Reactive Calculations ===
                            const qty = parseFloat(item?.quantity) || 0;
                            const unitCost = parseFloat(item?.unitCost) || 0;
                            const markup = parseFloat(item?.markupPercent) || 0; // Default 0% for cleaner display
                            
                            // Builder Cost = Qty × Unit Cost (before markup)
                            const builderCost = qty * unitCost;
                            
                            // Markup Amount = Builder Cost × (Markup / 100)
                            const markupAmount = builderCost * (markup / 100);
                            
                            // Client Price = Builder Cost + Markup Amount
                            const clientPrice = builderCost + markupAmount;
                            
                            const isChangeOrder = item.isChangeOrder;
                            const costType = item.costType || "Material";
                            
                            // Hierarchical numbering (1.1, 1.2, etc.)
                            const itemNumber = `${gIndex + 1}.${index + 1}`;

                            // Cost Type Badge Classes (Tactical)
                            const getCostTypeBadgeClasses = (type) => {
                              switch (type?.toLowerCase()) {
                                case "labor":
                                  return "bg-amber-500/20 text-amber-300 border-amber-500/50";
                                case "material":
                                default:
                                  return "bg-blue-500/20 text-blue-300 border-blue-500/50";
                              }
                            };

                            return (
                              <tr
                                key={item.id || index}
                                className={`${
                                  activeScope !== "base"
                                    ? "bg-amber-900/10 border-l-4 border-l-amber-500/50"
                                    : "hover:bg-slate-900/40 border-l-4 border-l-transparent"
                                }`}
                              >
                                {/* Item Number */}
                                <td className="px-2 py-2 align-middle">
                                  <span className="text-xs text-slate-500 font-mono">{itemNumber}</span>
                                </td>
                                
                                {/* Item Name - with indentation */}
                                <td className="px-2 py-2 align-middle">
                                  <div className="pl-2 border-l-2 border-slate-700/50">
                                    <input
                                      data-item-id={item.id}
                                      className="w-full bg-transparent border border-slate-700/30 rounded px-2 py-1.5 text-slate-100 text-sm font-medium focus:outline-none focus:ring-1 focus:ring-emerald-500/50 focus:border-emerald-500/50"
                                      value={item.itemName || ""}
                                      placeholder="Enter item..."
                                      onChange={(e) =>
                                        handleGroupItemChange(gIndex, index, "itemName", e.target.value)
                                      }
                                    />
                                  </div>
                                </td>
                                
                                {/* Description - contextual info like "General Labor Rate" */}
                                <td className="px-2 py-2 align-middle">
                                  <input
                                    className="w-full bg-transparent border border-slate-700/30 rounded px-2 py-1.5 text-slate-400 text-xs focus:outline-none focus:ring-1 focus:ring-emerald-500/50"
                                    value={item.description || ""}
                                    placeholder={costType === "Labor" ? "Labor rate type..." : "Material type..."}
                                    onChange={(e) =>
                                      handleGroupItemChange(gIndex, index, "description", e.target.value)
                                    }
                                  />
                                </td>
                                
                                {/* Qty + Unit combined */}
                                <td className="px-2 py-2 align-middle">
                                  <div className="flex items-center gap-1">
                                    <input
                                      type="number"
                                      step="0.01"
                                      className="w-12 bg-transparent border border-slate-700/30 rounded px-1 py-1.5 text-center text-slate-100 font-mono text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-emerald-500/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                      value={item.quantity === 0 || item.quantity === "0" ? "" : (item.quantity ?? "")}
                                      placeholder="0"
                                      onChange={(e) =>
                                        handleGroupItemChange(gIndex, index, "quantity", e.target.value)
                                      }
                                    />
                                    <select
                                      className="w-14 bg-slate-800/50 border border-slate-700/30 rounded px-1 py-1.5 text-slate-300 focus:outline-none focus:ring-1 focus:ring-emerald-500/50 text-xs text-center font-medium"
                                      value={item.unit || "EA"}
                                      onChange={(e) =>
                                        handleGroupItemChange(gIndex, index, "unit", e.target.value)
                                      }
                                    >
                                      <option value="SF">SF</option>
                                      <option value="LF">LF</option>
                                      <option value="EA">EA</option>
                                      <option value="HRS">HRS</option>
                                      <option value="LS">LS</option>
                                      <option value="GAL">GAL</option>
                                      <option value="LB">LB</option>
                                    </select>
                                  </div>
                                </td>
                                
                                {/* Unit Cost with $ prefix */}
                                <td className="px-2 py-2 align-middle">
                                  <div className="flex items-center">
                                    <span className="text-slate-500 text-sm mr-1">$</span>
                                    <input
                                      type="number"
                                      step="0.01"
                                      className={`w-full bg-transparent border rounded px-1 py-1.5 text-right font-mono text-sm font-semibold focus:outline-none focus:ring-1 focus:ring-emerald-500/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
                                        item.autoEstimated 
                                          ? 'border-amber-500/50 text-amber-300 bg-amber-500/10' 
                                          : 'border-slate-700/30 text-slate-100'
                                      }`}
                                      value={item.unitCost === 0 || item.unitCost === "0" ? "" : (item.unitCost ?? "")}
                                      placeholder="0.00"
                                      onChange={(e) =>
                                        handleGroupItemChange(gIndex, index, "unitCost", e.target.value)
                                      }
                                      title={item.autoEstimated ? "⚠️ Auto-estimated - verify pricing" : ""}
                                    />
                                  </div>
                                </td>
                                
                                {/* Cost Type Badge */}
                                <td className="px-2 py-2 align-middle">
                                  <select
                                    className={`w-full rounded px-1 py-1.5 text-xs font-bold tracking-wide border focus:outline-none focus:ring-1 focus:ring-emerald-500/50 ${getCostTypeBadgeClasses(costType)}`}
                                    value={costType}
                                    onChange={(e) =>
                                      handleGroupItemChange(gIndex, index, "costType", e.target.value)
                                    }
                                  >
                                    <option value="Material" className="bg-slate-900 text-slate-100">Material</option>
                                    <option value="Labor" className="bg-slate-900 text-slate-100">Labor</option>
                                  </select>
                                </td>
                                
                                {/* Builder Cost (qty × unitCost) */}
                                <td className="px-2 py-2 align-middle text-right">
                                  <span className="text-slate-300 font-mono text-sm">
                                    ${builderCost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </td>
                                
                                {/* Markup % + $ amount */}
                                <td className="px-2 py-2 align-middle">
                                  <div className="flex flex-col items-center gap-0.5">
                                    <div className="flex items-center">
                                      <input
                                        type="number"
                                        step="1"
                                        className="w-10 bg-transparent border border-slate-700/30 rounded px-1 py-0.5 text-center text-slate-100 font-mono text-xs font-semibold focus:outline-none focus:ring-1 focus:ring-emerald-500/50 [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                                        value={item.markupPercent === 0 || item.markupPercent === "0" ? "" : (item.markupPercent ?? "")}
                                        placeholder="0"
                                        onChange={(e) =>
                                          handleGroupItemChange(gIndex, index, "markupPercent", e.target.value)
                                        }
                                      />
                                      <span className="text-slate-500 text-xs ml-0.5">%</span>
                                    </div>
                                    <span className="text-slate-500 text-xs">
                                      ${markupAmount.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                    </span>
                                  </div>
                                </td>
                                
                                {/* Client Total (Calculated) */}
                                <td className="px-2 py-2 align-middle text-right">
                                  <span className="text-emerald-400 font-bold font-mono text-sm">
                                    ${clientPrice.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </span>
                                </td>
                                
                                {/* Delete Button */}
                                <td className="px-2 py-2 align-middle text-center">
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleDeleteItem(gIndex, index);
                                    }}
                                    className="p-1 text-slate-500 hover:text-red-400 hover:bg-red-500/10 rounded transition-colors"
                                    title="Delete item"
                                  >
                                    <Trash2 className="h-3.5 w-3.5" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                      </React.Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

        </div>
        {/* End of scroll container */}

        {/* === TACTICAL SUMMARY FOOTER - STICKY BOTTOM === */}
        <div className="flex-none border-t border-slate-700 bg-slate-900">
          {/* Current Scope Totals */}
          {activeScope !== "base" && (
            <div className="px-4 py-2 border-b border-slate-800 bg-amber-500/5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-4 w-4 text-amber-400" />
                  <span className="text-xs font-bold text-amber-300 tracking-wide">{activeScopeInfo.name}</span>
                </div>
                <div className="text-right">
                  <span className="text-xs text-slate-400 mr-2">Scope Total:</span>
                  <span className="text-amber-300 font-mono font-bold">
                    ${currentScopeTotals.clientTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </div>
              </div>
            </div>
          )}
          
          {/* Main Totals Grid - 5 columns */}
          <div className="grid grid-cols-5 divide-x divide-slate-800">
            {/* TOTAL COST (My Hard Cost) - With Material/Labor Breakdown */}
            <div className="px-3 py-3 text-center">
              <div className="text-xs text-slate-500 uppercase tracking-widest mb-1 font-medium">
                Total Cost
              </div>
              {/* Material/Labor Breakdown */}
              <div className="flex justify-center gap-3 text-xs mb-1">
                <span className="text-blue-400">
                  Mat: ${costBreakdown.materialCost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                <span className="text-amber-400">
                  Lab: ${costBreakdown.laborCost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </span>
                {/* Quick fix button if labor is $0 */}
                {costBreakdown.laborCost === 0 && costBreakdown.materialCost > 0 && (
                  <button
                    onClick={reclassifyAllItems}
                    className="text-emerald-400 hover:text-emerald-300 underline"
                    title="Auto-detect and fix Labor vs Material types"
                  >
                    Fix
                  </button>
                )}
              </div>
              {/* Total */}
              <div className="text-slate-200 font-mono text-xl font-bold">
                ${allTotals.grandTotal.builderTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                My hard cost
              </div>
            </div>
            
            {/* BASE PRICE (Total Client Price) */}
            <div className="px-3 py-3 text-center">
              <div className="text-xs text-slate-400 uppercase tracking-widest mb-1 flex items-center justify-center gap-1 font-medium">
                <Lock className="h-3 w-3" />
                Base Price
              </div>
              <div className="text-slate-200 font-mono text-xl font-semibold">
                ${allTotals.base.clientTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-slate-600 mt-0.5">
                Client price
              </div>
            </div>
            
            {/* Change Orders Total */}
            <div className="px-3 py-3 text-center bg-amber-500/5">
              <div className="text-xs text-amber-400 uppercase tracking-widest mb-1 font-medium">
                CO Total ({scopes.changeOrders.length})
              </div>
              <div className="text-amber-300 font-mono text-xl font-bold">
                ${(allTotals.grandTotal.clientTotal - allTotals.base.clientTotal).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-amber-600/70 mt-0.5">
                {scopes.changeOrders.length > 0 
                  ? scopes.changeOrders.map(co => `CO#${co.number}`).join(" + ")
                  : "No change orders"}
              </div>
            </div>
            
            {/* Net Profit - Dynamic Coloring */}
            {(() => {
              const profit = allTotals.grandTotal.clientTotal - allTotals.grandTotal.builderTotal;
              const margin = allTotals.grandTotal.clientTotal > 0 
                ? ((profit / allTotals.grandTotal.clientTotal) * 100)
                : 0;
              const marginColor = getMarginColor(margin);
              
              return (
                <div className="px-3 py-3 text-center bg-emerald-500/5">
                  <div className={`text-xs uppercase tracking-widest mb-1 font-medium ${marginColor}`}>Net Profit</div>
                  <div className={`font-mono text-xl font-bold ${marginColor}`}>
                    ${profit.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                  <div className={`text-xs mt-0.5 ${marginColor}`}>
                    {margin.toFixed(1)}% margin
                  </div>
                </div>
              );
            })()}
            
            {/* Grand Total */}
            <div className="px-3 py-3 text-center bg-slate-800/30">
              <div className="text-xs text-emerald-300 uppercase tracking-widest mb-1 font-medium">
                Grand Total
              </div>
              <div className="text-emerald-300 font-mono text-2xl font-bold">
                ${allTotals.grandTotal.clientTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </div>
              <div className="text-xs text-slate-500 mt-0.5">
                Base + COs
              </div>
            </div>
          </div>
          
          {/* Material Prices Timestamp */}
          <div className="text-center py-2 border-t border-slate-800/50">
            <span className="text-xs text-slate-500 flex items-center justify-center gap-1.5">
              <Info className="h-3 w-3" />
              Material prices last updated: January 2026 • Labor rates: Fully loaded Midwest market
            </span>
          </div>
        </div>

      {/* === OVERWATCH TACTICAL HUD (Overlay) === */}
      {hudOpen && (
        <OverwatchHUD 
          estimateData={{ groups, totals }}
          onAddToEstimate={handleAddFromHUD}
          onClose={() => setHudOpen(false)}
        />
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* GRANDMASTER PROJECT ASSESSMENT MODAL */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {grandmasterModalOpen && projectAssessment && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" style={{ zIndex: 2147483645 }}>
          <div className="bg-slate-900 border border-slate-700 rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="px-6 py-4 border-b border-slate-800 bg-gradient-to-r from-amber-900/30 to-slate-900">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center">
                    <Shield className="h-5 w-5 text-white" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-white">GRANDMASTER ASSESSMENT</h3>
                    <p className="text-xs text-slate-400">30-Year Veteran Project Analysis • {projectAssessment.room_type?.toUpperCase()} PROJECT</p>
                  </div>
                </div>
                <button
                  onClick={() => setGrandmasterModalOpen(false)}
                  className="p-2 text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Modal Body - Scrollable */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              
              {/* Recommendation Banner */}
              <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-sm text-amber-200 font-medium">Veteran Recommendation</p>
                    <p className="text-xs text-slate-300 mt-1">{projectAssessment.recommendation}</p>
                  </div>
                </div>
              </div>

              {/* SECTION 1: DEMOLITION & PROTECTION */}
              {assessmentChecklist.demolition.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Trash2 className="h-4 w-4 text-red-400" />
                    <h4 className="text-sm font-bold text-white uppercase tracking-wider">Demolition & Protection</h4>
                    <span className="text-xs text-slate-500">(The Tear Out Rule)</span>
                  </div>
                  <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 divide-y divide-slate-700/50">
                    {assessmentChecklist.demolition.map((item) => (
                      <label key={item.id} className="flex items-center gap-4 p-4 hover:bg-slate-800/50 cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={item.checked}
                          onChange={() => toggleAssessmentItem('demolition', item.id)}
                          className="w-5 h-5 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500/50"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-white">{item.demo_item}</p>
                          <p className="text-xs text-slate-400 mt-0.5">{item.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-amber-400">${item.estimated_cost?.toLocaleString()}</p>
                          <p className="text-xs text-slate-500">{item.unit}</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* SECTION 2: RISK FACTORS */}
              {assessmentChecklist.risks.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <ShieldAlert className="h-4 w-4 text-orange-400" />
                    <h4 className="text-sm font-bold text-white uppercase tracking-wider">Risk Factors</h4>
                    <span className="text-xs text-slate-500">(Hidden Costs)</span>
                  </div>
                  <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 divide-y divide-slate-700/50">
                    {assessmentChecklist.risks.map((item) => (
                      <label key={item.id} className="flex items-center gap-4 p-4 hover:bg-slate-800/50 cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={item.checked}
                          onChange={() => toggleAssessmentItem('risks', item.id)}
                          className="w-5 h-5 rounded border-slate-600 bg-slate-800 text-orange-500 focus:ring-orange-500/50"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm font-medium text-white">{item.name}</p>
                            <span className={`px-1.5 py-0.5 text-xs font-bold uppercase rounded ${
                              item.probability === 'high' ? 'bg-red-500/20 text-red-400' :
                              item.probability === 'medium' ? 'bg-orange-500/20 text-orange-400' :
                              'bg-slate-500/20 text-slate-400'
                            }`}>
                              {item.probability}
                            </span>
                          </div>
                          <p className="text-xs text-slate-400 mt-0.5">{item.description}</p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-orange-400">${item.estimated_cost?.toLocaleString()}</p>
                          <p className="text-xs text-slate-500">Allowance</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* SECTION 3: MISSING SCOPE ITEMS */}
              {assessmentChecklist.missing.length > 0 && (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Plus className="h-4 w-4 text-emerald-400" />
                    <h4 className="text-sm font-bold text-white uppercase tracking-wider">Missing Scope Items</h4>
                    <span className="text-xs text-slate-500">(Commonly Forgotten)</span>
                  </div>
                  <div className="bg-slate-800/50 rounded-xl border border-slate-700/50 divide-y divide-slate-700/50">
                    {assessmentChecklist.missing.map((item) => (
                      <label key={item.id} className="flex items-start gap-4 p-4 hover:bg-slate-800/50 cursor-pointer transition-colors">
                        <input
                          type="checkbox"
                          checked={item.checked}
                          onChange={() => toggleAssessmentItem('missing', item.id)}
                          className="w-5 h-5 rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500/50 mt-0.5"
                        />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="px-2 py-0.5 text-xs font-bold uppercase rounded bg-emerald-500/20 text-emerald-400">
                              {item.category}
                            </span>
                          </div>
                          <p className="text-xs text-slate-400 mt-1">{item.description}</p>
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {item.suggested_items?.map((suggested, idx) => (
                              <span key={idx} className="px-2 py-0.5 text-xs bg-slate-700/50 text-slate-300 rounded">
                                {suggested}
                              </span>
                            ))}
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-emerald-400">${item.estimated_costs?.total?.toLocaleString()}</p>
                          <p className="text-xs text-slate-500">Est. Total</p>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              )}

            </div>

            {/* Modal Footer */}
            <div className="px-6 py-4 border-t border-slate-800 bg-slate-900/80">
              <div className="flex items-center justify-between">
                <div className="text-sm">
                  <span className="text-slate-400">Selected Additions: </span>
                  <span className="text-white font-bold">
                    ${(
                      assessmentChecklist.demolition.filter(i => i.checked).reduce((sum, i) => sum + (i.estimated_cost || 0), 0) +
                      assessmentChecklist.risks.filter(i => i.checked).reduce((sum, i) => sum + (i.estimated_cost || 0), 0) +
                      assessmentChecklist.missing.filter(i => i.checked).reduce((sum, i) => sum + (i.estimated_costs?.total || 0), 0)
                    ).toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setGrandmasterModalOpen(false)}
                    className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleGrandmasterBuild}
                    disabled={grandmasterLoading}
                    className="px-6 py-2.5 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-white text-sm font-bold rounded-lg shadow-lg shadow-amber-500/25 hover:shadow-amber-500/40 transition-all flex items-center gap-2 disabled:opacity-50"
                  >
                    {grandmasterLoading ? (
                      <>
                        <Loader2 className="h-4 w-4 animate-spin" />
                        Building...
                      </>
                    ) : (
                      <>
                        <Zap className="h-4 w-4" />
                        Generate Full Estimate
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* UNIVERSAL DOCUMENT GENERATOR - PREVIEW MODAL */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <DocumentPreviewModal
        isOpen={docPreviewOpen}
        onClose={() => {
          setDocPreviewOpen(false);
          // Return to Mission Brief if we came from there
          if (returnToMissionBrief) {
            setReturnToMissionBrief(false);
            setMissionBriefOpen(true);
          }
        }}
        documentType={docPreviewType}
        // FIX: Construct data object if selectedEstimate is null
        estimateData={selectedEstimate || {
          estimate_id: conversationalEstimateId || "temp",
          job_name: "Draft Estimate",
          groups: scopes.base.groups,
          totalCost: costBreakdown.grandTotal
        }}
        // FIX: Fallback client info
        clientInfo={selectedLead ? {
            name: selectedLead.contact_name || selectedLead.name,
            address: selectedLead.address || selectedLead.job_address,
            phone: selectedLead.phone,
            lead_id: selectedLead.lead_id
        } : { name: "Valued Client", address: "TBD" }}
        onSend={async (method, data) => toast.success(`Sent via ${method}`)}
      />

      {/* === MISSION BRIEF OVERLAY === */}
      {missionBriefOpen && (
        <div 
          className="fixed inset-0 flex items-center justify-center p-4"
          style={{ 
            zIndex: 2147483646,
            backgroundColor: 'rgba(0, 0, 0, 0.85)',
            backdropFilter: 'blur(8px)',
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) setMissionBriefOpen(false);
          }}
          onKeyDown={(e) => {
            if (e.key === 'Escape') setMissionBriefOpen(false);
          }}
        >
          <div 
            className="w-full max-w-5xl max-h-[90vh] flex flex-col rounded-lg overflow-hidden"
            style={{
              backgroundColor: '#0f172a',
              border: '1px solid #1e293b',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)',
              animation: 'missionBriefFadeIn 0.3s ease',
            }}
          >
            {/* Header */}
            <div 
              className="flex-shrink-0 flex items-center justify-between px-6 py-4"
              style={{
                background: 'linear-gradient(to right, rgba(15, 23, 42, 1), rgba(30, 41, 59, 0.8))',
                borderBottom: '1px solid #1e293b',
              }}
            >
              <div className="flex items-center gap-4">
                <h2 
                  className="font-rajdhani font-bold uppercase"
                  style={{ 
                    color: '#10b981', 
                    fontSize: '22px', 
                    letterSpacing: '3px' 
                  }}
                >
                  MISSION BRIEF
                </h2>
                {/* Documents auto-populate on every estimate save - no manual button needed */}
              </div>
              <div className="flex items-center gap-4">
                {selectedEstimate?.estimate_id && (
                  <span 
                    className="px-3 py-1 rounded text-xs font-mono"
                    style={{ 
                      backgroundColor: 'rgba(100, 116, 139, 0.2)',
                      color: '#94a3b8',
                      border: '1px solid rgba(100, 116, 139, 0.3)',
                    }}
                  >
                    {selectedEstimate.estimate_id}
                  </span>
                )}
                <button
                  onClick={() => setMissionBriefOpen(false)}
                  className="text-slate-400 hover:text-red-400 transition-colors text-2xl font-light"
                  data-testid="mission-brief-close"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Scrollable Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
              
              {/* Section 1: Client Information */}
              <div>
                <h3 className="text-xs font-bold uppercase text-slate-500 tracking-wider mb-3 pb-2 border-b border-slate-800">
                  CLIENT INFORMATION
                </h3>
                <div 
                  className="p-5 rounded-lg"
                  style={{
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.05), rgba(16, 185, 129, 0.02))',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                  }}
                >
                  <div className="flex items-start gap-5">
                    {/* Avatar */}
                    <div 
                      className="flex-shrink-0 w-16 h-16 rounded-lg flex items-center justify-center font-rajdhani font-bold text-xl"
                      style={{
                        backgroundColor: 'rgba(16, 185, 129, 0.15)',
                        border: '2px solid #10b981',
                        color: '#10b981',
                      }}
                    >
                      {(selectedLead?.contact_name || selectedLead?.name || 'C').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase()}
                    </div>
                    {/* Details */}
                    <div className="flex-1 space-y-2">
                      <div>
                        <p className="font-rajdhani font-bold text-xl text-white">
                          {selectedLead?.contact_name || selectedLead?.name || 'Client Name'}
                        </p>
                        <p className="text-xs text-slate-500 uppercase tracking-wider">
                          {selectedLead?.service_type || 'General Contracting'}
                        </p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
                        <div className="flex">
                          <span className="text-slate-500 w-20">Address:</span>
                          <span className="font-mono text-slate-300">{selectedLead?.address || selectedLead?.project_address || 'Not specified'}</span>
                        </div>
                        <div className="flex">
                          <span className="text-slate-500 w-20">Phone:</span>
                          <span className="font-mono text-slate-300">{selectedLead?.phone || 'Not specified'}</span>
                        </div>
                        <div className="flex">
                          <span className="text-slate-500 w-20">Email:</span>
                          <span className="font-mono text-slate-300">{selectedLead?.email || 'Not specified'}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2: Mission Objectives */}
              <div>
                <h3 className="text-xs font-bold uppercase text-slate-500 tracking-wider mb-3 pb-2 border-b border-slate-800">
                  MISSION OBJECTIVES
                </h3>
                {/* Empty State - No estimate created yet */}
                {!selectedEstimate && !conversationalEstimateId && scopes.base.groups.length === 0 ? (
                  <div 
                    className="p-8 rounded-lg text-center"
                    style={{
                      backgroundColor: 'rgba(30, 41, 59, 0.5)',
                      border: '2px dashed #334155',
                    }}
                  >
                    <p className="text-slate-400 text-sm mb-2">No estimate created yet</p>
                    <p className="text-slate-500 text-xs">Use the ESTIMATING COMMAND to build an estimate, then documents will auto-populate here.</p>
                  </div>
                ) : (
                  <div 
                    className="p-5 rounded-lg"
                    style={{
                      backgroundColor: 'rgba(30, 41, 59, 0.5)',
                      border: '1px solid #1e293b',
                    }}
                  >
                    <h4 className="font-rajdhani font-bold text-xl text-white uppercase mb-4">
                      {selectedEstimate?.job_name || scopeNotes?.substring(0, 50) || 'New Construction Project'}
                    </h4>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      {/* Contract Value */}
                      <div className="space-y-1">
                        <p className="text-xs font-bold uppercase text-slate-500 tracking-wider">CONTRACT VALUE</p>
                        <p className="text-xl font-bold text-emerald-400">
                          ${allTotals.grandTotal.clientTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </p>
                      </div>
                      {/* Start Date */}
                      <div className="space-y-1">
                        <p className="text-xs font-bold uppercase text-slate-500 tracking-wider">START DATE</p>
                        <p className="text-base text-slate-300">
                          {selectedEstimate?.start_date || 'TBD'}
                        </p>
                      </div>
                      {/* Threat Assessment - Clickable */}
                      <div className="space-y-1">
                        <p className="text-xs font-bold uppercase text-slate-500 tracking-wider">⚠ THREAT ASSESSMENT</p>
                        <button 
                          onClick={() => {
                            setMissionBriefOpen(false);
                            setThreatModalOpen(true);
                          }}
                          className="inline-block px-4 py-2 rounded font-bold text-xs uppercase cursor-pointer transition-all hover:scale-105"
                          style={{
                            backgroundColor: threatAssessment.threatLevel === 'red' ? 'rgba(255, 68, 68, 0.2)' : 
                                            threatAssessment.threatLevel === 'yellow' ? 'rgba(245, 158, 11, 0.2)' : 'rgba(16, 185, 129, 0.2)',
                            border: `2px solid ${threatAssessment.threatLevel === 'red' ? '#ff4444' : 
                                                threatAssessment.threatLevel === 'yellow' ? '#f59e0b' : '#10b981'}`,
                            color: threatAssessment.threatLevel === 'red' ? '#ff4444' : 
                                  threatAssessment.threatLevel === 'yellow' ? '#f59e0b' : '#10b981',
                            animation: threatAssessment.threatLevel === 'red' ? 'threatPulse 2s ease-in-out infinite' : 'none',
                          }}
                          data-testid="threat-assessment-btn"
                        >
                          {threatAssessment.threatLevel === 'red' ? 'CRITICAL' : 
                           threatAssessment.threatLevel === 'yellow' ? 'CAUTION' : 'OPTIMAL'} - {threatAssessment.timelineWeeks || 4} Weeks
                          <span className="ml-2 text-xs opacity-70">→</span>
                        </button>
                      </div>
                    {/* Profit Margin */}
                    <div className="space-y-1">
                      <p className="text-xs font-bold uppercase text-slate-500 tracking-wider">PROFIT MARGIN</p>
                      <p className="text-base text-slate-300">
                        {selectedEstimate?.profit_percent || 20}%
                      </p>
                    </div>
                  </div>
                </div>
                )}
              </div>

              {/* Section 3: Operational Documents */}
              <div id="operational-documents-section">
                <h3 className="text-xs font-bold uppercase text-slate-500 tracking-wider mb-3 pb-2 border-b border-slate-800">
                  OPERATIONAL DOCUMENTS
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {/* Main Proposal Card */}
                  <button
                    onClick={() => ensureEstimateAndOpenBuilder(() => {
                      setSelectedChangeOrderForProposal(null);
                      setProposalBuilderOpen(true);
                    })}
                    className="group text-left p-5 rounded-md transition-all duration-200 hover:translate-x-1"
                    style={{
                      backgroundColor: 'rgba(30, 41, 59, 0.5)',
                      border: '1px solid rgba(16, 185, 129, 0.3)',
                      borderLeft: '3px solid #10b981',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(30, 41, 59, 0.7)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(30, 41, 59, 0.5)';
                    }}
                    data-testid="mission-brief-doc-client_proposal"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-sm text-white uppercase">CLIENT PROPOSAL</p>
                        <p className="text-xs text-slate-500 mt-1">Professional proposal for main estimate</p>
                      </div>
                      <span className="text-emerald-400 group-hover:translate-x-1 transition-all">→</span>
                    </div>
                  </button>
                  
                  {/* Change Order Proposal Cards */}
                  {scopes.changeOrders.map((co) => (
                    <button
                      key={`co-proposal-${co.id}`}
                      onClick={() => {
                        setMissionBriefOpen(false);
                        setSelectedChangeOrderForProposal(co);
                        setProposalBuilderOpen(true);
                      }}
                      className="group text-left p-5 rounded-md transition-all duration-200 hover:translate-x-1"
                      style={{
                        backgroundColor: 'rgba(30, 41, 59, 0.5)',
                        border: '1px solid rgba(245, 158, 11, 0.3)',
                        borderLeft: '3px solid #f59e0b',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(30, 41, 59, 0.7)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(30, 41, 59, 0.5)';
                      }}
                      data-testid={`mission-brief-doc-co-${co.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-bold text-sm text-amber-400 uppercase">CO #{co.number} PROPOSAL</p>
                          <p className="text-xs text-slate-500 mt-1">Change Order #{co.number} proposal</p>
                        </div>
                        <span className="text-amber-400 group-hover:translate-x-1 transition-all">→</span>
                      </div>
                    </button>
                  ))}
                  
                  {/* Operator Order Card - Opens Full-Screen Builder */}
                  <button
                    onClick={() => ensureEstimateAndOpenBuilder(() => setOperatorOrderBuilderOpen(true))}
                    className="group text-left p-5 rounded-md transition-all duration-200 hover:translate-x-1"
                    style={{
                      backgroundColor: 'rgba(30, 41, 59, 0.5)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderLeft: '3px solid #3b82f6',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(30, 41, 59, 0.7)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.backgroundColor = 'rgba(30, 41, 59, 0.5)';
                    }}
                    data-testid="mission-brief-doc-work_order"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-sm text-blue-400 uppercase">OPERATOR ORDER</p>
                        <p className="text-xs text-slate-500 mt-1">Mission brief with crew instructions and safety protocols</p>
                      </div>
                      <span className="text-blue-400 group-hover:translate-x-1 transition-all">→</span>
                    </div>
                  </button>
                  
                  {/* SOW - CLIENT - Opens dedicated builder */}
                  <button
                    onClick={() => ensureEstimateAndOpenBuilder(() => setClientSOWBuilderOpen(true))}
                    className="group text-left p-5 rounded-md transition-all duration-200 hover:translate-x-1"
                    style={{
                      backgroundColor: 'rgba(30, 41, 59, 0.5)',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                      borderLeft: '3px solid transparent',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderLeftColor = '#10b981';
                      e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)';
                      e.currentTarget.style.backgroundColor = 'rgba(30, 41, 59, 0.7)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderLeftColor = 'transparent';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.backgroundColor = 'rgba(30, 41, 59, 0.5)';
                    }}
                    data-testid="mission-brief-doc-sow_client"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-sm text-white uppercase">SOW - CLIENT</p>
                        <p className="text-xs text-slate-500 mt-1">Detailed scope of work for client approval</p>
                      </div>
                      <span className="text-slate-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all">→</span>
                    </div>
                  </button>
                  
                  {/* SOW - SUBCONTRACTOR - Opens dedicated builder */}
                  <button
                    onClick={() => ensureEstimateAndOpenBuilder(() => setSubcontractorSOWBuilderOpen(true))}
                    className="group text-left p-5 rounded-md transition-all duration-200 hover:translate-x-1"
                    style={{
                      backgroundColor: 'rgba(30, 41, 59, 0.5)',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                      borderLeft: '3px solid transparent',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderLeftColor = '#f59e0b';
                      e.currentTarget.style.borderColor = 'rgba(245, 158, 11, 0.3)';
                      e.currentTarget.style.backgroundColor = 'rgba(30, 41, 59, 0.7)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderLeftColor = 'transparent';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.backgroundColor = 'rgba(30, 41, 59, 0.5)';
                    }}
                    data-testid="mission-brief-doc-sow_subcontractor"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-sm text-white uppercase">SOW - SUBCONTRACTOR</p>
                        <p className="text-xs text-slate-500 mt-1">Scope specifications for subcontractor engagement</p>
                      </div>
                      <span className="text-slate-500 group-hover:text-amber-400 group-hover:translate-x-1 transition-all">→</span>
                    </div>
                  </button>
                  
                  {/* SOW - MILESTONE - Opens dedicated full-page builder */}
                  <button
                    onClick={() => ensureEstimateAndOpenBuilder(() => setMilestoneSOWBuilderOpen(true))}
                    className="group text-left p-5 rounded-md transition-all duration-200 hover:translate-x-1"
                    style={{
                      backgroundColor: 'rgba(30, 41, 59, 0.5)',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                      borderLeft: '3px solid transparent',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderLeftColor = '#10b981';
                      e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)';
                      e.currentTarget.style.backgroundColor = 'rgba(30, 41, 59, 0.7)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderLeftColor = 'transparent';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.backgroundColor = 'rgba(30, 41, 59, 0.5)';
                    }}
                    data-testid="mission-brief-doc-sow_milestone"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-sm text-white uppercase">MILESTONE OBJECTIVES</p>
                        <p className="text-xs text-slate-500 mt-1">Milestone-based scope and payment schedule</p>
                      </div>
                      <span className="text-slate-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all">→</span>
                    </div>
                  </button>
                  
                  {/* SUPPLY MANIFEST - Opens dedicated full-page builder */}
                  <button
                    onClick={() => ensureEstimateAndOpenBuilder(() => setLogisticsManifestBuilderOpen(true))}
                    className="group text-left p-5 rounded-md transition-all duration-200 hover:translate-x-1"
                    style={{
                      backgroundColor: 'rgba(30, 41, 59, 0.5)',
                      border: '1px solid rgba(255, 255, 255, 0.05)',
                      borderLeft: '3px solid transparent',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderLeftColor = '#10b981';
                      e.currentTarget.style.borderColor = 'rgba(16, 185, 129, 0.3)';
                      e.currentTarget.style.backgroundColor = 'rgba(30, 41, 59, 0.7)';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderLeftColor = 'transparent';
                      e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.05)';
                      e.currentTarget.style.backgroundColor = 'rgba(30, 41, 59, 0.5)';
                    }}
                    data-testid="mission-brief-doc-material_list"
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-bold text-sm text-white uppercase">SUPPLY MANIFEST</p>
                        <p className="text-xs text-slate-500 mt-1">Complete material procurement and delivery logistics</p>
                      </div>
                      <span className="text-slate-500 group-hover:text-emerald-400 group-hover:translate-x-1 transition-all">→</span>
                    </div>
                  </button>
                  
                  {/* VIEW ALL CHATGPT DOCUMENTS - Opens unified viewer */}
                  <button
                    onClick={() => ensureEstimateAndOpenBuilder(() => {
                      setMissionBriefOpen(false);
                      setMissionBriefViewerOpen(true);
                    })}
                    className="group text-left p-5 rounded-md transition-all duration-200 hover:translate-x-1 col-span-full"
                    style={{
                      background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.15), rgba(59, 130, 246, 0.15))',
                      border: '1px solid rgba(16, 185, 129, 0.4)',
                    }}
                    data-testid="mission-brief-view-all"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Sparkles className="h-5 w-5 text-emerald-400" />
                        <div>
                          <p className="font-bold text-sm text-emerald-400 uppercase">VIEW ALL CHATGPT DOCUMENTS</p>
                          <p className="text-xs text-slate-400 mt-1">View all 6 AI-generated Mission Brief documents with accurate timelines</p>
                        </div>
                      </div>
                      <span className="text-emerald-400 group-hover:translate-x-1 transition-all">→</span>
                    </div>
                  </button>
                </div>
              </div>

              {/* Section 4: AI Tactical Documentation */}
              <div>
                <h3 className="text-xs font-bold uppercase text-slate-500 tracking-wider mb-3 pb-2 border-b border-slate-800">
                  AI TACTICAL DOCUMENTATION
                </h3>
                <div 
                  className="p-5 rounded-lg"
                  style={{
                    background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.08), rgba(16, 185, 129, 0.02))',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                  }}
                >
                  <h4 className="font-rajdhani font-bold text-base text-white uppercase mb-2">
                    AI Documentation System
                  </h4>
                  <p className="text-xs text-slate-400 leading-relaxed mb-4">
                    Generate mission-critical documents, operational scopes, change orders, and tactical specifications using AI intelligence. Precision-formatted for construction operations.
                  </p>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={() => {
                        setMissionBriefOpen(false);
                        handleGenerateAISow();
                      }}
                      className="px-5 py-2.5 rounded text-xs font-bold uppercase transition-all hover:shadow-lg"
                      style={{
                        backgroundColor: 'rgba(16, 185, 129, 0.15)',
                        border: '1px solid rgba(16, 185, 129, 0.4)',
                        color: '#10b981',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.25)';
                        e.currentTarget.style.boxShadow = '0 0 20px rgba(16, 185, 129, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.15)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      data-testid="ai-generate-scope"
                    >
                      Generate Custom Scope
                    </button>
                    <button
                      onClick={() => {
                        setMissionBriefOpen(false);
                        toast.info("Custom document generation coming soon");
                      }}
                      className="px-5 py-2.5 rounded text-xs font-bold uppercase transition-all hover:shadow-lg"
                      style={{
                        backgroundColor: 'rgba(16, 185, 129, 0.15)',
                        border: '1px solid rgba(16, 185, 129, 0.4)',
                        color: '#10b981',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.25)';
                        e.currentTarget.style.boxShadow = '0 0 20px rgba(16, 185, 129, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.15)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      data-testid="ai-create-document"
                    >
                      Create Custom Document
                    </button>
                    <button
                      onClick={() => {
                        setMissionBriefOpen(false);
                        createChangeOrder();
                      }}
                      className="px-5 py-2.5 rounded text-xs font-bold uppercase transition-all hover:shadow-lg"
                      style={{
                        backgroundColor: 'rgba(16, 185, 129, 0.15)',
                        border: '1px solid rgba(16, 185, 129, 0.4)',
                        color: '#10b981',
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.25)';
                        e.currentTarget.style.boxShadow = '0 0 20px rgba(16, 185, 129, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = 'rgba(16, 185, 129, 0.15)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                      data-testid="ai-create-change-order"
                    >
                      Create Change Order
                    </button>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      )}

      {/* Threat Pulse Animation */}
      <style>{`
        @keyframes threatPulse {
          0%, 100% { box-shadow: 0 0 20px rgba(255, 68, 68, 0.3); }
          50% { box-shadow: 0 0 60px rgba(255, 68, 68, 0.6); }
        }
        @keyframes missionBriefFadeIn {
          from { opacity: 0; transform: scale(0.95) translateY(20px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>

      {/* === INVOICE SCOPE SELECTION MODAL === */}
      {invoiceModalOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center" style={{ zIndex: 2147483645 }}>
          <div className="bg-slate-900 border border-slate-700 rounded-lg shadow-xl w-full max-w-md mx-4">
            {/* Modal Header */}
            <div className="px-5 py-4 border-b border-slate-800">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Receipt className="h-5 w-5 text-emerald-400" />
                  <h3 className="text-base font-bold text-white">Generate Invoice</h3>
                </div>
                <button
                  onClick={() => setInvoiceModalOpen(false)}
                  className="p-1 text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              <p className="text-xs text-slate-400 mt-1">Select which scopes to include in the invoice</p>
            </div>
            
            {/* Scope Selection */}
            <div className="px-5 py-4 space-y-3">
              {/* Base Contract Option */}
              <label className="flex items-center gap-3 p-3 rounded-lg border border-slate-700 hover:border-slate-600 cursor-pointer transition-colors">
                <input
                  type="checkbox"
                  checked={invoiceScopes.base}
                  onChange={(e) => setInvoiceScopes(prev => ({ ...prev, base: e.target.checked }))}
                  className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-emerald-500 focus:ring-emerald-500"
                />
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <Lock className="h-3.5 w-3.5 text-slate-400" />
                    <span className="text-sm font-semibold text-slate-200">Base Contract</span>
                  </div>
                  <span className="text-xs text-slate-500">${allTotals.base.clientTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                </div>
              </label>
              
              {/* Change Orders */}
              {scopes.changeOrders.length > 0 && (
                <>
                  <div className="text-xs text-slate-500 uppercase tracking-wider px-1">Change Orders</div>
                  {scopes.changeOrders.map((co) => {
                    const coTotals = computeScopeTotals(co.groups);
                    const isSelected = invoiceScopes.changeOrders.includes(co.id);
                    return (
                      <label 
                        key={co.id}
                        className={`flex items-center gap-3 p-3 rounded-lg border cursor-pointer transition-colors ${
                          isSelected 
                            ? "border-amber-500/50 bg-amber-500/10" 
                            : "border-slate-700 hover:border-slate-600"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={(e) => {
                            setInvoiceScopes(prev => ({
                              ...prev,
                              changeOrders: e.target.checked
                                ? [...prev.changeOrders, co.id]
                                : prev.changeOrders.filter(id => id !== co.id)
                            }));
                          }}
                          className="w-4 h-4 rounded border-slate-600 bg-slate-800 text-amber-500 focus:ring-amber-500"
                        />
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <FileText className="h-3.5 w-3.5 text-amber-400" />
                            <span className="text-sm font-semibold text-amber-300">{co.name}</span>
                          </div>
                          <span className="text-xs text-slate-500">${coTotals.clientTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                        </div>
                      </label>
                    );
                  })}
                </>
              )}
              
              {scopes.changeOrders.length === 0 && (
                <div className="text-center py-4 text-slate-500 text-xs">
                  No Change Orders created yet
                </div>
              )}
            </div>
            
            {/* Selected Total */}
            <div className="px-5 py-3 bg-slate-800/50 border-t border-slate-800">
              <div className="flex items-center justify-between">
                <span className="text-xs text-slate-400">Invoice Total:</span>
                <span className="text-lg font-bold text-emerald-400 font-mono">
                  ${(() => {
                    let total = invoiceScopes.base ? allTotals.base.clientTotal : 0;
                    invoiceScopes.changeOrders.forEach(coId => {
                      const co = scopes.changeOrders.find(c => c.id === coId);
                      if (co) {
                        total += computeScopeTotals(co.groups).clientTotal;
                      }
                    });
                    return total.toLocaleString("en-US", { minimumFractionDigits: 2 });
                  })()}
                </span>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="px-5 py-4 border-t border-slate-800 flex items-center justify-end gap-3">
              <button
                onClick={() => setInvoiceModalOpen(false)}
                className="px-4 py-2 text-xs text-slate-400 hover:text-white hover:bg-slate-800 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleGenerateInvoice}
                disabled={!invoiceScopes.base && invoiceScopes.changeOrders.length === 0}
                className="px-4 py-2 text-xs font-bold bg-emerald-500 text-slate-900 hover:bg-emerald-400 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                <Download className="h-3.5 w-3.5" />
                Generate Invoice
              </button>
            </div>
          </div>
        </div>
      )}

      {/* === AI SOW DOCUMENT PREVIEW MODAL === */}
      {showSowModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center" style={{ zIndex: 2147483645 }}>
          <div className="bg-slate-900 border border-slate-700 rounded-lg shadow-xl w-full max-w-3xl mx-4 max-h-[80vh] flex flex-col">
            {/* Modal Header */}
            <div className="px-5 py-4 border-b border-slate-800 flex-shrink-0">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Brain className="h-5 w-5 text-purple-400" />
                  <h3 className="text-base font-bold text-white">AI Statement of Work</h3>
                </div>
                <button
                  onClick={() => setShowSowModal(false)}
                  className="p-1 text-slate-400 hover:text-white hover:bg-slate-800 rounded transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
              
              {/* Tab Buttons */}
              <div className="flex gap-2 mt-4">
                <button
                  onClick={() => setSowActiveTab("client")}
                  className={`px-4 py-2 text-xs font-semibold rounded-lg transition-colors ${
                    sowActiveTab === "client"
                      ? "bg-emerald-500/20 text-emerald-300 border border-emerald-500/50"
                      : "bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-600"
                  }`}
                >
                  CLIENT BRIEF
                </button>
                <button
                  onClick={() => setSowActiveTab("operator")}
                  className={`px-4 py-2 text-xs font-semibold rounded-lg transition-colors ${
                    sowActiveTab === "operator"
                      ? "bg-purple-500/20 text-purple-300 border border-purple-500/50"
                      : "bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-600"
                  }`}
                >
                  OPERATOR ORDER
                </button>
                <button
                  onClick={() => {
                    setSowActiveTab("schedule");
                    if (!projectSchedule) handleGenerateSchedule();
                  }}
                  className={`px-4 py-2 text-xs font-semibold rounded-lg transition-colors flex items-center gap-1.5 ${
                    sowActiveTab === "schedule"
                      ? "bg-amber-500/20 text-amber-300 border border-amber-500/50"
                      : "bg-slate-800 text-slate-400 border border-slate-700 hover:border-slate-600"
                  }`}
                >
                  <Calendar className="h-3.5 w-3.5" />
                  PROJECT SCHEDULE
                </button>
              </div>
            </div>
            
            {/* Content Area */}
            <div className="flex-1 overflow-y-auto p-5">
              {(generatingSow || generatingSchedule) ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-8 w-8 animate-spin text-purple-400" />
                  <span className="ml-3 text-slate-400">
                    {generatingSchedule ? "Generating schedule..." : "Generating documents..."}
                  </span>
                </div>
              ) : sowActiveTab === "schedule" ? (
                /* Phased Timeline View */
                <div className="space-y-4">
                  {/* Schedule Header */}
                  {projectSchedule && (
                    <div className="flex items-center justify-between p-4 bg-slate-800/50 rounded-lg border border-slate-700">
                      <div className="flex items-center gap-4">
                        <div className="flex items-center gap-2 text-emerald-400">
                          <Clock className="h-5 w-5" />
                          <span className="font-bold">{projectSchedule.total_days} Days</span>
                        </div>
                        <div className="flex items-center gap-2 text-amber-400">
                          <CheckCircle2 className="h-5 w-5" />
                          <span className="font-bold">{projectSchedule.total_phases} Phases</span>
                        </div>
                        {projectSchedule.timeline?.recommended_duration && (
                          <div className="flex items-center gap-2 text-blue-400">
                            <Calendar className="h-5 w-5" />
                            <span className="font-bold">{projectSchedule.timeline.recommended_duration}</span>
                          </div>
                        )}
                        {projectSchedule.generated_by && (
                          <span className="px-2 py-0.5 text-xs bg-emerald-500/20 text-emerald-400 rounded-full">
                            {projectSchedule.generated_by}
                          </span>
                        )}
                      </div>
                      <button
                        onClick={handleGenerateSchedule}
                        className="px-3 py-1.5 text-xs font-semibold bg-slate-700 text-slate-300 hover:bg-slate-600 rounded-lg transition-colors flex items-center gap-1.5"
                      >
                        <RefreshCw className="h-3 w-3" />
                        Regenerate
                      </button>
                    </div>
                  )}
                  
                  {/* Phased Timeline */}
                  {projectSchedule?.phases?.map((phase, idx) => {
                    const colorClasses = {
                      red: "border-red-500 bg-red-500/10",
                      amber: "border-amber-500 bg-amber-500/10",
                      blue: "border-blue-500 bg-blue-500/10",
                      emerald: "border-emerald-500 bg-emerald-500/10",
                    };
                    const iconColors = {
                      red: "text-red-400 bg-red-500/20",
                      amber: "text-amber-400 bg-amber-500/20",
                      blue: "text-blue-400 bg-blue-500/20",
                      emerald: "text-emerald-400 bg-emerald-500/20",
                    };
                    const phaseColor = colorClasses[phase.color] || colorClasses.blue;
                    const iconColor = iconColors[phase.color] || iconColors.blue;
                    
                    return (
                      <div key={idx} className={`relative border-l-4 rounded-lg p-4 ${phaseColor}`}>
                        {/* Phase connector line */}
                        {idx < projectSchedule.phases.length - 1 && (
                          <div className="absolute left-[-2px] bottom-0 w-[4px] h-4 bg-slate-700 translate-y-full" />
                        )}
                        
                        {/* Phase Header */}
                        <div className="flex items-start justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center font-bold text-lg ${iconColor}`}>
                              {phase.phase_number}
                            </div>
                            <div>
                              <h3 className="font-bold text-white tracking-wide">{phase.phase_name}</h3>
                              <div className="flex items-center gap-3 text-xs text-slate-400 mt-0.5">
                                <span className="flex items-center gap-1">
                                  <Clock className="h-3 w-3" />
                                  {phase.day_range || `${phase.days} Day${phase.days > 1 ? "s" : ""}`}
                                </span>
                                {phase.week_range && (
                                  <span className="text-blue-400">({phase.week_range})</span>
                                )}
                                <span className="flex items-center gap-1">
                                  <User className="h-3 w-3" />
                                  {phase.assigned_to}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Tasks */}
                        <div className="ml-13 space-y-1.5 mb-3">
                          {phase.tasks.map((task, tIdx) => (
                            <div key={tIdx} className="flex items-center gap-2 text-sm text-slate-300">
                              <div className="w-1.5 h-1.5 rounded-full bg-slate-500" />
                              {task}
                            </div>
                          ))}
                        </div>
                        
                        {/* Milestone */}
                        <div className="ml-13 flex items-center gap-2 px-3 py-2 bg-slate-900/50 rounded-lg border border-slate-700/50">
                          <CheckCircle2 className="h-4 w-4 text-emerald-400 flex-shrink-0" />
                          <span className="text-xs font-semibold text-emerald-300">MILESTONE:</span>
                          <span className="text-xs text-slate-300">{phase.milestone}</span>
                        </div>
                      </div>
                    );
                  })}
                  
                  {!projectSchedule && (
                    <div className="text-center py-12 text-slate-500">
                      <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
                      <p>No schedule generated yet.</p>
                      <button
                        onClick={handleGenerateSchedule}
                        className="mt-4 px-4 py-2 bg-amber-500 text-white font-bold rounded-lg hover:bg-amber-400 transition-colors"
                      >
                        Generate Schedule
                      </button>
                    </div>
                  )}
                  
                  {/* Edit Timeline Placeholder */}
                  <div className="mt-6 p-4 bg-slate-800/30 rounded-lg border border-dashed border-slate-700 text-center">
                    <p className="text-xs text-slate-500">
                      💡 Future Enhancement: Drag & drop phases to reorder timeline
                    </p>
                  </div>
                </div>
              ) : (
                <div className="bg-slate-800/50 rounded-lg p-4 border border-slate-700">
                  <pre className="text-sm text-slate-200 whitespace-pre-wrap font-sans leading-relaxed">
                    {sowActiveTab === "client" ? sowContent.client : sowContent.operator}
                  </pre>
                </div>
              )}
            </div>
            
            {/* Actions */}
            <div className="px-5 py-4 border-t border-slate-800 flex justify-end gap-3 flex-shrink-0">
              {sowActiveTab !== "schedule" && (
                <>
                  <button
                    onClick={() => {
                      const text = sowActiveTab === "client" ? sowContent.client : sowContent.operator;
                      navigator.clipboard.writeText(text);
                      toast.success("Copied to clipboard!");
                    }}
                    disabled={generatingSow}
                    className="px-4 py-2 text-xs font-semibold bg-slate-800 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Copy className="h-3.5 w-3.5" />
                    Copy to Clipboard
                  </button>
                  <button
                    onClick={() => {
                      const text = sowActiveTab === "client" ? sowContent.client : sowContent.operator;
                      const filename = sowActiveTab === "client" ? "client_brief.txt" : "operator_order.txt";
                      const blob = new Blob([text], { type: "text/plain" });
                      const url = URL.createObjectURL(blob);
                      const link = document.createElement("a");
                      link.href = url;
                      link.download = filename;
                      document.body.appendChild(link);
                      link.click();
                      document.body.removeChild(link);
                      URL.revokeObjectURL(url);
                      toast.success("Document downloaded!");
                    }}
                    disabled={generatingSow}
                    className="px-4 py-2 text-xs font-bold bg-purple-500 text-white hover:bg-purple-400 rounded-lg transition-colors flex items-center gap-2"
                  >
                    <Download className="h-3.5 w-3.5" />
                    Download
                  </button>
                </>
              )}
              {sowActiveTab === "schedule" && projectSchedule && (
                <button
                  onClick={() => {
                    const scheduleText = projectSchedule.phases.map(p => 
                      `PHASE ${p.phase_number}: ${p.phase_name}\n` +
                      `Duration: ${p.days} day(s)\n` +
                      `Assigned: ${p.assigned_to}\n` +
                      `Tasks:\n${p.tasks.map(t => `  • ${t}`).join('\n')}\n` +
                      `Milestone: ${p.milestone}\n`
                    ).join('\n---\n\n');
                    
                    const blob = new Blob([scheduleText], { type: "text/plain" });
                    const url = URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = url;
                    link.download = "project_schedule.txt";
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(url);
                    toast.success("Schedule downloaded!");
                  }}
                  className="px-4 py-2 text-xs font-bold bg-amber-500 text-white hover:bg-amber-400 rounded-lg transition-colors flex items-center gap-2"
                >
                  <Download className="h-3.5 w-3.5" />
                  Download Schedule
                </button>
              )}
            </div>
          </div>
        </div>
      )}

 

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* SITE SUPERINTENDENT - Floating Chat Widget (Overlay) */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      <AiCopilotModal
        isOpen={superintendentOpen}
        onClose={() => setSuperintendentOpen(false)}
        onMinimize={() => setSuperintendentMinimized(!superintendentMinimized)}
        isMinimized={superintendentMinimized}
        chatHistory={superintendentChat}
        setChatHistory={setSuperintendentChat}
        onSendMessage={handleSuperintendentMessage}
        onFileUpload={handleFileUpload}
        isLoading={superintendentLoading}
        clientName={selectedLead?.contact_name || selectedLead?.name}
        estimateContext={{
          groups: getActiveGroups(),
          totalCost: costBreakdown.totalCost,
        }}
      />

      {/* ═══════════════════════════════════════════════════════════════════ */}
      {/* THREAT ASSESSMENT HUD - Draggable Intel Briefing */}
      {/* ═══════════════════════════════════════════════════════════════════ */}
      {threatModalOpen && (() => {
        // Calculate "Path to Green" - what's needed to hit targets
        const { targetGPPerMonth, minimumGPPerMonth } = riskSettings;
        const currentGP = threatAssessment.gpVelocity;
        const totalMarkup = threatAssessment.totalMarkup || 0;
        const durationMonths = estDurationWeeks / 4.33;
        
        // Calculate required changes to hit GREEN (target GP)
        const gpGap = targetGPPerMonth - currentGP;
        const gpGapToYellow = minimumGPPerMonth - currentGP;
        
        // Option 1: Reduce schedule to hit target GP with current markup
        const weeksNeededForGreen = totalMarkup > 0 ? (totalMarkup / targetGPPerMonth) * 4.33 : estDurationWeeks;
        const weeksNeededForYellow = totalMarkup > 0 ? (totalMarkup / minimumGPPerMonth) * 4.33 : estDurationWeeks;
        
        // Option 2: Increase margin to hit target GP with current schedule
        const additionalProfitNeeded = gpGap * durationMonths;
        const additionalProfitToYellow = gpGapToYellow * durationMonths;
        
        // Option 3: Calculate required markup percentage increase
        const currentClientTotal = currentScopeTotals.clientTotal || 0;
        const currentBuilderTotal = currentScopeTotals.builderTotal || 0;
        const currentMarginPercent = currentClientTotal > 0 ? ((currentClientTotal - currentBuilderTotal) / currentClientTotal) * 100 : 0;
        const targetMarginPercent = 25; // Safe threshold
        
        // Simulated values based on current duration adjustment
        const simulatedGP = totalMarkup / durationMonths;
        const simulatedThreatLevel = simulatedGP >= targetGPPerMonth ? "green" : simulatedGP >= minimumGPPerMonth ? "yellow" : "red";
        
        return (
          <div 
            ref={threatModalRef}
            className={`fixed w-[440px] bg-slate-950 border-2 rounded-lg shadow-2xl overflow-hidden select-none ${
              isDraggingThreatModal ? 'cursor-grabbing' : ''
            } ${
              simulatedThreatLevel === "red" ? "border-red-500/70" :
              simulatedThreatLevel === "yellow" ? "border-amber-500/70" : "border-emerald-500/70"
            }`}
            style={{
              zIndex: 2147483646, // Just below Site Superintendent modal
              left: threatModalPosition.x,
              top: threatModalPosition.y,
              boxShadow: simulatedThreatLevel === "red" 
                ? '0 0 30px rgba(239, 68, 68, 0.3)' 
                : simulatedThreatLevel === "yellow"
                  ? '0 0 30px rgba(245, 158, 11, 0.3)'
                  : '0 0 30px rgba(16, 185, 129, 0.3)',
            }}
            onMouseDown={handleThreatModalMouseDown}
          >
            {/* Header - Drag Handle */}
            <div className={`drag-handle-threat px-4 py-3 border-b-2 cursor-grab active:cursor-grabbing ${
              simulatedThreatLevel === "red" 
                ? "bg-gradient-to-r from-red-900/90 to-red-950 border-red-500/50" 
                : simulatedThreatLevel === "yellow"
                  ? "bg-gradient-to-r from-amber-900/90 to-amber-950 border-amber-500/50"
                  : "bg-gradient-to-r from-emerald-900/90 to-emerald-950 border-emerald-500/50"
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  {simulatedThreatLevel === "red" ? (
                    <ShieldAlert className="h-7 w-7 text-red-400 animate-pulse" />
                  ) : simulatedThreatLevel === "yellow" ? (
                    <Shield className="h-7 w-7 text-amber-400" />
                  ) : (
                    <ShieldCheck className="h-7 w-7 text-emerald-400" />
                  )}
                  <div>
                    <h2 
                      className={`text-base font-black uppercase tracking-wider ${
                        simulatedThreatLevel === "red" ? "text-red-400" :
                        simulatedThreatLevel === "yellow" ? "text-amber-400" : "text-emerald-400"
                      }`}
                      style={{ fontFamily: "'Courier New', monospace" }}
                    >
                      {simulatedThreatLevel === "green" ? "MISSION GO" : 
                       simulatedThreatLevel === "yellow" ? "RISK DETECTED" : "CRITICAL THREAT"}
                    </h2>
                    <p className="text-xs text-slate-400" style={{ fontFamily: "'Courier New', monospace" }}>
                      DRAG TO MOVE • LIVE SIMULATION
                    </p>
                  </div>
                </div>
                <button 
                  onClick={() => setThreatModalOpen(false)}
                  className="p-1.5 hover:bg-slate-700/50 rounded transition-colors"
                >
                  <X className="h-4 w-4 text-slate-400" />
                </button>
              </div>
            </div>
            
            {/* Body - Intel Sectors */}
            <div className="p-4 space-y-4 max-h-[70vh] overflow-y-auto" style={{ fontFamily: "'Courier New', monospace" }}>
              
              {/* LIVE STATS - Real-time Display */}
              <div className={`border rounded-lg p-3 ${
                simulatedThreatLevel === "red" ? "border-red-500/30 bg-red-950/20" :
                simulatedThreatLevel === "yellow" ? "border-amber-500/30 bg-amber-950/20" :
                "border-emerald-500/30 bg-emerald-950/20"
              }`}>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div>
                    <p className="text-xs text-slate-500 uppercase">GP Velocity</p>
                    <p className={`text-lg font-black ${
                      simulatedGP >= targetGPPerMonth ? 'text-emerald-400' :
                      simulatedGP >= minimumGPPerMonth ? 'text-amber-400' : 'text-red-400'
                    }`}>
                      ${simulatedGP.toFixed(0)}
                    </p>
                    <p className="text-xs text-slate-500">/month</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Duration</p>
                    <p className="text-lg font-black text-white">{estDurationWeeks}</p>
                    <p className="text-xs text-slate-500">weeks</p>
                  </div>
                  <div>
                    <p className="text-xs text-slate-500 uppercase">Margin</p>
                    <p className={`text-lg font-black ${currentMarginPercent >= 25 ? 'text-emerald-400' : 'text-amber-400'}`}>
                      {currentMarginPercent.toFixed(1)}%
                    </p>
                    <p className="text-xs text-slate-500">profit</p>
                  </div>
                </div>
              </div>

              {/* DURATION SIMULATOR - Interactive */}
              <div className="border border-slate-700 rounded-lg p-3 bg-slate-900/50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs text-slate-400 uppercase tracking-wider">⏱ Adjust Duration (Live)</span>
                  <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                    simulatedThreatLevel === "green" ? "bg-emerald-500/20 text-emerald-400" :
                    simulatedThreatLevel === "yellow" ? "bg-amber-500/20 text-amber-400" :
                    "bg-red-500/20 text-red-400"
                  }`}>
                    {simulatedThreatLevel.toUpperCase()}
                  </span>
                </div>
                <div className="flex items-center justify-center gap-3">
                  <button 
                    className="w-10 h-10 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-bold text-xl transition-colors"
                    onClick={() => setEstDurationWeeks(Math.max(1, estDurationWeeks - 1))}
                  >−</button>
                  <div className="w-24 text-center">
                    <span className="text-2xl font-black text-white">{estDurationWeeks}</span>
                    <span className="text-sm text-slate-400 ml-1">weeks</span>
                  </div>
                  <button 
                    className="w-10 h-10 rounded-lg bg-slate-700 hover:bg-slate-600 text-white font-bold text-xl transition-colors"
                    onClick={() => setEstDurationWeeks(Math.min(52, estDurationWeeks + 1))}
                  >+</button>
                </div>
                {/* Progress bar to target */}
                <div className="mt-3">
                  <div className="flex justify-between text-xs text-slate-500 mb-1">
                    <span>$0</span>
                    <span className="text-amber-400">${minimumGPPerMonth.toLocaleString()} MIN</span>
                    <span className="text-emerald-400">${targetGPPerMonth.toLocaleString()} TARGET</span>
                  </div>
                  <div className="h-2 bg-slate-800 rounded-full overflow-hidden">
                    <div 
                      className={`h-full transition-all duration-300 ${
                        simulatedGP >= targetGPPerMonth ? 'bg-emerald-500' :
                        simulatedGP >= minimumGPPerMonth ? 'bg-amber-500' : 'bg-red-500'
                      }`}
                      style={{ width: `${Math.min(100, (simulatedGP / targetGPPerMonth) * 100)}%` }}
                    />
                  </div>
                </div>
              </div>
              
              {/* PATH TO GREEN - The Fix */}
              {simulatedThreatLevel !== "green" && (
                <div className="border-2 border-emerald-500/50 rounded-lg p-3 bg-emerald-950/30">
                  <h3 className="text-xs font-bold text-emerald-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <Zap className="h-4 w-4" />
                    PATH TO GREEN - MISSION CORRECTION
                  </h3>
                  <div className="space-y-3 text-sm">
                    {/* Option 1: Reduce Schedule */}
                    <div className="flex items-start gap-2 p-2 bg-slate-900/50 rounded border border-slate-700">
                      <span className="text-emerald-500 font-bold mt-0.5">A</span>
                      <div>
                        <p className="text-slate-300">
                          <strong className="text-emerald-400">REDUCE SCHEDULE:</strong>
                        </p>
                        <p className="text-white font-bold">
                          Set to {Math.ceil(weeksNeededForGreen)} weeks 
                          <span className="text-emerald-400 ml-1">
                            (−{estDurationWeeks - Math.ceil(weeksNeededForGreen)} weeks)
                          </span>
                        </p>
                      </div>
                    </div>
                    
                    {/* Option 2: Increase Margin */}
                    <div className="flex items-start gap-2 p-2 bg-slate-900/50 rounded border border-slate-700">
                      <span className="text-emerald-500 font-bold mt-0.5">B</span>
                      <div>
                        <p className="text-slate-300">
                          <strong className="text-emerald-400">INCREASE PROFIT:</strong>
                        </p>
                        <p className="text-white font-bold">
                          Add ${additionalProfitNeeded.toLocaleString("en-US", { maximumFractionDigits: 0 })} to scope
                          <span className="text-emerald-400 ml-1">
                            (+{((additionalProfitNeeded / currentClientTotal) * 100).toFixed(1)}% markup)
                          </span>
                        </p>
                      </div>
                    </div>
                    
                    {/* Quick Fix Summary */}
                    <div className="mt-2 p-2 bg-emerald-500/10 border border-emerald-500/30 rounded text-center">
                      <p className="text-xs text-emerald-300">
                        <strong>REQUIRED:</strong> +${gpGap.toLocaleString("en-US", { maximumFractionDigits: 0 })}/mo GP to reach TARGET
                      </p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* GREEN STATUS - Success */}
              {simulatedThreatLevel === "green" && (
                <div className="border-2 border-emerald-500/50 rounded-lg p-4 bg-emerald-950/30 text-center">
                  <ShieldCheck className="h-10 w-10 text-emerald-400 mx-auto mb-2" />
                  <h3 className="text-lg font-bold text-emerald-400 uppercase">MISSION GO</h3>
                  <p className="text-sm text-slate-300 mt-1">
                    Project meets profit targets. GP Velocity at ${simulatedGP.toFixed(0)}/mo exceeds minimum threshold.
                  </p>
                </div>
              )}
              
              {/* Scope Reference */}
              <div className="border border-slate-700 rounded-lg p-3 bg-slate-900/30">
                <div className="flex justify-between text-xs">
                  <span className="text-slate-500">Scope Total:</span>
                  <span className="text-emerald-400 font-bold">
                    {`$${currentScopeTotals.clientTotal.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  </span>
                </div>
                <div className="flex justify-between text-xs mt-1">
                  <span className="text-slate-500">Total Profit:</span>
                  <span className="text-white font-bold">
                    {`$${totalMarkup.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  </span>
                </div>
              </div>
            </div>
          </div>
        );
      })()}
      
      {/* ═══════════════════════════════════════════════════════════════════════════
          PROPOSAL BUILDER MODAL
          ═══════════════════════════════════════════════════════════════════════════ */}
      {proposalBuilderOpen && (
        <div 
          className="fixed inset-0 overflow-y-auto"
          style={{ backgroundColor: "#020617", zIndex: 2147483647 }}
        >
          <ProposalBuilder
            key={`proposal-${selectedEstimate?.estimate_id || conversationalEstimateId}-${selectedChangeOrderForProposal?.id || 'main'}`}
            estimateId={selectedEstimate?.estimate_id || conversationalEstimateId}
            estimateData={{
              groups: getActiveGroups() || [],
              allScopes: scopes,
              changeOrders: scopes.changeOrders || [],
              totalCost: costBreakdown.totalCost
            }}
            clientInfo={{
              name: selectedLead?.contact_name || selectedLead?.name || "",
              email: selectedLead?.email || "",
              phone: selectedLead?.phone || "",
              address: selectedLead?.address || selectedLead?.project_address || "",
              project_name: selectedEstimate?.job_name || selectedLead?.project_name || "Project Proposal"
            }}
            companySettings={companySettings}
            changeOrderId={selectedChangeOrderForProposal?.id || null}
            changeOrderData={selectedChangeOrderForProposal || null}
            onClose={() => {
              setProposalBuilderOpen(false);
              setSelectedChangeOrderForProposal(null);
              setMissionBriefOpen(true);
            }}
            onPreviewSend={(proposal) => {
              // Open the existing document preview modal for sending
              setProposalBuilderOpen(false);
              setSelectedChangeOrderForProposal(null);
              setDocPreviewType('client_proposal');
              setDocPreviewOpen(true);
            }}
          />
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════
          OPERATOR ORDER BUILDER MODAL
          ═══════════════════════════════════════════════════════════════════════════ */}
      {operatorOrderBuilderOpen && (
        <div 
          className="fixed inset-0 overflow-y-auto"
          style={{ backgroundColor: "#020617", zIndex: 2147483647 }}
        >
          <OperatorOrderBuilder
            key={`operator-${selectedEstimate?.estimate_id || conversationalEstimateId}`}
            estimateId={selectedEstimate?.estimate_id || conversationalEstimateId}
            estimateData={{
              groups: getActiveGroups() || [],
              allScopes: scopes,
              job_name: selectedEstimate?.job_name
            }}
            clientInfo={{
              name: selectedLead?.contact_name || selectedLead?.name || "",
              email: selectedLead?.email || "",
              phone: selectedLead?.phone || "",
              address: selectedLead?.address || selectedLead?.project_address || "",
              project_name: selectedEstimate?.job_name || selectedLead?.project_name || "Field Operation"
            }}
            companySettings={companySettings}
            onClose={() => {
              setOperatorOrderBuilderOpen(false);
              setMissionBriefOpen(true);
            }}
          />
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════
          CLIENT SOW BUILDER MODAL
          ═══════════════════════════════════════════════════════════════════════════ */}
      {clientSOWBuilderOpen && (
        <div 
          className="fixed inset-0 overflow-y-auto"
          style={{ backgroundColor: "#020617", zIndex: 2147483647 }}
        >
          <ClientSOWBuilder
            key={`clientsow-${selectedEstimate?.estimate_id || conversationalEstimateId}`}
            estimateId={selectedEstimate?.estimate_id || conversationalEstimateId}
            estimateData={{
              groups: getActiveGroups() || [],
              allScopes: scopes,
              job_name: selectedEstimate?.job_name
            }}
            clientInfo={{
              name: selectedLead?.contact_name || selectedLead?.name || "",
              email: selectedLead?.email || "",
              phone: selectedLead?.phone || "",
              address: selectedLead?.address || selectedLead?.project_address || "",
              project_name: selectedEstimate?.job_name || selectedLead?.project_name || "Project"
            }}
            companySettings={companySettings}
            onClose={() => {
              setClientSOWBuilderOpen(false);
              setMissionBriefOpen(true);
            }}
          />
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════
          SUBCONTRACTOR SOW BUILDER MODAL
          ═══════════════════════════════════════════════════════════════════════════ */}
      {subcontractorSOWBuilderOpen && (
        <div 
          className="fixed inset-0 overflow-y-auto"
          style={{ backgroundColor: "#020617", zIndex: 2147483647 }}
        >
          <SubcontractorSOWBuilder
            key={`subsow-${selectedEstimate?.estimate_id || conversationalEstimateId}`}
            estimateId={selectedEstimate?.estimate_id || conversationalEstimateId}
            estimateData={{
              groups: getActiveGroups() || [],
              allScopes: scopes,
              job_name: selectedEstimate?.job_name
            }}
            clientInfo={{
              name: selectedLead?.contact_name || selectedLead?.name || "",
              email: selectedLead?.email || "",
              phone: selectedLead?.phone || "",
              address: selectedLead?.address || selectedLead?.project_address || "",
              project_name: selectedEstimate?.job_name || selectedLead?.project_name || "Project"
            }}
            companySettings={companySettings}
            onClose={() => {
              setSubcontractorSOWBuilderOpen(false);
              setMissionBriefOpen(true);
            }}
          />
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════
          MILESTONE SOW BUILDER MODAL
          ═══════════════════════════════════════════════════════════════════════════ */}
      {milestoneSOWBuilderOpen && (
        <div 
          className="fixed inset-0 overflow-y-auto"
          style={{ backgroundColor: "#020617", zIndex: 2147483647 }}
        >
          <MilestoneSOWBuilder
            key={`milestone-${selectedEstimate?.estimate_id || conversationalEstimateId}`}
            estimateId={selectedEstimate?.estimate_id || conversationalEstimateId}
            estimateData={{
              groups: getActiveGroups() || [],
              allScopes: scopes,
              job_name: selectedEstimate?.job_name
            }}
            clientInfo={{
              name: selectedLead?.contact_name || selectedLead?.name || "",
              email: selectedLead?.email || "",
              phone: selectedLead?.phone || "",
              address: selectedLead?.address || selectedLead?.project_address || "",
              project_name: selectedEstimate?.job_name || selectedLead?.project_name || "Project"
            }}
            companySettings={companySettings}
            onClose={() => {
              setMilestoneSOWBuilderOpen(false);
              setMissionBriefOpen(true);
            }}
          />
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════
          LOGISTICS MANIFEST BUILDER MODAL
          ═══════════════════════════════════════════════════════════════════════════ */}
      {logisticsManifestBuilderOpen && (
        <div 
          className="fixed inset-0 overflow-y-auto"
          style={{ backgroundColor: "#020617", zIndex: 2147483647 }}
        >
          <LogisticsManifestBuilder
            key={`logistics-${selectedEstimate?.estimate_id || conversationalEstimateId}`}
            estimateId={selectedEstimate?.estimate_id || conversationalEstimateId}
            estimateData={{
              groups: getActiveGroups() || [],
              allScopes: scopes,
              job_name: selectedEstimate?.job_name
            }}
            clientInfo={{
              name: selectedLead?.contact_name || selectedLead?.name || "",
              email: selectedLead?.email || "",
              phone: selectedLead?.phone || "",
              address: selectedLead?.address || selectedLead?.project_address || "",
              project_name: selectedEstimate?.job_name || selectedLead?.project_name || "Project"
            }}
            companySettings={companySettings}
            onClose={() => {
              setLogisticsManifestBuilderOpen(false);
              setMissionBriefOpen(true);
            }}
          />
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════════════════
          MISSION BRIEF VIEWER - View all 6 ChatGPT documents
          ═══════════════════════════════════════════════════════════════════════════ */}
      {missionBriefViewerOpen && (
        <MissionBriefViewer
          key={`mission-brief-viewer-${selectedEstimate?.estimate_id || conversationalEstimateId}`}
          estimateId={selectedEstimate?.estimate_id || conversationalEstimateId}
          onClose={() => {
            setMissionBriefViewerOpen(false);
            setMissionBriefOpen(true);
          }}
        />
      )}
    </div>
  );
}